/**
 * Meanbook Note - Core Application Logic (CRM & Order Board)
 * ควบคุม UI, ปฏิกิริยาการคัดลอก, และประสานงานข้อมูล Firebase Firestore แบบเรียลไทม์
 */

import "./style.css";
import app, { db, auth, messaging, signInWithPopup, signInWithRedirect, GoogleAuthProvider, signOut, onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail } from "./firebase.js";
import { getToken } from "firebase/messaging";
import { parseNoteContent, setAddressDatabase, parseQuickOrderContent } from "./parser.js";
import { DAILY_NOTE_TITLE_PREFIX, READ_SYNC_PROFILE, VIRTUAL_QUEUES, isVirtualQueueId, defaultCarrierSettings } from "./modules/constants.js";
import { getCarrierSettings, detectCarrier, detectCarriers, updateLiveCarrierBadge, getCarrierLabelHtml } from "./modules/carrier.js";
import { escapeHTML, showToast, showConfirm, formatThaiDate, updateMobileTabLabels, renderFieldRow } from "./modules/uiHelpers.js";
import { playSynthSound, triggerNotificationSound, isNotificationAllowedNow as soundIsNotificationAllowedNow, createNotification, sendLocalWebNotification, createCombinedNotification, sendCombinedWebNotification, updateAppBadge, populateTimeDropdowns } from "./modules/sound.js";
import { getAssignedRoles as authGetAssignedRoles, getUserRoles as authGetUserRoles, hasRole as authHasRole, canUndoOrder } from "./modules/auth.js";
import { setupPdfScanner, updatePDFUploadButtonVisibility } from "./modules/pdfScanner.js";
import {
  setupPackerModal,
  openPackerSelectModal,
  reopenPackerSelectModal,
  setPackersList
} from "./modules/packerModal.js";
import {
  setupSettingsModal,
  showSettings,
  closeSettings,
  switchSettingsTab,
  updateCustomSoundUI,
  loadStaffList,
  setReturnToPackerSelectAfterSettings
} from "./modules/settingsModal.js";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  where,
  serverTimestamp,
  getDocs,
  getDoc,
  writeBatch
} from "firebase/firestore";

// --- สถานะแอปพลิเคชัน (Application State) ---
let activeNoteId = null;
let activeNoteData = null;
let allNotes = [];
let allOrders = [];
// allPackers moved to src/modules/packerModal.js
let keyedOrders = new Set(JSON.parse(localStorage.getItem('keyedOrders') || '[]'));
function toggleKeyedStatus(orderId) {
  if (keyedOrders.has(orderId)) {
    keyedOrders.delete(orderId);
  } else {
    keyedOrders.add(orderId);
  }
  localStorage.setItem('keyedOrders', JSON.stringify(Array.from(keyedOrders)));
  renderOrderDashboard();
}
let isMobile = window.innerWidth <= 1024;
let toastTimeoutId = null;
let knownPrepareOrders = new Set();
let isInitialLoadOfOrders = true;
let isPaymentStatusLocked = false;
let isQuickOrderMode = false;
let isBatchSelectMode = false;
let currentUserStaffData = null;
let isCompactMobile = window.innerWidth <= 600;


let orderSyncBuckets = new Map();
let searchOrdersCache = new Map();
let noteOrdersFetchPromises = new Map();
const completedSearchQueries = new Set();
let currentSearchSeq = 0;

function getRealtimeSyncProfile() {
  const roles = getUserRoles();
  const isAdminLike = roles.includes("Super Admin") || roles.includes("Order Manager");
  const isPacker = roles.includes("Packer & Picker");
  const isShipper = roles.includes("Shipper");

  let orderDays = READ_SYNC_PROFILE.staffOrderDays;
  let orderLimit = READ_SYNC_PROFILE.staffOrderLimit;

  if (isAdminLike) {
    orderDays = READ_SYNC_PROFILE.adminOrderDays;
    orderLimit = READ_SYNC_PROFILE.adminOrderLimit;
  } else if (isPacker) {
    orderDays = READ_SYNC_PROFILE.packerOrderDays;
    orderLimit = READ_SYNC_PROFILE.packerOrderLimit;
  } else if (isShipper) {
    orderDays = READ_SYNC_PROFILE.shipperOrderDays;
    orderLimit = READ_SYNC_PROFILE.shipperOrderLimit;
  }

  return {
    orderDays,
    orderLimit,
    dailyNotesOnly: !isAdminLike && isPacker,
    skipNotes: !isAdminLike && !isPacker
  };
}

function getAllowedDailyNoteTitles(now = new Date()) {
  const currentYear = now.getFullYear();
  const titles = [`${DAILY_NOTE_TITLE_PREFIX} ${currentYear}`];
  if (now.getMonth() === 0) {
    titles.push(`${DAILY_NOTE_TITLE_PREFIX} ${currentYear - 1}`);
  }
  return titles;
}

function buildNotesSyncQuery(profile) {
  const notesRef = collection(db, "notes");
  if (profile.skipNotes) {
    return null;
  }

  if (profile.dailyNotesOnly) {
    const allowedTitles = getAllowedDailyNoteTitles();
    return query(
      notesRef,
      where("title", "in", allowedTitles),
      limit(allowedTitles.length)
    );
  }

  return query(notesRef);
}

function rebuildAllOrdersFromCaches() {
  const merged = new Map();
  searchOrdersCache.forEach((order, id) => merged.set(id, order));
  orderSyncBuckets.forEach((bucket) => {
    bucket.forEach((order, id) => merged.set(id, order));
  });
  allOrders = Array.from(merged.values());
}

function setOrderSyncBucket(bucketName, snapshot) {
  const bucket = new Map();
  snapshot.forEach((docSnap) => {
    bucket.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
  });
  orderSyncBuckets.set(bucketName, bucket);
  rebuildAllOrdersFromCaches();
}

function buildActiveOrderSyncQueries(profile) {
  const roles = getUserRoles();
  const isAdminLike = roles.includes("Super Admin") || roles.includes("Order Manager");
  const hasPacker = roles.includes("Packer & Picker");
  const hasShipper = roles.includes("Shipper");
  const ordersRef = collection(db, "orders");
  const queries = [];
  const addQuery = (key, ...constraints) => {
    queries.push({ key, q: query(ordersRef, ...constraints) });
  };

  if (isAdminLike) {
    addQuery("active-unready", where("readyToShip", "==", false));
    addQuery("active-pending-info", where("isPendingInfo", "==", true));
  }

  if (isAdminLike || hasPacker || hasShipper) {
    addQuery("active-prepare", where("readyToShip", "==", true), where("isPacked", "==", false));
  }

  if (isAdminLike || hasShipper) {
    addQuery(
      "active-shipping",
      where("readyToShip", "==", true),
      where("isPacked", "==", true),
      where("deliveryType", "==", "express"),
      where("trackingNumber", "==", "")
    );
  }

  if (isAdminLike || hasPacker) {
    addQuery(
      "active-pickup",
      where("readyToShip", "==", true),
      where("isPacked", "==", true),
      where("pickupDone", "==", false),
      where("deliveryType", "in", ["pickup", "messenger"])
    );
  }

  return queries;
}

function updateOrderQueueBadges() {
  const prepareCount = allOrders.filter(o => o.readyToShip === true && o.isPacked !== true && o.isPendingInfo !== true).length;
  updateAppBadge(prepareCount);

  const prepareBadge = document.getElementById("prepare-queue-badge");
  if (prepareBadge) {
    prepareBadge.textContent = prepareCount;
    prepareBadge.style.display = prepareCount > 0 ? "inline-block" : "none";
  }

  const readyCount = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express' && o.isPendingInfo !== true).length;
  const shippingBadge = document.getElementById("shipping-queue-badge");
  if (shippingBadge) {
    shippingBadge.textContent = readyCount;
    shippingBadge.style.display = readyCount > 0 ? "inline-block" : "none";
  }

  const pickupCount = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger') && o.isPendingInfo !== true).length;
  const pickupBadge = document.getElementById("pickup-queue-badge");
  if (pickupBadge) {
    pickupBadge.textContent = pickupCount;
    pickupBadge.style.display = pickupCount > 0 ? "inline-block" : "none";
  }

  const completedCount = allOrders.filter(isOrderCompletedWithinShipperLimit).length;
  const completedBadge = document.getElementById("completed-queue-badge");
  if (completedBadge) {
    completedBadge.textContent = completedCount;
    completedBadge.style.display = completedCount > 0 ? "inline-block" : "none";
  }

  const pendingInfoCount = allOrders.filter(o => o.isPendingInfo === true).length;
  const pendingInfoBadge = document.getElementById("pending-info-queue-badge");
  if (pendingInfoBadge) {
    pendingInfoBadge.textContent = pendingInfoCount;
    pendingInfoBadge.style.display = pendingInfoCount > 0 ? "inline-block" : "none";
  }

  const roles = getUserRoles();
  let unfilteredUndo = [];
  if (roles.includes("Super Admin")) {
    unfilteredUndo = allOrders.filter(o =>
      (o.readyToShip === true && !o.isPacked) ||
      (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
      (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
      (o.deliveryType === 'express' && !!o.trackingNumber) ||
      (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
      (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip)
    );
  } else {
    const undoMap = new Map();
    if (roles.includes("Order Manager")) {
      allOrders.filter(o =>
        (o.readyToShip === true && !o.isPacked) ||
        (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
        (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType !== 'express')
      ).forEach(o => undoMap.set(o.id, o));
    }
    if (roles.includes("Packer & Picker")) {
      allOrders.filter(o =>
        (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
        (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType !== 'express')
      ).forEach(o => undoMap.set(o.id, o));
    }
    if (roles.includes("Shipper")) {
      allOrders.filter(o =>
        (o.deliveryType === 'express' && !!o.trackingNumber) ||
        (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType === 'express')
      ).forEach(o => undoMap.set(o.id, o));
    }
    unfilteredUndo = Array.from(undoMap.values());
  }

  const undoBadge = document.getElementById("undo-queue-badge");
  if (undoBadge) {
    const undoCount = unfilteredUndo.filter(o => isToday(o.updatedAt)).length;
    undoBadge.textContent = undoCount;
    undoBadge.style.display = undoCount > 0 ? "inline-block" : "none";
  }
}

function refreshOrderViewsAfterSync() {
  updateOrderQueueBadges();
  renderNotesList();
  updateNoteMeta();
  updateCleanupBadge();
  if (activeNoteId || isWideMobileViewport()) {
    renderOrderDashboard();
  }
}

async function fetchOrdersForNoteOnDemand(noteId) {
  if (!noteId || !hasRole(["Super Admin", "Order Manager"])) return;

  const bucketName = `note:${noteId}`;
  if (orderSyncBuckets.has(bucketName) || noteOrdersFetchPromises.has(noteId)) return;

  const fetchPromise = (async () => {
    try {
      dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
      if (window.lucide) window.lucide.createIcons();

      const q = query(collection(db, "orders"), where("noteId", "==", noteId));
      const snapshot = await getDocs(q);
      setOrderSyncBucket(bucketName, snapshot);

      if (activeNoteId === noteId) {
        refreshOrderViewsAfterSync();
      }
    } catch (err) {
      console.error("On-demand note orders fetch error:", err);
      if (activeNoteId === noteId) {
        showToast("ไม่สามารถโหลดออเดอร์เก่าของโน้ตนี้ได้", "danger");
      }
    } finally {
      noteOrdersFetchPromises.delete(noteId);
      if (activeNoteId === noteId) {
        dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
        if (window.lucide) window.lucide.createIcons();
      }
    }
  })();

  noteOrdersFetchPromises.set(noteId, fetchPromise);
}

function isWideMobileViewport() {
  return window.innerWidth > 600 && window.innerWidth <= 1024;
}

function isGlobalSearchActive() {
  if (activeNoteId !== null) return false;
  const searchNotesInput = document.getElementById("search-notes");
  const searchActiveInput = document.getElementById("search-active-orders");
  const hasSearch = !!(
    (searchNotesInput && searchNotesInput.value.trim() !== "") ||
    (searchActiveInput && searchActiveInput.value.trim() !== "")
  );
  if (window.innerWidth <= 600) {
    return true;
  } else {
    return hasSearch;
  }
}

function matchesOrderSearch(o, queryText) {
  if (!o || !queryText) return true;
  const q = queryText.toLowerCase().trim();
  if (!q) return true;

  // 1. Direct text fields match
  const fields = [
    o.customerName,
    o.thaiName,
    o.phone,
    o.addressBody,
    o.subDistrict,
    o.district,
    o.province,
    o.postalCode,
    o.items,
    o.rawText,
    o.trackingNumber,
    o.adminNote,
    o.packerName,
    o.platform
  ];

  for (let i = 0; i < fields.length; i++) {
    const val = fields[i];
    if (val && String(val).toLowerCase().includes(q)) {
      return true;
    }
  }

  // 2. Phone / digits normalized match (e.g. 081-234-5678 vs 0812345678)
  const cleanQueryDigits = q.replace(/\D/g, "");
  if (cleanQueryDigits.length >= 3) {
    if (o.phone && String(o.phone).replace(/\D/g, "").includes(cleanQueryDigits)) return true;
    if (o.rawText && String(o.rawText).replace(/\D/g, "").includes(cleanQueryDigits)) return true;
    if (o.trackingNumber && String(o.trackingNumber).replace(/\D/g, "").includes(cleanQueryDigits)) return true;
  }

  return false;
}

function isNoteCleanable(note) {
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  // นับจากวันที่แก้ไขล่าสุด (updatedAt)
  const noteTime = note.updatedAt ?
    (note.updatedAt.toDate ? note.updatedAt.toDate() : new Date(note.updatedAt)) :
    (note.createdAt ? (note.createdAt.toDate ? note.createdAt.toDate() : new Date(note.createdAt)) : new Date());

  if (noteTime >= twoYearsAgo) return false;

  const noteOrders = allOrders.filter(o => o.noteId === note.id);
  const allCompleted = noteOrders.every(o =>
    o.trackingNumber ||
    ((o.deliveryType === 'pickup' || o.deliveryType === 'messenger') && o.readyToShip === true && o.pickupDone === true)
  );

  return allCompleted;
}

function getCleanupNotes() {
  return allNotes.filter(isNoteCleanable);
}

function toggleShortcuts() {
  const isAllowedDropdown = hasRole(["Super Admin", "Order Manager"]);
  if (!isAllowedDropdown) return;

  const container = document.getElementById("sidebar-shortcuts-container");
  if (!container) return;

  const isCollapsed = container.classList.contains("collapsed") || container.style.display === "none";
  if (isCollapsed) {
    expandShortcuts();
  } else {
    collapseShortcuts();
  }
}

function expandShortcuts() {
  const container = document.getElementById("sidebar-shortcuts-container");
  const btnToggle = document.getElementById("btn-toggle-shortcuts");
  if (container) {
    container.classList.remove("collapsed");
    container.style.display = "flex";
  }
  if (btnToggle) {
    btnToggle.classList.remove("collapsed");
    const icon = btnToggle.querySelector("i, svg");
    if (icon) icon.style.transform = "rotate(0deg)";
  }
  localStorage.setItem("shortcutsCollapsed", "false");
}

function collapseShortcuts() {
  const container = document.getElementById("sidebar-shortcuts-container");
  const btnToggle = document.getElementById("btn-toggle-shortcuts");
  if (container) {
    container.classList.add("collapsed");
    container.style.display = "none";
  }
  if (btnToggle) {
    btnToggle.classList.add("collapsed");
    const icon = btnToggle.querySelector("i, svg");
    if (icon) icon.style.transform = "rotate(-90deg)";
  }
  localStorage.setItem("shortcutsCollapsed", "true");
}

// --- ระบบธีม สว่าง/มืด (Light/Dark Theme System) ---
const savedTheme = localStorage.getItem("appTheme") || "light";
if (savedTheme === "light") {
  document.body.classList.add("light-theme");
  document.body.classList.remove("dark-theme");
} else {
  document.body.classList.remove("light-theme");
  document.body.classList.add("dark-theme");
}


// --- อ้างอิง DOM Elements ---
const dom = {
  // Sidebar
  btnNewNote: document.getElementById("btn-new-note"),
  btnQuickOrder: document.getElementById("btn-quick-order"),
  btnPrepareQueue: document.getElementById("btn-prepare-queue"),
  searchNotes: document.getElementById("search-notes"),
  searchLocalNote: document.getElementById("search-local-note"),
  notesListUl: document.getElementById("notes-list-ul"),

  // Board Header / Meta
  noteTitleInput: document.getElementById("note-title-input"),
  noteDateCreated: document.getElementById("note-date-created"),
  noteStats: document.getElementById("note-stats"),
  saveStatusText: document.getElementById("save-status-text"),
  btnDeleteNote: document.getElementById("btn-delete-note"),

  // Orders Board Panel
  ordersEmptyState: document.getElementById("orders-empty-state"),
  ordersCardsListDiv: document.getElementById("orders-cards-list-div"),
  ordersPanel: document.getElementById("orders-panel"),
  btnBatchPrepare: document.getElementById("btn-batch-prepare"),
  btnBatchPrepareText: document.getElementById("btn-batch-prepare-text"),
  btnBatchCancel: document.getElementById("btn-batch-cancel"),
  selectAllCheckbox: document.getElementById("select-all-checkbox"),
  selectAllWrapper: document.getElementById("select-all-wrapper"),

  // Form Drawer Elements
  btnCancelOrder: document.getElementById("btn-cancel-order"),
  btnSaveOrder: document.getElementById("btn-save-order"),
  btnCloseForm: document.getElementById("btn-close-form"),
  btnFormUndo: document.getElementById("btn-form-undo"),

  // Mobile UI Helper
  appContainer: document.querySelector(".app-container"),
  mobileOrdersToEditor: document.getElementById("mobile-orders-to-editor"),
  navTabs: document.querySelectorAll(".nav-tab"),
  ordersBadgeDot: document.getElementById("orders-badge-dot")
};

// Form Input References
const formInputs = {
  orderId: document.getElementById("form-order-id"),
  rawText: document.getElementById("form-raw-text"),
  customerName: document.getElementById("form-customer-name"),
  thaiName: document.getElementById("form-thai-name"),
  phone: document.getElementById("form-phone"),
  addressBody: document.getElementById("form-address-body"),
  subDistrict: document.getElementById("form-subdistrict"),
  district: document.getElementById("form-district"),
  province: document.getElementById("form-province"),
  postalCode: document.getElementById("form-postalcode"),
  items: document.getElementById("form-items"),
  adminNote: document.getElementById("form-admin-note"),
  trackingNumber: document.getElementById("form-tracking-number"),
  carrier: document.getElementById("form-carrier"),
  statusPaid: document.getElementById("form-status-paid"),
  statusUnpaid: document.getElementById("form-status-unpaid"),
  statusDeposit: document.getElementById("form-status-deposit"),
  depositAmount: document.getElementById("form-deposit-amount"),
  depositAmountGroup: document.getElementById("form-group-deposit-amount"),
  paymentStatusGroup: document.getElementById("form-group-payment-status"),
  customerNameGroup: document.getElementById("form-group-customer-name"),
  platformGroup: document.getElementById("form-group-platform"),
  deliveryPickup: document.getElementById("form-delivery-pickup"),
  deliveryMessenger: document.getElementById("form-delivery-messenger"),
  deliveryExpress: document.getElementById("form-delivery-express"),
  statusReady: document.getElementById("form-status-ready"),
  statusUnready: document.getElementById("form-status-unready")
};

const formDeliveryStickers = [
  formInputs.deliveryPickup,
  formInputs.deliveryMessenger,
  formInputs.deliveryExpress
];

const formPlatformStickers = [
  document.getElementById("form-platform-page"),
  document.getElementById("form-platform-ig"),
  document.getElementById("form-platform-line"),
  document.getElementById("form-platform-store"),
  document.getElementById("form-platform-personal")
];

// --- ฟังก์ชันช่วยเหลือ (Utility Functions) ---

// ตรวจสอบว่าวันที่ของ Timestamp อยู่ในวันเดียวกันกับวันนี้หรือไม่

function setCompletedAtIfNeeded(updateData, existingOrder, newTracking, newPickupDone) {
  const wasCompleted = existingOrder ? (!!existingOrder.trackingNumber || (existingOrder.readyToShip && existingOrder.isPacked && existingOrder.pickupDone)) : false;
  const isCompleted = !!newTracking || newPickupDone;
  if (!wasCompleted && isCompleted) {
    updateData.completedAt = serverTimestamp();
  }
}

function isOrderCompletedWithinShipperLimit(o) {
  const isCompleted = o.trackingNumber || (o.readyToShip === true && o.pickupDone === true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger'));
  if (!isCompleted) return false;
  if (o.isPendingInfo === true) return false;
  
  const roles = getUserRoles();
  if (!roles.includes("Shipper")) return true;
  
  const time = o.updatedAt ? 
    (o.updatedAt.toDate ? o.updatedAt.toDate() : new Date(o.updatedAt)) : new Date(0);
    
  const diff = Date.now() - time.getTime();
  return diff <= 48 * 60 * 60 * 1000;
}

function isToday(timestamp) {
  if (!timestamp) return false;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
}

function getAssignedRoles() {
  return authGetAssignedRoles(currentUserStaffData);
}

function getUserRoles() {
  return authGetUserRoles(currentUserStaffData);
}

function hasRole(requiredRoles) {
  return authHasRole(requiredRoles, currentUserStaffData);
}

// อัปเดตข้อมูลโปรไฟล์ผู้ใช้ปัจจุบันบนหน้า UI
function updateUserProfileUI(staffData) {
  const nameEl = document.getElementById("current-user-name");
  const emailEl = document.getElementById("current-user-email");
  const rolesEl = document.getElementById("current-user-roles");
  const staffSec = document.getElementById("staff-management-section");
  const userRoleEl = document.querySelector(".user-role");

  const switcherContainer = document.getElementById("role-switcher-container");
  const switcherButtonsDiv = document.getElementById("role-switcher-buttons");

  if (staffData) {
    const displayName = staffData.displayName || staffData.email;
    const email = staffData.email;
    const assignedRoles = staffData.roles || [];

    if (nameEl) nameEl.textContent = displayName;
    if (emailEl) emailEl.textContent = email;
    if (rolesEl) rolesEl.textContent = assignedRoles.join(", ") || "ไม่มีสิทธิ์/บทบาท";

    // ดึงบทบาทสิทธิ์ที่กำลังเปิดใช้งานจริง (คำนึงถึงสลับบทบาท)
    const activeRoles = getUserRoles();
    if (userRoleEl) {
      let emoji = "👤";
      if (activeRoles.includes("Super Admin")) emoji = "👑";
      else if (activeRoles.includes("Order Manager")) emoji = "📝";
      else if (activeRoles.includes("Packer & Picker")) emoji = "📦";
      else if (activeRoles.includes("Shipper")) emoji = "🚚";
      userRoleEl.textContent = `${emoji} ${activeRoles.join(" + ")}`;
    }

    if (staffSec) {
      // ตรวจสอบจากบทบาทที่กำหนดจริง (Assigned Roles) เท่านั้น สำหรับสิทธิ์จัดการสิทธิ์พนักงาน
      if (assignedRoles.includes("Super Admin")) {
        staffSec.style.display = "block";
        loadStaffList();
      } else {
        staffSec.style.display = "none";
      }
    }

    // จัดการชุดปุ่มสลับบทบาทสิทธิ์
    if (switcherContainer && switcherButtonsDiv) {
      if (assignedRoles.length > 1) {
        switcherContainer.style.display = "block";
        switcherButtonsDiv.innerHTML = "";

        const currentActiveRole = localStorage.getItem("activeRole") || assignedRoles[0];
        const getRoleEmoji = (r) => {
          if (r === "Super Admin") return "👑";
          if (r === "Order Manager") return "📝";
          if (r === "Packer & Picker") return "📦";
          if (r === "Shipper") return "🚚";
          return "👤";
        };

        // ปุ่มสำหรับแต่ละตำแหน่ง (เลือกตำแหน่งใดตำแหน่งหนึ่งเท่านั้น)
        assignedRoles.forEach(role => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = `btn-role-toggle ${currentActiveRole === role ? "active" : ""}`;
          btn.innerHTML = `${getRoleEmoji(role)} ${role}`;
          btn.addEventListener("click", () => {
            if (currentActiveRole !== role) {
              localStorage.setItem("activeRole", role);
              updateUserProfileUI(currentUserStaffData);
              applyRolePermissions();
              initRealtimeSync();
              if (activeNoteId) {
                renderOrderDashboard();
              }
            }
          });
          switcherButtonsDiv.appendChild(btn);
        });
      } else {
        switcherContainer.style.display = "none";
      }
    }
  } else {
    if (nameEl) nameEl.textContent = "-";
    if (emailEl) emailEl.textContent = "-";
    if (rolesEl) rolesEl.textContent = "-";
    if (userRoleEl) userRoleEl.textContent = "👤 ยังไม่ได้ล็อกอิน";
    if (staffSec) staffSec.style.display = "none";
    if (switcherContainer) switcherContainer.style.display = "none";
  }
}

// (Staff Management functions moved to src/modules/settingsModal.js)

// ล้างการสลับคลาสสำหรับมุมมองหน้าจอมือถือ
function setMobilePane(paneClass) {
  dom.appContainer.classList.remove("show-sidebar", "show-editor", "show-orders");
  dom.appContainer.classList.add(paneClass);

  dom.navTabs.forEach(tab => {
    tab.classList.remove("active");
    if (tab.getAttribute("data-target") === paneClass.replace("show-", "") + "-panel") {
      tab.classList.add("active");
    }
  });

  // ซิงค์ประวัติหน้าเว็บบนมือถือสำหรับปุ่ม Back ของ Android / Browser
  if (isMobile && history.state && history.state.pane !== paneClass) {
    history.pushState({ pane: paneClass }, "");
  }
}

function isNotificationAllowedNow() {
  return soundIsNotificationAllowedNow(getAssignedRoles());
}

// (Notification Settings UI moved to src/modules/settingsModal.js)

// --- ฟังก์ชันประมวลผลการทำงานหลัก (Core App Operations) ---

// 1. ตรวจจับข้อมูลเรียลไทม์จากฐานข้อมูล Firestore พร้อมระบบจัดการ Unsubscribe ป้องกัน Memory Leak
let realtimeUnsubscribes = [];

function stopRealtimeSync() {
  if (realtimeUnsubscribes.length > 0) {
    console.log(`[Sync] Stopping ${realtimeUnsubscribes.length} active Firestore listeners...`);
    realtimeUnsubscribes.forEach((unsub) => {
      try {
        if (typeof unsub === "function") unsub();
      } catch (e) {
        console.warn("[Sync] Error during listener unsubscribe:", e);
      }
    });
    realtimeUnsubscribes = [];
  }
  orderSyncBuckets.clear();
  searchOrdersCache.clear();
  noteOrdersFetchPromises.clear();
  completedSearchQueries.clear();
  allNotes = [];
  allOrders = [];
  setPackersList([]);
  knownPrepareOrders.clear();
  isInitialLoadOfOrders = true;
}

// 1. ตรวจจับข้อมูลเรียลไทม์จากฐานข้อมูล Firestore
function initRealtimeSync() {
  // สั่งหยุด Listeners เก่าทั้งหมดก่อนเริ่มต้นใหม่ทุกครั้ง เพื่อป้องกัน Memory Leak และการอ่านซ้ำซ้อน
  stopRealtimeSync();

  const syncProfile = getRealtimeSyncProfile();
  const qNotes = buildNotesSyncQuery(syncProfile);

  if (qNotes) {
    const unsubNotes = onSnapshot(qNotes, (snapshot) => {
      allNotes = [];
      snapshot.forEach((doc) => {
        allNotes.push({ id: doc.id, ...doc.data() });
      });
      allNotes.sort((a, b) => {
        const getVal = (x) => (x.updatedAt?.toMillis ? x.updatedAt.toMillis() : (x.updatedAt ? new Date(x.updatedAt).getTime() : 0));
        return getVal(b) - getVal(a);
      });

      renderNotesList();
      updateCleanupBadge();

    if (activeNoteId) {
      const isVirtualQueue = ["prepare-queue", "shipping-queue", "completed-queue", "pickup-queue", "undo-queue", "cleanup-queue"].includes(activeNoteId);
      if (!isVirtualQueue) {
        const updatedNote = allNotes.find(n => n.id === activeNoteId);
        if (updatedNote) {
          activeNoteData = updatedNote;
          if (document.activeElement !== dom.noteTitleInput) {
            dom.noteTitleInput.value = updatedNote.title;
          }

          // Ensure the editor and delete button are fully enabled (robust fallback for laggy new note selections)
          dom.noteTitleInput.disabled = false;
          dom.btnDeleteNote.style.display = "inline-flex";
          resetAddOrderButton();

          updateNoteMeta();
        } else {
          activeNoteId = null;
          activeNoteData = null;
          disableEditor();
        }
      }
    }
  }, (error) => {
    console.error("Firestore notes sync error:", error);
    showToast("เกิดข้อผิดพลาดในการโหลดโน้ต", "danger");
    });
    realtimeUnsubscribes.push(unsubNotes);
  } else {
    allNotes = [];
    renderNotesList();
    updateCleanupBadge();
  }

  // Keep realtime reads bounded per role; older orders are still fetched by explicit search.
  const limitDate = new Date();
  limitDate.setDate(limitDate.getDate() - syncProfile.orderDays);
  const qOrders = query(
    collection(db, "orders"),
    where("updatedAt", ">=", limitDate),
    orderBy("updatedAt", "desc"),
    limit(syncProfile.orderLimit)
  );
  console.info("Firestore realtime sync profile:", syncProfile);

  buildActiveOrderSyncQueries(syncProfile).forEach(({ key, q }) => {
    const unsubActive = onSnapshot(q, (snapshot) => {
      setOrderSyncBucket(key, snapshot);
      refreshOrderViewsAfterSync();
    }, (error) => {
      console.error(`Firestore active orders sync error (${key}):`, error);
    });
    realtimeUnsubscribes.push(unsubActive);
  });
  
  const unsubOrders = onSnapshot(qOrders, (snapshot) => {
    setOrderSyncBucket("recent", snapshot);

    // ตรวจสอบและดักการแจ้งเตือนออเดอร์รอแพ็คใหม่
    const prepareOrdersThisTime = allOrders.filter(o => o.readyToShip === true && o.isPacked !== true && o.isPendingInfo !== true);

    const newPrepareOrders = [];
    for (const order of prepareOrdersThisTime) {
      if (!knownPrepareOrders.has(order.id)) {
        knownPrepareOrders.add(order.id);
        newPrepareOrders.push(order);
      }
    }

    if (newPrepareOrders.length > 0 && !isInitialLoadOfOrders) {
      if (isNotificationAllowedNow()) {
        triggerNotificationSound();
        if (newPrepareOrders.length === 1) {
          sendLocalWebNotification(newPrepareOrders[0]);
        } else {
          sendCombinedWebNotification(newPrepareOrders.length);
        }
      }
    }

    // ล้างรหัสออเดอร์ออกจาก Set ที่ถูกย้ายออกจากคิวรอแพ็คแล้ว
    const prepareIdsThisTime = new Set(prepareOrdersThisTime.map(o => o.id));
    for (const id of knownPrepareOrders) {
      if (!prepareIdsThisTime.has(id)) {
        knownPrepareOrders.delete(id);
      }
    }

    if (isInitialLoadOfOrders) {
      isInitialLoadOfOrders = false;
    }

    // อัปเดตตัวเลขคิวรอจัดเตรียมพัสดุ (สำหรับทีมงาน 2 - คัดกรอง readyToShip แต่ยังไม่แพ็ค ครอบคลุมทุกประเภทจัดส่ง)
    const prepareCount = prepareOrdersThisTime.length;

    // อัปเดต PWA App Badge แสดงผลตัวเลขบนหน้าจอโฮม
    updateAppBadge(prepareCount);

    const prepareBadge = document.getElementById("prepare-queue-badge");
    if (prepareBadge) {
      prepareBadge.textContent = prepareCount;
      prepareBadge.style.display = prepareCount > 0 ? "inline-block" : "none";
    }

    // อัปเดตตัวเลขพัสดุต้องจัดส่งในคิวเรียลไทม์ (สำหรับทีมงาน 2 - คัดกรอง readyToShip, แพ็คแล้ว และรอลงเลข express)
    const readyCount = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express' && o.isPendingInfo !== true).length;
    const shippingBadge = document.getElementById("shipping-queue-badge");
    if (shippingBadge) {
      shippingBadge.textContent = readyCount;
      shippingBadge.style.display = readyCount > 0 ? "inline-block" : "none";
    }

    // อัปเดตตัวเลขรับหน้าร้าน/ส่งไรเดอร์ในคิว (pickup/messenger ที่แพ็คเสร็จแล้วรอการรับส่ง)
    const pickupCount = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger') && o.isPendingInfo !== true).length;
    const pickupBadge = document.getElementById("pickup-queue-badge");
    if (pickupBadge) {
      pickupBadge.textContent = pickupCount;
      pickupBadge.style.display = pickupCount > 0 ? "inline-block" : "none";
    }

    const completedCount = allOrders.filter(isOrderCompletedWithinShipperLimit).length;
    const completedBadge = document.getElementById("completed-queue-badge");
    if (completedBadge) {
      completedBadge.textContent = completedCount;
      completedBadge.style.display = completedCount > 0 ? "inline-block" : "none";
    }

    const pendingInfoCount = allOrders.filter(o => o.isPendingInfo === true).length;
    const pendingInfoBadge = document.getElementById("pending-info-queue-badge");
    if (pendingInfoBadge) {
      pendingInfoBadge.textContent = pendingInfoCount;
      pendingInfoBadge.style.display = pendingInfoCount > 0 ? "inline-block" : "none";
    }


    // อัปเดตตัวเลขคิวดึงงานกลับ (สำหรับปุ่ม Undo) ตามสิทธิ์บทบาทปัจจุบัน (กรองเฉพาะรายการวันเดียวกัน)
    const roles = getUserRoles();
    let undoCount = 0;
    let unfilteredUndo = [];
    if (roles.includes("Super Admin")) {
      unfilteredUndo = allOrders.filter(o =>
        (o.readyToShip === true && !o.isPacked) ||
        (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
        (o.deliveryType === 'express' && !!o.trackingNumber) ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
        (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip)
      );
    } else {
      const undoMap = new Map();
      if (roles.includes("Order Manager")) {
        allOrders.filter(o =>
          (o.readyToShip === true && !o.isPacked) ||
          (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
          (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType !== 'express')
        ).forEach(o => undoMap.set(o.id, o));
      }
      if (roles.includes("Packer & Picker")) {
        allOrders.filter(o =>
          (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
          (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType !== 'express')
        ).forEach(o => undoMap.set(o.id, o));
      }
      if (roles.includes("Shipper")) {
        allOrders.filter(o =>
          (o.deliveryType === 'express' && !!o.trackingNumber) ||
          (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType === 'express')
        ).forEach(o => undoMap.set(o.id, o));
      }
      unfilteredUndo = Array.from(undoMap.values());
    }
    undoCount = unfilteredUndo.filter(o => isToday(o.updatedAt)).length;


    const undoBadge = document.getElementById("undo-queue-badge");
    if (undoBadge) {
      undoBadge.textContent = undoCount;
      undoBadge.style.display = undoCount > 0 ? "inline-block" : "none";
    }

    renderNotesList();
    updateNoteMeta();
    updateCleanupBadge();

    if (activeNoteId || isWideMobileViewport()) {
      renderOrderDashboard();
    }
  }, (error) => {
    console.error("Firestore orders sync error:", error);
    showToast("เกิดข้อผิดพลาดในการโหลดออเดอร์", "danger");
  });
  realtimeUnsubscribes.push(unsubOrders);

  const qPackers = query(collection(db, "packers"), orderBy("createdAt", "asc"));
  const unsubPackers = onSnapshot(qPackers, (snapshot) => {
    const packers = [];
    snapshot.forEach((doc) => {
      packers.push({ id: doc.id, ...doc.data() });
    });
    setPackersList(packers);
  }, (error) => {
    console.error("Firestore packers sync error:", error);
  });
  realtimeUnsubscribes.push(unsubPackers);

  // ซิงค์เสียงเรียกเข้าส่วนกลาง (Global Custom Ringtone) จาก Firestore เรียลไทม์
  const unsubSound = onSnapshot(doc(db, "settings", "sound"), (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();

      // ดึงข้อมูลและบันทึกสล็อตเสียงพิเศษทั้ง 3 ช่องแบบไดนามิก
      for (let i = 1; i <= 3; i++) {
        const url = data[`custom${i}_url`] || "";
        const name = data[`custom${i}_name`] || "";
        localStorage.setItem(`globalCustomSound_custom${i}`, url);
        localStorage.setItem(`globalCustomFilename_custom${i}`, name);
      }

      // อัปเดต UI ของสล็อตที่กำลังเลือกอยู่
      const soundTypeSelect = document.getElementById("setting-noti-sound-type");
      if (soundTypeSelect) {
        updateCustomSoundUI(soundTypeSelect.value);
      }
    }
  }, (error) => {
    console.warn("Firestore global sound settings sync error (might not be created yet):", error);
  });
  realtimeUnsubscribes.push(unsubSound);
}

// 2. เคลียร์ข้อมูลฟอร์ม Editor เมื่อไม่มีการเลือกโน้ต
function disableEditor() {
  dom.noteTitleInput.value = "";
  dom.noteTitleInput.disabled = true;
  dom.btnDeleteNote.style.display = "none";
  dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> ไม่มีโน้ตที่เลือก`;
  dom.noteStats.innerHTML = `<i data-lucide="package"></i> ออเดอร์ทั้งหมด: -`;

  const titleRow = document.querySelector(".note-title-row");
  if (titleRow) titleRow.style.display = "none";

  const actionBar = document.getElementById("board-action-bar-div");
  if (actionBar) actionBar.style.display = "none";

  const globalSearchStats = document.getElementById("note-global-search-stats");
  if (globalSearchStats) globalSearchStats.style.display = "none";

  setSearchWrapperDisplay("none");

  const searchActiveInput = document.getElementById("search-active-orders");
  if (searchActiveInput) searchActiveInput.value = "";

  const matchesSpan = document.getElementById("search-matches-count-span");
  if (matchesSpan) matchesSpan.style.display = "none";

  const searchLocalInput = document.getElementById("search-local-note");
  if (searchLocalInput) searchLocalInput.value = "";

  const localMatchesSpan = document.getElementById("local-search-matches-count-span");
  if (localMatchesSpan) localMatchesSpan.style.display = "none";

  const localSearchWrapper = document.getElementById("local-search-wrapper-div");
  if (localSearchWrapper) localSearchWrapper.style.display = "none";

  const addOrderBtn = document.getElementById("btn-add-order");
  if (addOrderBtn) addOrderBtn.disabled = true;

  dom.ordersEmptyState.style.display = "flex";
  dom.ordersEmptyState.innerHTML = `
    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 20px; text-align: center; font-family: 'Noto Sans Thai', 'Inter', sans-serif; user-select: none; opacity: 0.85;">
      <span style="font-size: 2.6rem; color: var(--color-text-main); font-weight: 500; line-height: 1;">ทำงาน</span>
      <span style="font-size: 4.2rem; color: var(--color-text-main); font-weight: 700; line-height: 1;">ทำงาน</span>
      <span style="font-size: 6.8rem; color: var(--color-text-main); font-weight: 800; line-height: 1; letter-spacing: -2px;">ทำงาน!!!</span>
    </div>
  `;
  dom.ordersCardsListDiv.style.display = "none";
  dom.ordersBadgeDot.style.display = "none";

  dom.appContainer.classList.remove("show-form");
  if (window.lucide) window.lucide.createIcons();
}

function resetAddOrderButton() {
  const addOrderBtn = document.getElementById("btn-add-order");
  if (addOrderBtn) {
    const isVirtualQueue = [
      "prepare-queue",
      "shipping-queue",
      "completed-queue",
      "pickup-queue",
      "undo-queue",
      "cleanup-queue",
      "pending-info-queue"
    ].includes(activeNoteId);

    if (isVirtualQueue) {
      addOrderBtn.style.display = "none";
      return;
    }

    const newAddOrderBtn = addOrderBtn.cloneNode(true);
    addOrderBtn.parentNode.replaceChild(newAddOrderBtn, addOrderBtn);

    newAddOrderBtn.style.display = "inline-flex";
    newAddOrderBtn.style.backgroundColor = "";

    const note = allNotes.find(n => n.id === activeNoteId);
    const isDailyNote = note && note.title && note.title.startsWith("ออเดอร์ประจำวัน");

    if (isDailyNote) {
      newAddOrderBtn.innerHTML = `<i data-lucide="zap"></i> <span>สร้างออเดอร์ด่วน</span>`;
      newAddOrderBtn.disabled = false;
      newAddOrderBtn.addEventListener("click", () => showForm(null, true, true));
    } else {
      newAddOrderBtn.innerHTML = `<i data-lucide="user-plus"></i> <span>+ เพิ่มออเดอร์ใหม่</span>`;
      newAddOrderBtn.disabled = false;
      newAddOrderBtn.addEventListener("click", () => showForm());
    }

    if (window.lucide) window.lucide.createIcons();
  }
}

// 3. ปลดล็อกและเปิดบอร์ดออเดอร์เมื่อเลือกโน้ต
function selectNote(noteId) {
  isBatchSelectMode = false;
  activeNoteId = noteId;

  const searchLocalInput = document.getElementById("search-local-note");
  if (searchLocalInput) searchLocalInput.value = "";
  const localMatchesSpan = document.getElementById("local-search-matches-count-span");
  if (localMatchesSpan) localMatchesSpan.style.display = "none";
  const localSearchWrapper = document.getElementById("local-search-wrapper-div");
  if (localSearchWrapper) {
    if (noteId === "cleanup-queue") {
      localSearchWrapper.style.display = "none";
    } else {
      localSearchWrapper.style.display = "flex";
    }
  }

  const titleRow = document.querySelector(".note-title-row");
  if (titleRow) titleRow.style.display = "flex";
  resetAddOrderButton();
  renderNotesList();
  const prepareQueueBtn = document.getElementById("btn-prepare-queue");
  const shippingQueueBtn = document.getElementById("btn-shipping-queue");
  const completedQueueBtn = document.getElementById("btn-completed-queue");
  const pickupQueueBtn = document.getElementById("btn-pickup-queue");
  const undoQueueBtn = document.getElementById("btn-undo-queue");
  const pendingInfoQueueBtn = document.getElementById("btn-pending-info-queue");

  // รีเซ็ตปุ่มค้นหาออเดอร์สากลเมื่อเลือกคิวงานหรือโน้ตอื่น
  const globalSearchBtn = document.getElementById("btn-global-search-shortcut");
  if (globalSearchBtn) {
    globalSearchBtn.classList.remove("active");
  }

  // รีเซ็ตปุ่มคิวงานปกติและปุ่มดำเนินการเสร็จสิ้น
  if (prepareQueueBtn) {
    prepareQueueBtn.classList.remove("active");
  }
  if (shippingQueueBtn) {
    shippingQueueBtn.classList.remove("active");
  }
  if (completedQueueBtn) {
    completedQueueBtn.classList.remove("active");
  }
  if (pickupQueueBtn) {
    pickupQueueBtn.classList.remove("active");
  }
  if (undoQueueBtn) {
    undoQueueBtn.classList.remove("active");
  }
  if (pendingInfoQueueBtn) {
    pendingInfoQueueBtn.classList.remove("active");
  }

  if (noteId === "cleanup-queue") {
    activeNoteData = { title: "โน้ตรอเคลียร์พื้นที่ (เกิน 2 ปี)" };

    dom.noteTitleInput.value = "🗑️ โน้ตรอเคลียร์พื้นที่ (สำเร็จและเกิน 2 ปี)";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    // เอาขอบไฮไลท์สีคลาวด์ฟอร์มออกจากโน้ตปกติ
    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));

    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";

    setSearchWrapperDisplay("none");

    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    updateNoteMeta();
    renderOrderDashboard();

    dom.appContainer.classList.remove("show-form");
    if (isMobile) {
      setMobilePane("show-editor");
    }
    return;
  }

  if (noteId === "pending-info-queue") {
    activeNoteData = { title: "Order ขอข้อมูลเพิ่ม" };
    dom.noteTitleInput.value = "❓ Order ขอข้อมูลเพิ่ม (รอการแก้ไข/เพิ่มเติมข้อมูล)";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    if (pendingInfoQueueBtn) {
      pendingInfoQueueBtn.classList.add("active");
    }

    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));
    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";
    setSearchWrapperDisplay("flex");
    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";
    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";
    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.style.display = "none";
    
    updateNoteMeta();
    renderOrderDashboard();
    dom.appContainer.classList.remove("show-form");
    if (typeof isMobile !== 'undefined' && isMobile) {
      setMobilePane("show-editor");
    } else if (typeof window !== 'undefined' && window.innerWidth <= 600) {
      setMobilePane("show-editor");
    }
    return;
  }

  if (noteId === "undo-queue") {
    activeNoteData = { title: "ดึงงานกลับ (Undo Queue)" };

    dom.noteTitleInput.value = "↩️ ดึงงานกลับ (Undo Queue)";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    if (undoQueueBtn) {
      undoQueueBtn.classList.add("active");
    }

    // เอาขอบไฮไลท์สีคลาวด์ฟอร์มออกจากโน้ตปกติ
    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));

    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";

    setSearchWrapperDisplay("flex");

    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.style.display = "none";

    updateNoteMeta();
    renderOrderDashboard();

    dom.appContainer.classList.remove("show-form");
    if (isMobile) {
      setMobilePane("show-editor");
    }
    return;
  }

  if (noteId === "prepare-queue") {
    activeNoteData = { title: "Order รอจัดเตรียม (คิวเตรียมสินค้า)" };

    dom.noteTitleInput.value = "📦 Order รอจัดเตรียม (คิวเตรียมสินค้า)";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    if (prepareQueueBtn) {
      prepareQueueBtn.classList.add("active");
    }

    // เอาขอบไฮไลท์สีคลาวด์ฟอร์มออกจากโน้ตปกติ
    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));

    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";

    setSearchWrapperDisplay("flex");

    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.style.display = "none";

    updateNoteMeta();
    renderOrderDashboard();

    dom.appContainer.classList.remove("show-form");
    if (isMobile) {
      setMobilePane("show-editor");
    }
    return;
  }

  if (noteId === "shipping-queue") {
    activeNoteData = { title: "Order รออัพเดทเลข" };

    dom.noteTitleInput.value = "🚚 Order รออัพเดทเลข (คิวลงเลขพัสดุ)";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    if (shippingQueueBtn) {
      shippingQueueBtn.classList.add("active");
    }

    // เอาขอบไฮไลท์สีคลาวด์ฟอร์มออกจากโน้ตปกติ
    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));

    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";

    setSearchWrapperDisplay("flex");

    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.style.display = "none";

    updateNoteMeta();
    renderOrderDashboard();

    dom.appContainer.classList.remove("show-form");
    if (isMobile) {
      setMobilePane("show-editor");
    }
    return;
  }

  if (noteId === "pickup-queue") {
    activeNoteData = { title: "รับหน้าร้าน/ส่งไรเดอร์ (คิวเตรียมของแผนกหน้าร้าน)" };

    dom.noteTitleInput.value = "🏪 คิวรับหน้าร้าน / ส่งไรเดอร์";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    if (pickupQueueBtn) {
      pickupQueueBtn.classList.add("active");
    }

    // เอาขอบไฮไลท์สีคลาวด์ฟอร์มออกจากโน้ตปกติ
    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));

    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";

    setSearchWrapperDisplay("flex");

    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.style.display = "none";

    updateNoteMeta();
    renderOrderDashboard();

    dom.appContainer.classList.remove("show-form");
    if (isMobile) {
      setMobilePane("show-editor");
    }
    return;
  }

  if (noteId === "completed-queue") {
    activeNoteData = { title: "ดำเนินการเรียบร้อยแล้ว" };

    dom.noteTitleInput.value = "✅ ดำเนินการเรียบร้อยแล้ว (จัดส่งสำเร็จ)";
    dom.noteTitleInput.disabled = true;
    dom.btnDeleteNote.style.display = "none";

    if (completedQueueBtn) {
      completedQueueBtn.classList.add("active");
    }

    // เอาขอบไฮไลท์สีคลาวด์ฟอร์มออกจากโน้ตปกติ
    document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));

    const actionBar = document.getElementById("board-action-bar-div");
    if (actionBar) actionBar.style.display = "flex";

    setSearchWrapperDisplay("flex");

    const searchActiveInput = document.getElementById("search-active-orders");
    if (searchActiveInput) searchActiveInput.value = "";

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.style.display = "none";

    updateNoteMeta();
    renderOrderDashboard();

    dom.appContainer.classList.remove("show-form");
    if (isMobile) {
      setMobilePane("show-editor");
    }
    return;
  }

  const note = allNotes.find(n => n.id === noteId);
  if (!note) return;

  activeNoteData = note;

  dom.noteTitleInput.disabled = false;
  dom.btnDeleteNote.style.display = "inline-flex";
  dom.noteTitleInput.value = note.title;

  dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
  if (window.lucide) window.lucide.createIcons();

  const actionBar = document.getElementById("board-action-bar-div");
  if (actionBar) actionBar.style.display = "flex";

  setSearchWrapperDisplay("flex");

  const searchActiveInput = document.getElementById("search-active-orders");
  if (searchActiveInput) searchActiveInput.value = "";

  const matchesSpan = document.getElementById("search-matches-count-span");
  if (matchesSpan) matchesSpan.style.display = "none";

  const addOrderBtn = document.getElementById("btn-add-order");
  if (addOrderBtn) {
    addOrderBtn.disabled = false;
    const isDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน");
    addOrderBtn.style.display = isDailyNote ? "inline-flex" : "none";
  }

  updateNoteMeta();
  renderOrderDashboard();
  fetchOrdersForNoteOnDemand(noteId);

  dom.appContainer.classList.remove("show-form");

  if (isMobile) {
    setMobilePane("show-editor");
  }
}

// 4. อัปเดตสถิติและข้อมูลจิปาถะในแถบ Editor
function updateNoteMeta() {
  updatePDFUploadButtonVisibility();
  if (!activeNoteId) return;


  let activeOrders = [];
  if (activeNoteId === "cleanup-queue") {
    const cleanupNotes = getCleanupNotes();
    dom.noteStats.innerHTML = `<i data-lucide="trash-2"></i> โน้ตรอเคลียร์: <strong>ทั้งหมด ${cleanupNotes.length} หัวข้อ</strong>`;
    dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> คัดกรองโน้ตที่เสร็จสิ้นทุกรายการและเกิน 2 ปี (นับจากวันที่แก้ไขล่าสุด) เพื่อเพิ่มพื้นที่ข้อมูล`;
    dom.ordersBadgeDot.style.display = "none";
    if (window.lucide) window.lucide.createIcons();
    return;
  }

  if (activeNoteId === "prepare-queue") {
    activeOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked !== true);
    dom.noteStats.innerHTML = `<i data-lucide="package"></i> คิวเตรียมของ: <strong>เหลือจัดเตรียม ${activeOrders.length} รายการ</strong>`;
    dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> โหมดตรวจสอบและเตรียมสินค้าคงคลัง`;
    dom.ordersBadgeDot.style.display = activeOrders.length > 0 ? "block" : "none";
    if (window.lucide) window.lucide.createIcons();
    return;
  }

  if (activeNoteId === "shipping-queue") {
    activeOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express');
    dom.noteStats.innerHTML = `<i data-lucide="package"></i> คิวงานพัสดุ: <strong>เหลือลงเลข ${activeOrders.length} รายการ</strong>`;
    dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> โหมดตรวจสอบของแผนกคัดของและรอลงเลขพัสดุ`;
    dom.ordersBadgeDot.style.display = activeOrders.length > 0 ? "block" : "none";
    if (window.lucide) window.lucide.createIcons();
    return;
  }

  if (activeNoteId === "pickup-queue") {
    activeOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger'));
    dom.noteStats.innerHTML = `<i data-lucide="store"></i> คิวหน้าร้าน/ไรเดอร์: <strong>เหลือส่งมอบ ${activeOrders.length} รายการ</strong>`;
    dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> โหมดตรวจสอบคิวหน้าร้านและแผนกเตรียมส่งไรเดอร์`;
    dom.ordersBadgeDot.style.display = activeOrders.length > 0 ? "block" : "none";
    if (window.lucide) window.lucide.createIcons();
    return;
  }

  if (activeNoteId === "completed-queue") {
    activeOrders = allOrders.filter(o => o.trackingNumber || (o.readyToShip === true && o.pickupDone === true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger')));
    dom.noteStats.innerHTML = `<i data-lucide="check-circle"></i> ดำเนินการเสร็จสมบูรณ์: <strong>รวมทั้งหมด ${activeOrders.length} รายการ</strong>`;
    dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> ประวัติรายการคลังสินค้าสำเร็จรูป`;
    dom.ordersBadgeDot.style.display = "none";
    if (window.lucide) window.lucide.createIcons();
    return;
  }

  activeOrders = allOrders.filter(o => o.noteId === activeNoteId);
  const total = activeOrders.length;
  const shipped = activeOrders.filter(o =>
    o.trackingNumber ||
    ((o.deliveryType === 'pickup' || o.deliveryType === 'messenger') && o.readyToShip === true && o.pickupDone === true)
  ).length;

  dom.noteStats.innerHTML = `<i data-lucide="package"></i> ออเดอร์: <strong>${shipped}/${total} ส่งแล้ว</strong>`;

  if (activeNoteData) {
    dom.noteDateCreated.innerHTML = `<i data-lucide="calendar"></i> วันที่แก้ไขล่าสุด: ${formatThaiDate(activeNoteData.updatedAt)}`;
  }

  if (total > 0) {
    dom.ordersBadgeDot.style.display = "block";
  } else {
    dom.ordersBadgeDot.style.display = "none";
  }

  if (window.lucide) window.lucide.createIcons();
}

// 5. แสดงผลรายการโน้ตทั้งหมดลงบน Sidebar ฝั่งซ้าย
function renderNotesList() {
  const searchQuery = dom.searchNotes ? dom.searchNotes.value.toLowerCase().trim() : "";

  if (searchQuery && window.innerWidth > 600) {
    if (activeNoteId !== null) {
      activeNoteId = null;
      activeNoteData = null;

      // Clear active classes of note items and shortcuts
      document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));
      document.querySelectorAll(".shortcut-btn").forEach(btn => btn.classList.remove("active"));
    }
  }

  dom.notesListUl.innerHTML = "";

  const roles = getUserRoles();
  const filtered = allNotes.filter(n => {
    // สำหรับพนักงาน Packer & Picker ให้แสดงเฉพาะโน้ตออเดอร์ประจำวันเท่านั้น (ยกเว้นมีบทบาท Super Admin/Order Manager/Shipper ร่วมด้วย)
    const canSeeAll = roles.includes("Super Admin") || roles.includes("Order Manager") || roles.includes("Shipper");
    if (!canSeeAll && roles.includes("Packer & Picker") && !(n.title && n.title.startsWith("ออเดอร์ประจำวัน"))) {
      return false;
    }

    if (isNoteCleanable(n) && !searchQuery) return false;

    if (n.title.toLowerCase().includes(searchQuery)) return true;

    // ค้นลึกถึงรายการลูกค้า/ที่อยู่ในการ์ดลูกภายใต้โน้ตนั้น ๆ
    const noteOrders = allOrders.filter(o => o.noteId === n.id);
    return noteOrders.some(o => matchesOrderSearch(o, searchQuery));
  });

  const resultsCountDiv = document.getElementById("search-results-count");
  if (resultsCountDiv) {
    resultsCountDiv.style.display = "none";
  }

  // GLOBAL SEARCH BOARD RENDERING (WHEN GLOBAL SEARCH IS ACTIVE)
  if (isGlobalSearchActive()) {
    renderOrderDashboard();
  }

  if (filtered.length === 0) {
    dom.notesListUl.innerHTML = `<div class="loading-spinner">ไม่พบโน้ตสำหรับคำค้นหา</div>`;
    return;
  }

  filtered.forEach(note => {
    const li = document.createElement("li");
    li.className = `note-item ${note.id === activeNoteId ? 'active' : ''}`;

    const noteOrders = allOrders.filter(o => o.noteId === note.id);
    const totalCount = noteOrders.length;
    const shippedCount = noteOrders.filter(o =>
      o.trackingNumber ||
      ((o.deliveryType === 'pickup' || o.deliveryType === 'messenger') && o.readyToShip === true && o.pickupDone === true)
    ).length;
    const isCompleted = totalCount > 0 && totalCount === shippedCount;

    // สกัดข้อความบางส่วนของออเดอร์ในโน้ต เพื่อไปพรีวิวเป็น Note Snippet แบบ Evernote
    const orderNames = noteOrders.map(o => o.customerName || o.thaiName || "").filter(Boolean);
    const orderItems = noteOrders.map(o => o.items || "").filter(Boolean).join(" ");
    let noteSnippet = "";
    if (orderNames.length > 0) {
      noteSnippet = `ออเดอร์: ${orderNames.join(", ")}`;
      if (orderItems) {
        noteSnippet += ` | ${orderItems.replace(/\n/g, " ")}`;
      }
    } else {
      noteSnippet = "ยังไม่มีข้อมูลสั่งซื้อในการ์ดโน้ตนี้";
    }
    if (noteSnippet.length > 80) {
      noteSnippet = noteSnippet.substring(0, 80) + "...";
    }

    li.innerHTML = `
      <div class="note-item-title">${escapeHTML(note.title) || "ไม่มีชื่อโน้ต"}</div>

      <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; width: 100%;">
        <div class="note-item-date">${formatThaiDate(note.updatedAt)}</div>
        ${totalCount > 0 ? `
          <div class="note-item-stats" style="margin-top: 0;">
            <span class="note-stats-badge ${isCompleted ? 'completed' : ''}">
              <i data-lucide="truck" style="width:10px; height:10px;"></i>
              ${shippedCount}/${totalCount} ส่งแล้ว
            </span>
          </div>
        ` : ''}
      </div>
    `;

    li.addEventListener("click", () => selectNote(note.id));
    dom.notesListUl.appendChild(li);
  });

  if (window.lucide) window.lucide.createIcons();
}

// --- คำสั่งจัดการล้างโน้ตเก่าเก็บสำหรับ Super Admin & Order Manager ---

async function deleteNoteAndOrders(targetDeleteId, noteTitle) {
  const confirmMessage = `คุณต้องการลบโน้ตหัวข้อหลัก "${noteTitle || "โน้ตไม่มีชื่อ"}" นี้ใช่หรือไม่? \nข้อมูลการ์ดรายการออเดอร์ของพนักงานทั้งหมดในโน้ตนี้จะถูกกวาดล้างลบถอนรากทันที! ไม่สามารถกู้คืนได้ เพื่อประหยัดพื้นที่ข้อมูล`;

  showConfirm("ยืนยันการลบโน้ตหลัก", confirmMessage, async () => {
    dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
    if (window.lucide) window.lucide.createIcons();

    try {
      const qOrders = query(collection(db, "orders"), where("noteId", "==", targetDeleteId));
      const orderSnap = await getDocs(qOrders);
      const batch = writeBatch(db);

      console.log(`Found ${orderSnap.size} orders to delete in batch.`);
      orderSnap.forEach((orderDoc) => {
        batch.delete(orderDoc.ref);
      });

      batch.delete(doc(db, "notes", targetDeleteId));
      await batch.commit();
      console.log("Batch delete commit successful!");

      showToast("ลบหัวข้อและล้างออเดอร์พัสดุทั้งหมดสำเร็จ!");

      if (activeNoteId === targetDeleteId) {
        activeNoteId = null;
        activeNoteData = null;
        disableEditor();
      }

      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();

      if (activeNoteId === "cleanup-queue") {
        renderOrderDashboard();
      }
    } catch (err) {
      console.error("Batch delete note error:", err);
      showToast("เกิดข้อผิดพลาดในการลบธุรกรรม", "danger");
      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();
    }
  });
}

async function deleteAllCleanupNotes() {
  const cleanupNotes = getCleanupNotes();
  if (cleanupNotes.length === 0) {
    showToast("ไม่มีโน้ตรอเคลียร์พื้นที่ในขณะนี้", "info");
    return;
  }

  const confirmMessage = `คุณต้องการลบโน้ตรอเคลียร์พื้นที่ทั้งหมด ${cleanupNotes.length} หัวข้อ ใช่หรือไม่?\nโน้ตและออเดอร์ทั้งหมดในรายการเหล่านี้จะถูกกวาดล้างลบถอนรากทันที! ไม่สามารถกู้คืนได้เพื่อเพิ่มพื้นที่ว่างให้กับระบบ`;

  showConfirm("ยืนยันการเคลียร์พื้นที่ทั้งหมด", confirmMessage, async () => {
    dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
    if (window.lucide) window.lucide.createIcons();

    try {
      const batch = writeBatch(db);
      let totalOrdersDeleted = 0;

      for (const note of cleanupNotes) {
        const qOrders = query(collection(db, "orders"), where("noteId", "==", note.id));
        const orderSnap = await getDocs(qOrders);
        orderSnap.forEach((orderDoc) => {
          batch.delete(orderDoc.ref);
          totalOrdersDeleted++;
        });
        batch.delete(doc(db, "notes", note.id));
      }

      await batch.commit();
      showToast(`เคลียร์พื้นที่สำเร็จ! ลบไปทั้งหมด ${cleanupNotes.length} โน้ต และ ${totalOrdersDeleted} ออเดอร์`);

      if (activeNoteId === "cleanup-queue") {
        renderOrderDashboard();
      }

      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();
    } catch (err) {
      console.error("Error batch deleting all cleanup notes:", err);
      showToast("เกิดข้อผิดพลาดในการเคลียร์พื้นที่ข้อมูล", "danger");
      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();
    }
  });
}

window.deleteNoteAndOrders = deleteNoteAndOrders;
window.deleteAllCleanupNotes = deleteAllCleanupNotes;

// 6. รวบรวมข้อมูลสั่งซื้อ เรนเดอร์เป็นรายการการ์ดบน Order Board (แถบกลาง)
function renderOrderDashboard() {
  const isVirtualQueue = [
    "prepare-queue",
    "shipping-queue",
    "completed-queue",
    "pickup-queue",
    "undo-queue",
    "cleanup-queue",
    "pending-info-queue"
  ].includes(activeNoteId);

  const isTargetNote = activeNoteId && !isVirtualQueue && !(activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน"));

  const addOrderBtn = document.getElementById("btn-add-order");

  if (isTargetNote) {
    if (dom.btnBatchPrepare) dom.btnBatchPrepare.style.display = "inline-flex";

    if (isBatchSelectMode) {
      if (dom.selectAllWrapper) dom.selectAllWrapper.style.display = "flex";
      if (dom.btnBatchCancel) dom.btnBatchCancel.style.display = "inline-flex";
      if (addOrderBtn) addOrderBtn.style.display = "none";
    } else {
      if (dom.selectAllWrapper) dom.selectAllWrapper.style.display = "none";
      if (dom.btnBatchCancel) dom.btnBatchCancel.style.display = "none";
      if (addOrderBtn) addOrderBtn.style.display = "inline-flex";
      if (dom.selectAllCheckbox) dom.selectAllCheckbox.checked = false;
    }
  } else {
    if (dom.btnBatchPrepare) dom.btnBatchPrepare.style.display = "none";
    if (dom.btnBatchCancel) dom.btnBatchCancel.style.display = "none";
    if (dom.selectAllWrapper) dom.selectAllWrapper.style.display = "none";
    if (addOrderBtn) {
      addOrderBtn.style.display = (isVirtualQueue || activeNoteId === null) ? "none" : "inline-flex";
    }
  }
  updateBatchPrepareButtonState();

  if (activeNoteId === "cleanup-queue") {
    const cleanupNotes = getCleanupNotes();

    // เรียงลำดับจากเก่าที่สุดก่อน เพื่อล้างข้อมูลตามลำดับอายุ
    cleanupNotes.sort((a, b) => {
      const timeA = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
      const timeB = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
      return timeA - timeB;
    });

    const matchesSpan = document.getElementById("search-matches-count-span");
    if (matchesSpan) matchesSpan.style.display = "none";

    if (cleanupNotes.length === 0) {
      dom.ordersEmptyState.style.display = "flex";
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(16, 185, 129, 0.1); color: var(--color-success);"><i data-lucide="shield-check"></i></div>
        <h3>ฐานข้อมูลสะอาดเรียบร้อยดีมาก! 🎉</h3>
        <p>ไม่มีโน้ตเก่าที่เสร็จสมบูรณ์เกิน 2 ปีหลงเหลืออยู่ในระบบคลาวด์ขณะนี้</p>
      `;
      dom.ordersCardsListDiv.style.display = "none";

      const actionBtn = document.getElementById("btn-add-order");
      if (actionBtn) {
        actionBtn.style.display = "none";
      }
      if (window.lucide) window.lucide.createIcons();
      return;
    }

    dom.ordersEmptyState.style.display = "none";
    dom.ordersCardsListDiv.innerHTML = "";
    dom.ordersCardsListDiv.style.display = "grid";

    // ตั้งปุ่มขวาบนของบอร์ดเป็น ลบทั้งหมด เพื่ออำนวยความสะดวก
    const actionBtn = document.getElementById("btn-add-order");
    if (actionBtn) {
      actionBtn.disabled = false;
      actionBtn.style.display = "inline-flex";
      actionBtn.style.backgroundColor = "var(--color-danger)";
      actionBtn.innerHTML = `<i data-lucide="trash-2"></i> <span>ลบทั้งหมด (${cleanupNotes.length})</span>`;

      const newActionBtn = actionBtn.cloneNode(true);
      actionBtn.parentNode.replaceChild(newActionBtn, actionBtn);
      newActionBtn.addEventListener("click", () => window.deleteAllCleanupNotes());
    }

    cleanupNotes.forEach((note) => {
      const card = document.createElement("div");
      card.className = "order-card cleanup-note-card";
      card.style.borderColor = "var(--color-danger-border)";
      card.style.position = "relative";
      card.style.padding = "20px";
      card.style.width = "100%";
      card.style.maxWidth = "490px";
      card.style.boxSizing = "border-box";

      const line = document.createElement("div");
      line.style.position = "absolute";
      line.style.top = "0";
      line.style.left = "0";
      line.style.right = "0";
      line.style.height = "4px";
      line.style.backgroundColor = "var(--color-danger)";
      line.style.borderRadius = "14px 14px 0 0";
      card.appendChild(line);

      const createdAtStr = formatThaiDate(note.createdAt);
      const updatedAtStr = formatThaiDate(note.updatedAt);

      const noteOrders = allOrders.filter(o => o.noteId === note.id);
      const totalCount = noteOrders.length;

      const contentContainer = document.createElement("div");
      contentContainer.style.display = "flex";
      contentContainer.style.flexDirection = "column";
      contentContainer.style.gap = "12px";
      contentContainer.style.height = "100%";

      contentContainer.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;">
          <div style="flex: 1;">
            <h4 style="font-size: 1.05rem; font-weight: 700; color: var(--color-text-main); margin-bottom: 4px; word-break: break-word;">
              📌 ${escapeHTML(note.title) || "ไม่มีชื่อโน้ต"}
            </h4>
            <p style="font-size: 0.75rem; color: var(--color-text-muted);">
              สร้างเมื่อ: ${createdAtStr}<br>แก้ไขล่าสุด: ${updatedAtStr}
            </p>
          </div>
          <span class="platform-tag" style="background-color: var(--color-danger-bg); color: var(--color-danger); border: 1px solid var(--color-danger-border); font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 6px; white-space: nowrap;">
            เก่าเกิน 2 ปี ⏰
          </span>
        </div>
        
        <div style="background-color: rgba(0,0,0,0.05); border-radius: 8px; padding: 10px 14px; margin: 4px 0; font-size: 0.8rem; color: var(--color-text-main); display: flex; align-items: center; gap: 6px;">
          <i data-lucide="check-circle" style="width: 14px; height: 14px; color: var(--color-success);"></i>
          <span>สำเร็จแล้วทุกรายการ (${totalCount}/${totalCount} ออเดอร์)</span>
        </div>

        <div style="display: flex; gap: 8px; margin-top: auto; padding-top: 12px; border-top: 1px solid var(--border-color);">
          <button class="btn btn-secondary btn-inspect" style="flex: 1; font-size: 0.75rem; padding: 6px 12px; gap: 4px;">
            <i data-lucide="eye" style="width: 14px; height: 14px;"></i> ตรวจสอบ
          </button>
          <button class="btn btn-danger btn-delete" style="flex: 1; font-size: 0.75rem; padding: 6px 12px; background-color: var(--color-danger); color: white; border: none; gap: 4px;">
            <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i> ลบโน้ต
          </button>
        </div>
      `;

      card.appendChild(contentContainer);

      const btnInspect = contentContainer.querySelector(".btn-inspect");
      if (btnInspect) {
        btnInspect.onclick = () => selectNote(note.id);
      }

      const btnDelete = contentContainer.querySelector(".btn-delete");
      if (btnDelete) {
        btnDelete.onclick = () => window.deleteNoteAndOrders(note.id, note.title);
      }

      dom.ordersCardsListDiv.appendChild(card);
    });

    if (window.lucide) window.lucide.createIcons();
    return;
  }

  if (activeNoteId === null && !isGlobalSearchActive()) {
    if (isMobile && window.innerWidth <= 600) {
      // Bypassed to render search empty state on mobile
    } else {
      const titleRow = document.querySelector(".note-title-row");
      if (titleRow) titleRow.style.display = "none";

      dom.noteTitleInput.value = "";
      dom.noteTitleInput.disabled = true;
      dom.btnDeleteNote.style.display = "none";

      dom.ordersEmptyState.style.display = "flex";
      dom.ordersEmptyState.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 20px; text-align: center; font-family: 'Noto Sans Thai', 'Inter', sans-serif; user-select: none; opacity: 0.85;">
          <span style="font-size: 2.6rem; color: var(--color-text-main); font-weight: 500; line-height: 1;">ทำงาน</span>
          <span style="font-size: 4.2rem; color: var(--color-text-main); font-weight: 700; line-height: 1;">ทำงาน</span>
          <span style="font-size: 6.8rem; color: var(--color-text-main); font-weight: 800; line-height: 1; letter-spacing: -2px;">ทำงาน!!!</span>
        </div>
      `;
      dom.ordersCardsListDiv.style.display = "none";

      const actionBar = document.getElementById("board-action-bar-div");
      if (actionBar) actionBar.style.display = "none";

      setSearchWrapperDisplay("none");

      const addOrderBtn = document.getElementById("btn-add-order");
      if (addOrderBtn) addOrderBtn.disabled = true;

      if (window.lucide) window.lucide.createIcons();
      return;
    }
  }

  let activeOrders = [];
  if (activeNoteId === "prepare-queue") {
    activeOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked !== true && o.isPendingInfo !== true);
  } else if (activeNoteId === "shipping-queue") {
    activeOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express' && o.isPendingInfo !== true);
  } else if (activeNoteId === "pickup-queue") {
    activeOrders = allOrders.filter(o => o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger') && o.isPendingInfo !== true);
  } else if (activeNoteId === "completed-queue") {
    activeOrders = allOrders.filter(isOrderCompletedWithinShipperLimit);
  } else if (activeNoteId === "pending-info-queue") {
    activeOrders = allOrders.filter(o => o.isPendingInfo === true);
  } else if (activeNoteId === "undo-queue") {
    const roles = getUserRoles();
    let unfilteredOrders = [];
    if (roles.includes("Super Admin")) {
      unfilteredOrders = allOrders.filter(o =>
        (o.readyToShip === true && !o.isPacked) ||
        (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
        (o.deliveryType === 'express' && !!o.trackingNumber) ||
        (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
        (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip)
      );
    } else {
      const undoMap = new Map();
      if (roles.includes("Order Manager")) {
        allOrders.filter(o =>
          (o.readyToShip === true && !o.isPacked) ||
          (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
          (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType !== 'express')
        ).forEach(o => undoMap.set(o.id, o));
      }
      if (roles.includes("Packer & Picker")) {
        allOrders.filter(o =>
          (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && o.deliveryType !== 'express') ||
          (o.readyToShip === true && o.isPacked === true && o.pickupDone === true && o.deliveryType !== 'express') ||
          (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType !== 'express')
        ).forEach(o => undoMap.set(o.id, o));
      }
      if (roles.includes("Shipper")) {
        allOrders.filter(o =>
          (o.deliveryType === 'express' && !!o.trackingNumber) ||
          (o.maxShipments > 1 && o.currentShipment > 1 && !o.readyToShip && o.deliveryType === 'express')
        ).forEach(o => undoMap.set(o.id, o));
      }
      unfilteredOrders = Array.from(undoMap.values());
    }
    activeOrders = unfilteredOrders.filter(o => isToday(o.updatedAt));
  } else if (activeNoteId) {
    activeOrders = allOrders.filter(o => o.noteId === activeNoteId);
  } else {
    // Global search across all notes/orders!
    const roles = getUserRoles();
    if (roles.includes("Super Admin") || roles.includes("Order Manager")) {
      activeOrders = allOrders;
    } else {
      const matchedIds = new Set();
      if (roles.includes("Shipper")) {
        allOrders.filter(o =>
          (o.readyToShip === true && o.isPacked !== true) || // prepare-queue
          (o.readyToShip === true && o.isPacked === true && !o.trackingNumber && o.deliveryType === 'express') || // shipping-queue
          (!!o.trackingNumber || (o.readyToShip === true && o.pickupDone === true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger'))) // completed-queue
        ).forEach(o => matchedIds.add(o.id));
      }
      if (roles.includes("Packer & Picker")) {
        allOrders.filter(o =>
          (o.readyToShip === true && o.isPacked !== true) || // prepare-queue
          (o.readyToShip === true && o.isPacked === true && o.pickupDone !== true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger')) || // pickup-queue
          (!!o.trackingNumber || (o.readyToShip === true && o.pickupDone === true && (o.deliveryType === 'pickup' || o.deliveryType === 'messenger'))) // completed-queue
        ).forEach(o => matchedIds.add(o.id));
      }
      activeOrders = allOrders.filter(o => matchedIds.has(o.id));
    }
  }

  // จัดเรียงออเดอร์ตามวันเวลาที่เพิ่มเข้ามา (หรือตามวันที่ทำเสร็จล่าสุดหากเป็นบอร์ดพัสดุส่งเสร็จสิ้น)
  if (activeNoteId === "completed-queue") {
    activeOrders.sort((a, b) => {
      const timeA = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
      const timeB = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
      return timeB - timeA; // ชิ้นที่เสร็จสิ้นล่าสุดแสดงด้านบนสุด
    });
  } else if (activeNoteId === null) {
    // Global search: sort by newest created first
    activeOrders.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return timeB - timeA;
    });
  } else {
    const isDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน");
    activeOrders.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return isDailyNote ? timeB - timeA : timeA - timeB;
    });
  }

  // การสแกนฟิลเตอร์ค้นหาการ์ดภายในโน้ตย่อย หรือการค้นหาระดับสากล
  let searchQuery = "";
  const searchActiveInput = document.getElementById("search-active-orders");

  if (window.innerWidth > 600 && dom.searchNotes && dom.searchNotes.value.trim() !== "") {
    searchQuery = dom.searchNotes.value.toLowerCase().trim();
  } else {
    searchQuery = searchActiveInput ? searchActiveInput.value.toLowerCase().trim() : "";
  }

  const searchLocalInput = document.getElementById("search-local-note");
  const localSearchQuery = searchLocalInput ? searchLocalInput.value.toLowerCase().trim() : "";

  let displayedOrders = [];
  if (!activeNoteId) {
    // Global search: only display matching cards if a query is actually typed!
    if (searchQuery) {
      displayedOrders = activeOrders.filter(o => matchesOrderSearch(o, searchQuery));
    } else {
      displayedOrders = [];
    }
  } else {
    // Note-specific or queue-specific search
    displayedOrders = activeOrders;
    if (searchQuery) {
      displayedOrders = activeOrders.filter(o => matchesOrderSearch(o, searchQuery));
    }

    if (localSearchQuery) {
      displayedOrders = displayedOrders.filter(o => matchesOrderSearch(o, localSearchQuery));
    }

    const localMatchesSpan = document.getElementById("local-search-matches-count-span");
    if (localMatchesSpan) {
      if (localSearchQuery) {
        localMatchesSpan.textContent = `พบ ${displayedOrders.length} รายการ`;
        localMatchesSpan.style.display = "inline-block";
      } else {
        localMatchesSpan.style.display = "none";
      }
    }
  }

  // Setup metadata and control states when in Global Search mode
  const titleRow = document.querySelector(".note-title-row");
  const globalSearchStats = document.getElementById("note-global-search-stats");
  const globalSearchStatsText = document.getElementById("global-search-stats-text");

  if (activeNoteId) {
    if (titleRow) titleRow.style.display = "flex";
    if (globalSearchStats) globalSearchStats.style.display = "none";
  } else {
    if (isMobile && window.innerWidth <= 600) {
      if (titleRow) titleRow.style.display = "none";
      if (globalSearchStats) globalSearchStats.style.display = "none";

      const actionBar = document.getElementById("board-action-bar-div");
      if (actionBar) actionBar.style.display = "none";

      const searchWrapper = document.getElementById("board-search-wrapper-div");
      if (searchWrapper) {
        setSearchWrapperDisplay("flex");
        searchWrapper.style.marginTop = "8px";
      }

      const searchActiveInput = document.getElementById("search-active-orders");
      if (searchActiveInput) {
        searchActiveInput.placeholder = "ค้นหาชื่อ, ที่อยู่, เบอร์โทร, รายการสินค้า...";
      }
    } else {
      // หน้าจอเดสก์ท็อป (> 600px) คืนกล่องค้นหาสากลไว้ตำแหน่งเดิมใต้หัวข้อโน้ต
      if (titleRow) titleRow.style.display = "flex";

      if (dom.searchNotes && dom.searchNotes.value.trim() !== "") {
        dom.noteTitleInput.value = `🔍 ผลการค้นหาสำหรับ: "${dom.searchNotes.value}"`;

        setSearchWrapperDisplay("none");
      } else {
        dom.noteTitleInput.value = `🔍 ค้นหาคำสั่งซื้อสากล (ทุกหัวข้อโน้ต)`;

        const searchWrapper = document.getElementById("board-search-wrapper-div");
        if (searchWrapper) {
          setSearchWrapperDisplay("flex");
          searchWrapper.style.marginTop = "0px";
        }

        const searchActiveInput = document.getElementById("search-active-orders");
        if (searchActiveInput) {
          searchActiveInput.placeholder = "ค้นหาชื่อ, ที่อยู่, เบอร์โทร, รายการสินค้าข้ามโน้ต...";
        }
      }

      dom.noteTitleInput.disabled = true;
      dom.btnDeleteNote.style.display = "none";

      const actionBar = document.getElementById("board-action-bar-div");
      if (actionBar) actionBar.style.display = "none";

      if (globalSearchStats && globalSearchStatsText) {
        globalSearchStats.style.display = "flex";
        globalSearchStatsText.innerHTML = `ออเดอร์ที่พบ: <strong style="color: var(--color-primary);">${displayedOrders.length} รายการ</strong>`;
      }
    }

    const addOrderBtn = document.getElementById("btn-add-order");
    if (addOrderBtn) addOrderBtn.disabled = true;
  }

  // อัปเดตบอกจำนวนยอดที่สแกนเจอ
  const matchesSpan = document.getElementById("search-matches-count-span");
  if (matchesSpan) {
    if (searchQuery && (activeNoteId || (isMobile && window.innerWidth <= 600))) {
      matchesSpan.style.display = "inline-block";
      matchesSpan.textContent = `พบ ${displayedOrders.length} รายการ`;
    } else {
      matchesSpan.style.display = "none";
    }
  }

  if (displayedOrders.length === 0) {
    dom.ordersEmptyState.style.display = "flex";
    if (activeNoteId === "prepare-queue") {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(14, 165, 233, 0.1); color: var(--color-info);"><i data-lucide="package"></i></div>
        <h3>คิวเตรียมสินค้าเสร็จสิ้นทั้งหมดแล้ว! 🎉</h3>
        <p>ยอดเยี่ยมมาก เก่งจริงๆ เก่งจริงๆ เก่งจริงๆ</p>
      `;
    } else if (activeNoteId === "shipping-queue") {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(16, 185, 129, 0.1); color: var(--color-success);"><i data-lucide="check-circle"></i></div>
        <h3>พัสดุจัดส่งหมดเรียบร้อยแล้ว! 🎉</h3>
        <p>ยอดเยี่ยมมาก ส่งหมดเกลี้ยงเลยนะคะเนี้ย!</p>
      `;
    } else if (activeNoteId === "pickup-queue") {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(249, 115, 22, 0.1); color: var(--color-warning);"><i data-lucide="check-circle"></i></div>
        <h3>เตรียมของหน้าร้านเสร็จครบถ้วนแล้ว! 🎉</h3>
        <p>ยอดเยี่ยมมากเบยยยยยยยย</p>
      `;
    } else if (activeNoteId === "completed-queue") {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(99, 102, 241, 0.1); color: var(--color-primary);"><i data-lucide="package"></i></div>
        <h3>ยังไม่มีรายการที่ดำเนินการจัดส่งสำเร็จ</h3>
        <p>เมื่อทีมงานใส่เลขพัสดุหรือส่งมอบสินค้าเสร็จสมบูรณ์ รายการเหล่านั้นจะมาปรากฏโชว์ที่นี่ทันทีเพื่อตรวจสอบ</p>
      `;
    } else if (activeNoteId === "undo-queue") {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(239, 68, 68, 0.1); color: var(--color-danger);"><i data-lucide="undo"></i></div>
        <h3>ไม่มีการ์ดออเดอร์ที่ดึงกลับได้</h3>
        <p>หากต้องการดึงงานกลับ กรุณาแจ้ง Order Manage</p>
      `;
    } else if (activeNoteId === "pending-info-queue") {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon" style="background-color: rgba(234, 179, 8, 0.1); color: var(--color-warning);"><i data-lucide="help-circle"></i></div>
        <h3>ไม่มีรายการรอข้อมูลเพิ่ม</h3>
        <p>ยอดเยี่ยมมาก ไม่มีออเดอร์ที่ติดปัญหาข้อมูล</p>
      `;
    } else if (activeNoteId) {
      dom.ordersEmptyState.innerHTML = `
        <div class="empty-icon"><i data-lucide="package-plus"></i></div>
        <h3>ยังไม่มีการ์ดออเดอร์ในโน้ตนี้</h3>
        <p>กดปุ่ม "+ เพิ่มออเดอร์ใหม่" ด้านบนเพื่อสร้างออเดอร์แรกของคุณ</p>
      `;
    } else {
      let queryVal = "";
      if (window.innerWidth > 600 && dom.searchNotes && dom.searchNotes.value.trim() !== "") {
        queryVal = dom.searchNotes.value;
      } else {
        queryVal = document.getElementById("search-active-orders")?.value || "";
      }

      if (!queryVal.trim()) {
        dom.ordersEmptyState.innerHTML = `
          <div class="empty-icon" style="background-color: rgba(16, 185, 129, 0.1); color: var(--color-primary);"><i data-lucide="search"></i></div>
          <h3>ระบบค้นหาออเดอร์ 🔍</h3>
          <p>กรอกชื่อลูกค้า เบอร์โทรศัพท์ หรือที่อยู่พัสดุข้างบนเพื่อค้นหาทันที</p>
        `;
      } else {
        dom.ordersEmptyState.innerHTML = `
          <div class="empty-icon"><i data-lucide="search"></i></div>
          <h3>ไม่พบออเดอร์สำหรับ "${queryVal}"</h3>
          <p>ลองค้นหาด้วยคำสำคัญอื่นๆ หรือชื่อลูกค้าอื่นดูครับ</p>
        `;
      }
    }
    dom.ordersCardsListDiv.style.display = "none";
    if (window.lucide) window.lucide.createIcons();
    return;
  }

  dom.ordersEmptyState.style.display = "none";
  dom.ordersCardsListDiv.innerHTML = "";
  dom.ordersCardsListDiv.style.display = "grid";

  displayedOrders.forEach((order, idx) => {
    const card = document.createElement("div");
    const isKeyed = typeof keyedOrders !== 'undefined' ? keyedOrders.has(order.id) : false;
    const paymentStatus = order.paymentStatus || (order.isPaid ? 'paid' : 'unpaid');
    card.className = `order-card fade-in ${paymentStatus} ${order.cardColor ? 'card-color-' + order.cardColor : ''} ${isKeyed ? 'keyed-card' : ''}`;
    card.dataset.id = order.id;

    // [1] คำนวณรายการสินค้า
    let itemsRenderedText = escapeHTML(order.items || "");
    if (order.maxShipments && order.maxShipments > 1 && order.shipmentItems) {
      itemsRenderedText = order.shipmentItems.map((chunk, index) => {
        if (!chunk) return "";
        const bulletLines = chunk.split('\n').map(line => `- ${escapeHTML(line)}`).join('\n');
        return `<strong>ส่งครั้งที่ ${index + 1}:</strong>\n${bulletLines}`;
      }).join('\n\n');
    }

    // [2] คำนวณสถานะรอบและประวัติจัดส่ง
    let shipmentStatusHtml = "";
    const maxShipments = order.maxShipments || 1;
    const currentShipment = order.currentShipment || 1;
    if (maxShipments > 1) {
      shipmentStatusHtml = `
        <div class="shipment-status-summary" style="font-size: 0.75rem; color: var(--color-warning); font-weight: 600; margin-top: 4px; display: flex; align-items: center; gap: 4px;">
          <i data-lucide="layers" style="width: 14px; height: 14px;"></i>
          <span>แบ่งส่งทั้งหมด ${maxShipments} ครั้ง (รอบปัจจุบัน: ส่งครั้งที่ ${currentShipment})</span>
        </div>
      `;
    }

    let historyHtml = "";
    if (order.trackingHistory && order.trackingHistory.length > 0) {
      const historyItemsHtml = order.trackingHistory.map(h => {
        if (!h) return "";
        const carrierText = h.carrier ? `${escapeHTML(h.carrier)} - ` : "";
        const packerText = h.packerName ? ` (คนแพ็ค: ${escapeHTML(h.packerName)})` : "";
        return `
          <div style="display: flex; justify-content: space-between; background: rgba(255,255,255,0.02); padding: 4px 8px; border-radius: 6px; font-size: 0.75rem;">
            <span style="color: var(--color-text-main);">ครั้งที่ ${h.shipment}: ${carrierText}${escapeHTML(h.trackingNumber)}</span>
            <span style="color: var(--color-text-muted);">${packerText}</span>
          </div>
        `;
      }).join("");

      historyHtml = `
        <div class="shipment-history-block" style="margin-top: 4px; border-top: 1px solid rgba(255, 255, 255, 0.04); padding-top: 8px;">
          <div style="font-weight: 700; color: var(--color-primary); font-size: 0.75rem; margin-bottom: 4px;">ประวัติการจัดส่ง (${order.trackingHistory.length}/${maxShipments}):</div>
          <div style="display: flex; flex-direction: column; gap: 4px;">
            ${historyItemsHtml}
          </div>
        </div>
      `;
    }

    // [3] คำนวณกล่องคัดลอกเลขพัสดุ
    let trackingSectionHtml = "";
    const role = localStorage.getItem("userRole") || "Super Admin";
    if (Array.isArray(order.trackingHistory) && order.trackingHistory.length > 0) {
      const historyBars = order.trackingHistory.map(h => {
        if (!h) return '';
        const shipmentVal = h.shipment || '';
        const label = order.maxShipments > 1 ? `ครั้งที่ ${shipmentVal}: ` : '';
        if (h.trackingNumber !== "รับหน้าร้าน/ส่งไรเดอร์") {
          return `
            <div class="address-field-row" style="background-color: var(--color-success-bg); border: 1px solid var(--color-success-border); padding: 8px 10px; margin-top: 6px; display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0;">
                <span style="color: var(--color-success); font-weight: 700; font-size: 0.8rem; white-space: nowrap;">${label}</span>
                <span class="field-label" style="color: var(--color-success); font-weight: 700; width: auto; margin-right: 8px; white-space: nowrap;">${getCarrierLabelHtml(h.carrier)}</span>
                <span class="field-value highlight-phone" style="color: var(--color-success); font-weight: 700; flex: 1; font-size: 0.85rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHTML(h.trackingNumber)}</span>
              </div>
              <button class="btn-copy-field btn-copy-tracking" data-tracking="${escapeHTML(h.trackingNumber)}" data-carrier="${escapeHTML(h.carrier || '')}" title="คัดลอกเลขพัสดุ" style="color: var(--color-success); padding: 2px;">
                <i data-lucide="copy"></i>
              </button>
            </div>
          `;
        } else {
          return `
            <div class="address-field-row" style="background-color: rgba(16, 185, 129, 0.04); border: 1px solid var(--color-success-border); padding: 8px 10px; margin-top: 6px; display: flex; align-items: center; justify-content: center;">
              <span style="color: var(--color-success); font-weight: 700; display: flex; align-items: center; gap: 6px; font-size: 0.85rem;">
                <i data-lucide="check-circle" style="width: 15px; height: 15px;"></i>
                ${label}ดำเนินการเรียบร้อยแล้ว (${escapeHTML(h.carrier || 'รับหน้าร้าน 🏪')})
              </span>
            </div>
          `;
        }
      }).join('');

      trackingSectionHtml += `<div style="margin-top: 4px; border-top: 1px solid rgba(255, 255, 255, 0.04); padding-top: 12px;">${historyBars}</div>`;
    }
    else if (order.trackingNumber || activeNoteId === "completed-queue") {
      trackingSectionHtml += `
        <div style="margin-top: 4px; border-top: 1px solid rgba(255, 255, 255, 0.04); padding-top: 12px;">
          <div class="address-field-row" style="background-color: var(--color-success-bg); border: 1px solid var(--color-success-border); padding: 8px 10px; display: flex; align-items: center; justify-content: space-between;">
            <div style="display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0;">
              <span class="field-label" style="color: var(--color-success); font-weight: 700; width: auto; margin-right: 8px; white-space: nowrap;">${getCarrierLabelHtml(order.carrier)}</span>
              <span class="field-value highlight-phone" style="color: var(--color-success); font-weight: 700; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHTML(order.trackingNumber || '')}</span>
            </div>
            <button class="btn-copy-field btn-copy-tracking" data-tracking="${escapeHTML(order.trackingNumber || '')}" data-carrier="${escapeHTML(order.carrier || '')}" title="คัดลอกเลขพัสดุ" style="color: var(--color-success); padding: 2px;">
              <i data-lucide="copy"></i>
            </button>
          </div>
        </div>
      `;
    }
    else if ((order.deliveryType === 'pickup' || order.deliveryType === 'messenger') && order.pickupDone) {
      trackingSectionHtml += `
        <div style="margin-top: 4px; border-top: 1px solid rgba(255, 255, 255, 0.04); padding-top: 12px;">
          <div class="address-field-row" style="background-color: rgba(16, 185, 129, 0.04); border: 1px solid var(--color-success-border); padding: 8px 10px; justify-content: center;">
            <span style="color: var(--color-success); font-weight: 700; display: flex; align-items: center; gap: 6px; font-size: 0.85rem;">
              <i data-lucide="check-circle" style="width: 15px; height: 15px;"></i>
              ดำเนินการเรียบร้อยแล้ว (${order.deliveryType === 'pickup' ? 'รับหน้าร้าน 🏪' : 'เรียกรถ 🚗'})
            </span>
          </div>
        </div>
      `;
    }

    let liveDetectHtml = 'พิมพ์หรือวางเลขพัสดุเพื่อตรวจจับขนส่งอัตโนมัติ';
    if (order.trackingNumber) {
      const carrierObj = detectCarrier(order.trackingNumber);
      if (carrierObj) {
        liveDetectHtml = `ตรวจพบขนส่ง: <span style="color: #38bdf8; font-weight: 700; display: inline-flex; align-items: center; gap: 3px;"><i data-lucide="${carrierObj.icon}" style="width:12px; height:12px;"></i> ${carrierObj.label}</span>`;
      } else {
        liveDetectHtml = `ตรวจพบขนส่ง: <span style="color: var(--color-text-muted); font-weight: 700; display: inline-flex; align-items: center; gap: 3px;"><i data-lucide="truck" style="width:12px; height:12px;"></i> อื่นๆ 🌐</span>`;
      }
    }

    if (order.readyToShip && order.isPacked && order.deliveryType === 'express' && !order.trackingNumber && activeNoteId !== "completed-queue") {
      const placeholderText = order.maxShipments > 1 ? `เลขพัสดุจัดส่งรอบที่ ${order.currentShipment}...` : "เลขพัสดุจัดส่ง...";
      trackingSectionHtml += `
        <div class="tracking-input-wrapper" style="flex-direction: column; gap: 6px; margin-top: 12px; border-top: 1px solid rgba(255, 255, 255, 0.04); padding-top: 12px;">
          <div class="live-carrier-detect" id="live-carrier-${order.id}" style="font-size: 0.72rem; color: var(--color-text-muted); display: flex; align-items: center; gap: 4px; padding-left: 2px;">
            ${liveDetectHtml}
          </div>
          <div style="display: flex; gap: 8px; width: 100%;">
            <div class="tracking-input-row" style="flex: 1;">
              <i data-lucide="truck"></i>
              <input type="text" class="tracking-input" id="tracking-input-${order.id}" placeholder="${placeholderText}" value="" data-id="${order.id}">
            </div>
            <button class="btn btn-primary btn-tracking-save" data-id="${order.id}">บันทึก</button>
          </div>
        </div>
      `;
    }

    // [4] คำนวณปุ่ม Undo
    let undoButtonHtml = "";
    const hasUndoPermission = canUndoOrder(order, getUserRoles());

    const showUndo = ((activeNoteId === "undo-queue") ||
      (order.maxShipments > 1 && order.currentShipment > 1 && !order.readyToShip && hasUndoPermission)) && isToday(order.updatedAt);

    if (showUndo) {
      undoButtonHtml = `
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border-color); display: flex; justify-content: flex-end;">
          <button type="button" class="btn btn-primary btn-undo-action" data-id="${order.id}" style="background-color: var(--color-warning); color: #fff; border: none; font-size: 0.8rem; font-weight: 700; gap: 6px; padding: 6px 12px; display: inline-flex; align-items: center; border-radius: 6px; cursor: pointer;">
            <i data-lucide="undo-2" style="width: 14px; height: 14px;"></i>
            <span>ดึงงานกลับ (Undo) ↩️</span>
          </button>
        </div>
      `;
    }

    const isDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน");
    const orderIndex = isDailyNote ? (displayedOrders.length - idx) : (idx + 1);
    const canExportOrderCard = hasRole(["Super Admin", "Order Manager"]);

    let parentBadge = "";
    if (activeNoteId === null || activeNoteId === "prepare-queue" || activeNoteId === "shipping-queue" || activeNoteId === "completed-queue" || activeNoteId === "pickup-queue") {
      const parentNote = allNotes.find(n => n.id === order.noteId);
      const parentTitle = parentNote ? parentNote.title : "โน้ตทั่วไป";
      parentBadge = `<span class="platform-tag" style="background-color: var(--color-success-bg); color: var(--color-success); border: 1px solid var(--color-success-border); display: inline-block; white-space: normal; word-break: break-word; max-width: 100%; line-height: 1.4; padding: 2px 8px; margin-top: 4px;">📌 ${escapeHTML(parentTitle)}</span>`;
    }

    // Calculate dynamic workflow sticker styles
    let readyStickerClass = "";
    let readyStickerIcon = "package";
    let readyStickerText = "เตรียมของ";

    if (order.deliveryType === 'pickup' || order.deliveryType === 'messenger') {
      if (order.readyToShip) {
        if (order.pickupDone) {
          readyStickerClass = "active ready";
          readyStickerIcon = "check-circle";
          readyStickerText = "สำเร็จ";
        } else if (order.isPacked) {
          readyStickerClass = "active pickup-route";
          readyStickerIcon = "store";
          readyStickerText = "รับของแล้ว";
        } else {
          readyStickerClass = "packed";
          readyStickerIcon = "package";
          readyStickerText = "แพ็คเสร็จแล้ว";
        }
      } else {
        readyStickerClass = "";
        readyStickerIcon = "package";
        readyStickerText = "เตรียมของ";
      }
    } else {
      // Express / Postal shipping workflow
      if (order.trackingNumber) {
        readyStickerClass = "active ready";
        readyStickerIcon = "check-circle";
        readyStickerText = "สำเร็จ";
      } else if (order.readyToShip) {
        if (order.isPacked) {
          readyStickerClass = "active ready";
          readyStickerIcon = "truck";
          readyStickerText = "รอเลขออเดอร์";
        } else {
          readyStickerClass = "packed";
          readyStickerIcon = "package";
          readyStickerText = "แพ็คเสร็จแล้ว";
        }
      } else {
        readyStickerClass = "";
        readyStickerIcon = "package";
        readyStickerText = "เตรียมของ";
      }
    }

    // Show readySticker in all boards and note lists
    const hideReadySticker = false;
    const isQueuePage = (activeNoteId === "prepare-queue" || activeNoteId === "shipping-queue" || activeNoteId === "completed-queue" || activeNoteId === "pickup-queue" || activeNoteId === "undo-queue");

    let pickupStickerHtml = "";
    let messengerStickerHtml = "";
    let expressStickerHtml = "";

    if (!isQueuePage || order.deliveryType === 'pickup') {
      pickupStickerHtml = `
        <span class="sticker sticker-delivery ${order.deliveryType === 'pickup' ? 'active pickup' : ''}" data-id="${order.id}" data-type="pickup">
          🏪 หน้าร้าน
        </span>
      `;
    }
    if (!isQueuePage || order.deliveryType === 'messenger') {
      messengerStickerHtml = `
        <span class="sticker sticker-delivery ${order.deliveryType === 'messenger' ? 'active messenger' : ''}" data-id="${order.id}" data-type="messenger">
          🚗 เรียกรถ
        </span>
      `;
    }
    if (!isQueuePage || order.deliveryType === 'express') {
      expressStickerHtml = `
        <span class="sticker sticker-delivery ${order.deliveryType === 'express' ? 'active express' : ''}" data-id="${order.id}" data-type="express">
          📦 ส่งพัสดุ
        </span>
      `;
    }

    const checkboxHtml = isTargetNote && isBatchSelectMode && !order.readyToShip
      ? `<input type="checkbox" class="order-select-checkbox" data-id="${order.id}" style="width: 18px; height: 18px; cursor: pointer; accent-color: var(--color-primary); flex-shrink: 0; align-self: center; margin-right: 8px;">`
      : '';

    card.innerHTML = `
      <div class="card-header-row" style="display: flex; align-items: center; gap: 8px;">
        ${checkboxHtml}
        <div class="customer-meta" style="flex: 1; min-width: 0;">
          <div class="customer-fb">
            <i data-lucide="user" style="width: 14px; height:14px; color:var(--color-primary);"></i>
            <span><span class="order-card-index">${orderIndex}. </span><span class="order-card-customer-name">${escapeHTML(order.customerName) || "ลูกค้า"}</span></span>
            ${order.platform ? `<span class="platform-tag">${escapeHTML(order.platform)}</span>` : ''}
            ${order.packerName ? `<span class="platform-tag order-packer-tag" style="background-color: var(--color-success-bg); color: var(--color-success); border-color: var(--color-success-border); display: inline-flex; align-items: center; gap: 4px;"><i data-lucide="package" style="width: 10px; height: 10px;"></i> ${escapeHTML(order.packerName)}</span>` : ''}
          </div>
          ${parentBadge ? `<div style="display: flex; flex-wrap: wrap; width: 100%; max-width: 100%;">${parentBadge}</div>` : ''}
        </div>
        <div class="header-actions" style="display: flex; gap: 8px;">
          ${canExportOrderCard ? `
            <div class="order-card-export-actions" aria-label="Order card image actions">
              <button type="button" class="btn-order-card-image btn-copy-order-card-image" data-id="${order.id}" title="คัดลอกรูปการ์ดออเดอร์">
                <i data-lucide="image"></i>
              </button>
              <button type="button" class="btn-order-card-image btn-download-order-card-image" data-id="${order.id}" title="ดาวน์โหลดรูปการ์ดออเดอร์">
                <i data-lucide="download"></i>
              </button>
            </div>
          ` : ''}
          ${(() => {
        const isDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน");
        if (hasRole(["Super Admin", "Order Manager"]) || (hasRole("Packer & Picker") && isDailyNote)) {
          return `
                ${hasRole(["Super Admin", "Order Manager"]) ? `
                <button class="btn-color-picker" data-id="${order.id}" title="เปลี่ยนสีการ์ด" style="color: var(--color-text-main); background: transparent; border: none; cursor: pointer; padding: 4px; display: flex; align-items: center; border-radius: 6px; transition: all 0.2s;">
                  <i data-lucide="palette" style="width: 15px; height: 15px;"></i>
                </button>
                ` : ''}
                <button class="btn-edit-order" data-id="${order.id}" title="แก้ไขการ์ดออเดอร์">
                  <i data-lucide="edit-3"></i>
                </button>
                <button class="btn-delete-order" data-id="${order.id}" title="ลบการ์ดออเดอร์" style="color: var(--color-danger); background: transparent; border: none; cursor: pointer; padding: 4px; display: flex; align-items: center; border-radius: 6px; transition: all 0.2s;">
                  <i data-lucide="trash-2" style="width: 15px; height: 15px;"></i>
                </button>
              `;
        }
        return '';
      })()}
          ${(activeNoteId === "shipping-queue" && hasRole(["Super Admin", "Shipper"])) ? `
            <button class="btn-toggle-keyed ${typeof isKeyed !== 'undefined' && isKeyed ? 'active' : ''}" data-id="${order.id}" title="${typeof isKeyed !== 'undefined' && isKeyed ? 'ยกเลิกมาร์ค' : 'มาร์คว่าคีย์แล้ว'}" style="color: ${typeof isKeyed !== 'undefined' && isKeyed ? 'var(--color-success)' : 'var(--color-text-muted)'}; background: ${typeof isKeyed !== 'undefined' && isKeyed ? 'var(--color-success-bg)' : 'rgba(0,0,0,0.05)'}; border: ${typeof isKeyed !== 'undefined' && isKeyed ? '1px solid var(--color-success)' : '1px solid transparent'}; cursor: pointer; padding: 4px 8px; display: flex; align-items: center; border-radius: 6px; transition: all 0.2s; font-size: 0.75rem; font-weight: bold; gap: 4px;">
              <i data-lucide="check-square" style="width: 14px; height: 14px;"></i> ${typeof isKeyed !== 'undefined' && isKeyed ? 'คีย์แล้ว' : 'คีย์แล้ว'}
            </button>
          ` : ''}
          ${(activeNoteId === "pending-info-queue" && hasRole(["Super Admin", "Order Manager"])) ? `
            <button class="btn-clear-pending" data-id="${order.id}" title="ข้อมูลครบแล้ว ส่งกลับคิวเดิม" style="color: var(--color-success); background: var(--color-success-bg); border: 1px solid var(--color-success); cursor: pointer; padding: 4px 8px; display: flex; align-items: center; border-radius: 6px; transition: all 0.2s; font-size: 0.75rem; font-weight: bold; gap: 4px;">
              <i data-lucide="check-circle" style="width: 14px; height: 14px;"></i> ส่งข้อมูลกลับ
            </button>
          ` : ''}
          ${(!order.isPendingInfo && isQueuePage && activeNoteId !== "pending-info-queue" && hasRole(["Super Admin", "Shipper", "Packer & Picker"])) ? `
            <button class="btn-set-pending" data-id="${order.id}" title="ออเดอร์มีปัญหา ส่งไปขอข้อมูลเพิ่ม" style="color: var(--color-warning); background: rgba(234, 179, 8, 0.15); border: 1px solid transparent; cursor: pointer; padding: 4px; display: flex; align-items: center; border-radius: 6px; transition: all 0.2s;">
              <i data-lucide="help-circle" style="width: 15px; height: 15px;"></i>
            </button>
          ` : ''}
        </div>
      </div>
      
      <!-- Stickers Panel: อัปเดต Firestore เด้งด่วนแบบเรียลไทม์เมื่อกดจิ้มบนการ์ด -->
      <div class="stickers-container ${isQueuePage ? 'readonly' : ''}">
        ${(() => {
        const status = order.paymentStatus || (order.isPaid ? 'paid' : 'unpaid');
        if (status === 'paid') {
          return `
              <span class="sticker sticker-payment paid" data-id="${order.id}">
                <i data-lucide="check-circle"></i>
                <span>ชำระเงินแล้ว</span>
              </span>
            `;
        } else if (status === 'deposit') {
          return `
              <span class="sticker sticker-payment deposit" data-id="${order.id}">
                <i data-lucide="coins"></i>
                <span>มัดจำแล้ว (${order.depositAmount || 0} ฿)</span>
              </span>
            `;
        } else {
          return `
              <span class="sticker sticker-payment unpaid" data-id="${order.id}">
                <i data-lucide="clock"></i>
                <span>รอชำระเงิน</span>
              </span>
            `;
        }
      })()}
        ${pickupStickerHtml}
        ${messengerStickerHtml}
        ${expressStickerHtml}
        ${hideReadySticker ? "" : `
        <span class="sticker sticker-ready ${readyStickerClass}" data-id="${order.id}">
          <i data-lucide="${readyStickerIcon}"></i>
          <span>${readyStickerText}</span>
        </span>
        `}
      </div>
      
      ${order.adminNote ? `
      <div style="background-color: var(--color-warning-bg); border-left: 4px solid var(--color-warning); padding: 10px 12px; margin: 12px 0; border-radius: 4px;">
        <div style="display: flex; align-items: center; gap: 6px; color: var(--color-warning); font-weight: 700; font-size: 0.85rem; margin-bottom: 4px;">
          <i data-lucide="alert-circle" style="width: 14px; height: 14px;"></i> หมายเหตุ (Order Manager)
        </div>
        <div style="color: var(--color-text-main); font-size: 0.9rem; white-space: pre-wrap; line-height: 1.4;">${escapeHTML(order.adminNote)}</div>
      </div>
      ` : ''}

      <!-- Address Grid: แสดงข้อมูลย่อยแบบสะอาดล้างเขต/คำนำออกหมดแล้ว -->
      <div class="address-grid">
        ${renderFieldRow("ผู้รับ", order.thaiName)}
        ${renderFieldRow("ที่อยู่", order.addressBody, false, true)}
        ${renderFieldRow("ตำบล", order.subDistrict)}
        ${renderFieldRow("อำเภอ", order.district)}
        ${renderFieldRow("จังหวัด", order.province)}
        ${renderFieldRow("รหัสไปรษณีย์", order.postalCode)}
        ${renderFieldRow("เบอร์โทร", order.phone, true)}
      </div>
      
      <!-- Items Block -->
      ${order.items ? `
        <div class="card-items-block">
          <h4>รายการที่สั่งซื้อ</h4>
          <p>${itemsRenderedText}</p>
        </div>
      ` : ''}

      <!-- Shipment Status & History -->
      ${shipmentStatusHtml}
      ${historyHtml}
      
      <!-- Collapsible Original Text Section: ดึงข้อความดิบดั้งเดิมออกมาเช็กได้เมื่อกางออก -->
      <button class="collapsible-trigger" data-id="${order.id}">
        <i data-lucide="chevron-down"></i>
        <span>ดูข้อความดั้งเดิม</span>
      </button>
      <div class="collapsible-content" id="collapsible-${order.id}">
        <pre class="original-raw-text">${escapeHTML(order.rawText || "")}</pre>
      </div>
      <!-- Tracking Section: ระบบจัดการการส่งหน้าร้าน/ไรเดอร์ หรือลงเลขพัสดุสำหรับปณ./ขนส่งทั่วไป -->
      ${trackingSectionHtml}
      ${undoButtonHtml}
    `;

    dom.ordersCardsListDiv.appendChild(card);
  });

  // Initialize dynamic carrier badges after appending cards
  displayedOrders.forEach(order => {
    if (order.deliveryType === 'express' && activeNoteId !== "completed-queue") {
      updateLiveCarrierBadge(order.id, order.trackingNumber || "", order.carrier);
    }
  });

  updatePDFUploadButtonVisibility();
  if (window.lucide) window.lucide.createIcons();
}

// 7. จัดการเปิดลิ้นชักฟอร์มขวาเพื่อ เพิ่ม / แก้ไขออเดอร์ย่อย (Smart Entry drawer control)
function showForm(orderId = null, defaultReady = false, defaultPaid = false) {
  const readyStatusGroup = document.getElementById("form-group-ready-status");
  const trackingGroup = document.getElementById("form-group-tracking-number");
  const carrierGroup = document.getElementById("form-group-carrier");
  const roles = getUserRoles();
  const isSuperAdmin = roles.includes("Super Admin");

  if (orderId) {
    const order = allOrders.find(o => o.id === orderId);
    if (!order) return;

    isQuickOrderMode = (order.platform === "" || !order.platform);
    isPaymentStatusLocked = isQuickOrderMode;

    if (formInputs.paymentStatusGroup) {
      formInputs.paymentStatusGroup.style.display = isQuickOrderMode ? "none" : "";
    }
    if (readyStatusGroup) readyStatusGroup.style.display = "";
    if (trackingGroup) trackingGroup.style.display = isSuperAdmin ? "" : "none";
    if (carrierGroup) carrierGroup.style.display = isSuperAdmin ? "" : "none";
    document.getElementById("form-title").innerHTML = `<i data-lucide="edit-3"></i> ${isQuickOrderMode ? 'แก้ไขการ์ดออเดอร์ด่วน' : 'แก้ไขการ์ดออเดอร์'}`;

    if (dom.btnFormUndo) {
      const hasPermission = canUndoOrder(order, roles);
      dom.btnFormUndo.style.display = hasPermission ? "inline-flex" : "none";
    }

    formInputs.orderId.value = order.id;
    formInputs.rawText.value = order.rawText || "";
    formInputs.customerName.value = order.customerName || "";
    formInputs.thaiName.value = order.thaiName || "";

    formPlatformStickers.forEach(btn => btn.classList.remove("active"));
    const orderPlat = order.platform || "ในเพจ";
    const targetPlatBtn = formPlatformStickers.find(btn => btn.getAttribute("data-value") === orderPlat);
    if (targetPlatBtn) {
      targetPlatBtn.classList.add("active");
    } else {
      document.getElementById("form-platform-page").classList.add("active");
    }
    formInputs.phone.value = order.phone || "";
    formInputs.addressBody.value = order.addressBody || "";
    formInputs.subDistrict.value = order.subDistrict || "";
    formInputs.district.value = order.district || "";
    formInputs.province.value = order.province || "";
    formInputs.postalCode.value = order.postalCode || "";
    formInputs.items.value = order.items || "";
    formInputs.adminNote.value = order.adminNote || "";
    formInputs.trackingNumber.value = order.trackingNumber || "";
    formInputs.carrier.value = order.carrier || "";

    const paymentStatus = order.paymentStatus || (order.isPaid ? "paid" : "unpaid");
    formInputs.statusPaid.classList.remove("active");
    formInputs.statusUnpaid.classList.remove("active");
    formInputs.statusDeposit.classList.remove("active");

    if (paymentStatus === "paid") {
      formInputs.statusPaid.classList.add("active");
      formInputs.depositAmountGroup.style.display = "none";
    } else if (paymentStatus === "deposit") {
      formInputs.statusDeposit.classList.add("active");
      formInputs.depositAmountGroup.style.display = "block";
    } else {
      formInputs.statusUnpaid.classList.add("active");
      formInputs.depositAmountGroup.style.display = "none";
    }
    formInputs.depositAmount.value = order.depositAmount || "";

    formDeliveryStickers.forEach(s => s.classList.remove("active"));
    if (order.deliveryType === "pickup") {
      formInputs.deliveryPickup.classList.add("active");
    } else if (order.deliveryType === "messenger") {
      formInputs.deliveryMessenger.classList.add("active");
    } else {
      formInputs.deliveryExpress.classList.add("active");
    }

    if (order.readyToShip) {
      formInputs.statusReady.classList.add("active");
      formInputs.statusUnready.classList.remove("active");
    } else {
      formInputs.statusUnready.classList.add("active");
      formInputs.statusReady.classList.remove("active");
    }
  } else {
    isQuickOrderMode = defaultPaid;
    isPaymentStatusLocked = defaultPaid;
    if (formInputs.paymentStatusGroup) {
      formInputs.paymentStatusGroup.style.display = defaultPaid ? "none" : "";
    }
    if (readyStatusGroup) readyStatusGroup.style.display = "none";
    if (trackingGroup) trackingGroup.style.display = "none";
    if (carrierGroup) carrierGroup.style.display = "none";
    document.getElementById("form-title").innerHTML = `<i data-lucide="user-plus"></i> ${defaultPaid ? 'สร้างการ์ดออเดอร์ด่วน' : 'สร้างการ์ดออเดอร์'}`;

    if (dom.btnFormUndo) {
      dom.btnFormUndo.style.display = "none";
    }

    formInputs.orderId.value = "";
    formInputs.rawText.value = "";
    formInputs.customerName.value = "";
    formInputs.thaiName.value = "";

    formPlatformStickers.forEach(btn => btn.classList.remove("active"));
    document.getElementById("form-platform-page").classList.add("active");
    formInputs.phone.value = "";
    formInputs.addressBody.value = "";
    formInputs.subDistrict.value = "";
    formInputs.district.value = "";
    formInputs.province.value = "";
    formInputs.postalCode.value = "";
    formInputs.items.value = "";
    formInputs.adminNote.value = "";
    formInputs.trackingNumber.value = "";
    formInputs.carrier.value = "";

    if (defaultPaid) {
      formInputs.statusPaid.classList.add("active");
      formInputs.statusUnpaid.classList.remove("active");
      formInputs.statusDeposit.classList.remove("active");
    } else {
      formInputs.statusUnpaid.classList.add("active");
      formInputs.statusPaid.classList.remove("active");
      formInputs.statusDeposit.classList.remove("active");
    }
    formInputs.depositAmount.value = "";
    formInputs.depositAmountGroup.style.display = "none";

    formDeliveryStickers.forEach(s => s.classList.remove("active"));
    formInputs.deliveryExpress.classList.add("active");

    if (defaultReady) {
      formInputs.statusReady.classList.add("active");
      formInputs.statusUnready.classList.remove("active");
    } else {
      formInputs.statusUnready.classList.add("active");
      formInputs.statusReady.classList.remove("active");
    }
  }

  // ซ่อนฟิลด์ชื่อ User และช่องทางการขายในโหมดออเดอร์ด่วน
  if (formInputs.customerNameGroup) {
    formInputs.customerNameGroup.style.display = isQuickOrderMode ? "none" : "";
  }
  if (formInputs.platformGroup) {
    formInputs.platformGroup.style.display = isQuickOrderMode ? "none" : "";
  }
  const normalPlaceholder = "ตัวอย่างแพทเทิร์นในการกรอก\n\n[ชื่อ User] [ในเเพจ, ใน ig, ในไลน์, หน้าร้าน, ส่วนตัว]\n[ชื่อจริงผู้รับ]\n[ที่อยู่หลัก][แขวง/ตำบล][เขต/อำเภอ][จังหวัด][รหัสไปรษณีย์]\n[เบอร์โทรศัพท์]\n\n[รายการสั่งซื้อ1]\n\n[รายการสั่งซื้อ2]\n\n[รายการสั่งซื้อ3]\n[มารับเอง, ถ้าไม่ได้ใส่คือส่งพัสดุ] [จ่ายแล้ว, มัดจำแล้ว [จำนวนเงิน], ถ้าไม่ใส่คือยังรอชำระเงิน]";
  const quickPlaceholder = "ตัวอย่างแพทเทิร์นในการกรอก\n\n[ชื่อจริงผู้รับ]\n[ที่อยู่หลัก][แขวง/ตำบล][เขต/อำเภอ][จังหวัด][รหัสไปรษณีย์]\n[เบอร์โทรศัพท์]\n\n[รายการสั่งซื้อ]";

  formInputs.rawText.placeholder = isQuickOrderMode ? quickPlaceholder : normalPlaceholder;

  dom.appContainer.classList.add("show-form");
  if (isMobile) {
    setMobilePane("show-orders");
  }

  if (window.lucide) window.lucide.createIcons();

  // โฟกัสไปช่องกล่องพิมพ์ทันทีเพื่อความสะดวกรวดเร็วของทีมงาน
  setTimeout(() => {
    formInputs.rawText.focus();
  }, 100);
}

function closeForm(shouldGoBack = true) {
  dom.appContainer.classList.remove("show-form");
  if (isMobile) {
    if (shouldGoBack && history.state && history.state.pane === "show-orders") {
      window.history.back();
      return;
    }
    setMobilePane("show-editor");
  }
}

// 8. ดำเนินการกดบันทึกข้อมูลออเดอร์ในฟอร์มขวาขึ้น Firebase
async function saveOrderData() {
  if (!activeNoteId) {
    showToast("กรุณาเลือกหัวข้อโน้ตก่อนครับ", "danger");
    return;
  }

  const orderId = formInputs.orderId.value;
  const rawText = formInputs.rawText.value.trim();
  const thaiName = formInputs.thaiName.value.trim();
  const phone = formInputs.phone.value.trim();
  const addressBody = formInputs.addressBody.value.trim();
  const subDistrict = formInputs.subDistrict.value.trim();
  const district = formInputs.district.value.trim();
  const province = formInputs.province.value.trim();
  const postalCode = formInputs.postalCode.value.trim();
  const items = formInputs.items.value.trim();
  const adminNote = formInputs.adminNote.value.trim();
  const trackingNumber = formInputs.trackingNumber.value.trim();
  let carrier = formInputs.carrier.value.trim();

  // Auto-detect carrier if tracking number is provided but carrier select is empty
  if (trackingNumber && !carrier) {
    const carrierObj = detectCarrier(trackingNumber);
    if (carrierObj) {
      carrier = carrierObj.name;
    } else {
      carrier = "อื่นๆ";
    }
  }

  let paymentStatus = "unpaid";
  if (formInputs.statusPaid.classList.contains("active")) {
    paymentStatus = "paid";
  } else if (formInputs.statusDeposit.classList.contains("active")) {
    paymentStatus = "deposit";
  }
  const isPaid = paymentStatus === "paid";
  const depositAmount = paymentStatus === "deposit" ? (formInputs.depositAmount.value.trim() || "") : "";

  let deliveryType = "express";
  if (formInputs.deliveryPickup.classList.contains("active")) {
    deliveryType = "pickup";
  } else if (formInputs.deliveryMessenger.classList.contains("active")) {
    deliveryType = "messenger";
  }

  // If a tracking number is specified, automatically force/support readyToShip = true
  const selectedReady = formInputs.statusReady.classList.contains("active");
  const readyToShip = selectedReady || !!trackingNumber;

  // Calculate shipmentItems and maxShipments
  const getShipmentItemsFromText = (text) => {
    if (!text) return [];
    const paragraphs = text.split(/\n\s*\n+/);
    return paragraphs.map(p => {
      return p.split('\n')
        .map(line => line.replace(/^\s*[-\*•]\s*/, "").trim())
        .filter(line => line !== "")
        .join('\n');
    }).filter(p => p !== "");
  };

  const shipmentItems = isQuickOrderMode ? [items] : getShipmentItemsFromText(items);
  const maxShipments = isQuickOrderMode ? 1 : (shipmentItems.length > 0 ? shipmentItems.length : 1);

  if (!rawText && !thaiName) {
    showToast("กรุณาระบุที่อยู่อย่างน้อยชื่อ หรือข้อความดิบครับ", "danger");
    return;
  }

  // แกะข้อมูลแพลตฟอร์มและชื่อหลักของลูกค้าจากบรรทัดแรก (ถ้าจัดแต่งรูปแบบมา)
  let textToParse = rawText;
  if (!/^\s*\d+\s*\./.test(textToParse)) {
    textToParse = "1. " + textToParse;
  }

  const parsedList = isQuickOrderMode ? parseQuickOrderContent(textToParse) : parseNoteContent(textToParse);
  const parsed = (parsedList && parsedList.length > 0) ? parsedList[0] : {};
  const formCustName = formInputs.customerName.value.trim();
  const customerName = formCustName || parsed.customerName || thaiName || "ลูกค้า";

  let platform = isQuickOrderMode ? "" : "ในเพจ";
  if (!isQuickOrderMode) {
    const activePlatBtn = formPlatformStickers.find(btn => btn.classList.contains("active"));
    if (activePlatBtn) {
      platform = activePlatBtn.getAttribute("data-value");
    }
  }

  dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
  if (window.lucide) window.lucide.createIcons();

  try {
    if (orderId) {
      // โหมดอัปเดตการ์ดเดิม
      const orderRef = doc(db, "orders", orderId);
      const existingOrder = allOrders.find(o => o.id === orderId);
      const pickupDone = existingOrder ? (existingOrder.pickupDone || false) : false;
      const isPacked = !!trackingNumber || (existingOrder ? (existingOrder.isPacked || false) : false);
      const trackingHistory = (existingOrder && existingOrder.trackingHistory) ? existingOrder.trackingHistory : [];
      const currentShipment = Math.min((existingOrder && existingOrder.currentShipment) ? existingOrder.currentShipment : 1, maxShipments);

      await updateDoc(orderRef, {
        rawText,
        customerName,
        platform,
        thaiName,
        phone,
        addressBody,
        subDistrict,
        district,
        province,
        postalCode,
        items,
        adminNote,
        isPaid,
        paymentStatus,
        depositAmount,
        deliveryType,
        readyToShip,
        isPacked,
        pickupDone,
        trackingNumber,
        carrier,
        maxShipments,
        shipmentItems,
        currentShipment,
        trackingHistory,
        updatedAt: serverTimestamp(),
        ...( (trackingNumber || pickupDone) && (!existingOrder || (!existingOrder.trackingNumber && !existingOrder.pickupDone)) ? { completedAt: serverTimestamp() } : {} )
      });
      showToast("อัปเดตข้อมูลการ์ดออเดอร์เรียบร้อย!");
    } else {
      // โหมดสร้างการ์ดใบใหม่
      const isPacked = !!trackingNumber;
      await addDoc(collection(db, "orders"), {
        noteId: activeNoteId,
        rawText,
        customerName,
        platform,
        thaiName,
        phone,
        addressBody,
        subDistrict,
        district,
        province,
        postalCode,
        items,
        adminNote,
        isPaid,
        paymentStatus,
        depositAmount,
        deliveryType,
        readyToShip,
        isPacked,
        pickupDone: false,
        trackingNumber,
        carrier,
        maxShipments,
        shipmentItems,
        currentShipment: 1,
        trackingHistory: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...( trackingNumber ? { completedAt: serverTimestamp() } : {} )
      });
      showToast("สร้างการ์ดคำสั่งซื้อใหม่สำเร็จ!");
    }

    // อัปเดตวันเวลาขยับโน้ตแม่ขึ้นบนสุดของรายการ sidebar
    await updateDoc(doc(db, "notes", activeNoteId), {
      updatedAt: serverTimestamp()
    });

    dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
    if (window.lucide) window.lucide.createIcons();

    closeForm();
  } catch (error) {
    console.error("Error saving order:", error);
    showToast("เกิดข้อผิดพลาดในการบันทึกข้อมูล", "danger");
    dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
    if (window.lucide) window.lucide.createIcons();
  }
}

// 9. ระบบช่วย Live Auto-fill บนหน้าฟอร์ม (วิเคราะห์ระหว่างพิมพ์/วาง)
function handleLiveAutofill() {
  const rawText = formInputs.rawText.value.trim();
  if (!rawText) return;

  let textToParse = rawText;
  if (!/^\s*\d+\s*\./.test(textToParse)) {
    textToParse = "1. " + textToParse;
  }

  const parsedList = isQuickOrderMode ? parseQuickOrderContent(textToParse) : parseNoteContent(textToParse);
  if (parsedList && parsedList.length > 0) {
    const parsed = parsedList[0];

    formInputs.customerName.value = parsed.customerName || "";
    formInputs.thaiName.value = parsed.thaiName || "";

    formPlatformStickers.forEach(btn => btn.classList.remove("active"));
    const parsedPlat = parsed.platform || "ในเพจ";
    const targetPlatBtn = formPlatformStickers.find(btn => btn.getAttribute("data-value") === parsedPlat);
    if (targetPlatBtn) {
      targetPlatBtn.classList.add("active");
    } else {
      document.getElementById("form-platform-page").classList.add("active");
    }
    formInputs.phone.value = parsed.phone || "";
    formInputs.addressBody.value = parsed.addressBody || "";
    formInputs.subDistrict.value = parsed.subDistrict || "";
    formInputs.district.value = parsed.district || "";
    formInputs.province.value = parsed.province || "";
    formInputs.postalCode.value = parsed.postalCode || "";

    let itemsVal = "";
    if (parsed.shipmentItems && parsed.shipmentItems.length > 0) {
      itemsVal = parsed.shipmentItems.map(chunk => {
        return chunk.split('\n').map(i => "- " + i).join("\n");
      }).join("\n\n");
    } else if (parsed.items) {
      itemsVal = typeof parsed.items === 'string' ? parsed.items : parsed.items.map(i => "- " + i).join("\n");
    }
    // ดึงโน้ตสำคัญเพิ่มเติม เช่น ส่งพร้อมเทอร์โบ/ฝากส่ง มารวมไว้ในช่องรายการสินค้าเพื่อไม่ให้ข้อมูลสูญหาย
    if (parsed.notes) {
      if (itemsVal) itemsVal += "\n";
      itemsVal += parsed.notes;
    }
    formInputs.items.value = itemsVal;

    // อัปเดตสติกเกอร์การชำระเงินและข้อมูลมัดจำจากข้อความลูกค้า
    if (!isPaymentStatusLocked) {
      const paymentStatus = parsed.paymentStatus || (parsed.isPaid ? "paid" : "unpaid");
      formInputs.statusPaid.classList.remove("active");
      formInputs.statusUnpaid.classList.remove("active");
      formInputs.statusDeposit.classList.remove("active");

      if (paymentStatus === "paid") {
        formInputs.statusPaid.classList.add("active");
        formInputs.depositAmountGroup.style.display = "none";
        formInputs.depositAmount.value = "";
      } else if (paymentStatus === "deposit") {
        formInputs.statusDeposit.classList.add("active");
        formInputs.depositAmountGroup.style.display = "block";
        formInputs.depositAmount.value = parsed.depositAmount || "";
      } else {
        formInputs.statusUnpaid.classList.add("active");
        formInputs.depositAmountGroup.style.display = "none";
        formInputs.depositAmount.value = "";
      }
    } else {
      // เมื่อถูกล็อก ให้การันตีว่าเป็น paid เสมอ ไม่ว่าจะกรอกหรือวางข้อความอะไร
      formInputs.statusPaid.classList.add("active");
      formInputs.statusUnpaid.classList.remove("active");
      formInputs.statusDeposit.classList.remove("active");
      formInputs.depositAmountGroup.style.display = "none";
      formInputs.depositAmount.value = "";
    }

    // อัปเดตสติกเกอร์ช่องทางจัดส่ง
    formDeliveryStickers.forEach(s => s.classList.remove("active"));
    if (parsed.deliveryType === "pickup") {
      formInputs.deliveryPickup.classList.add("active");
    } else if (parsed.deliveryType === "messenger") {
      formInputs.deliveryMessenger.classList.add("active");
    } else {
      formInputs.deliveryExpress.classList.add("active");
    }
  }
}

// 10. สร้างโน้ตแม่รายการหัวข้อใหม่
function createNewNote() {
  const newNotePayload = {
    title: "หัวข้อสั่งซื้อออเดอร์ใหม่",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
  if (window.lucide) window.lucide.createIcons();

  addDoc(collection(db, "notes"), newNotePayload)
    .then((docRef) => {
      showToast("สร้างหัวข้อโน้ตหลักสำเร็จ!");
      selectNote(docRef.id);
      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();
    })
    .catch((err) => {
      console.error("Error creating note:", err);
      showToast("เกิดข้อผิดพลาดในการสร้างหัวข้อโน้ต", "danger");
      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();
    });
}

// 10.5 สร้างออเดอร์ด่วนและเปิดฟอร์มกรอกข้อมูลตามปกติ
let isCreatingQuickOrder = false;
async function createQuickOrder() {
  if (isCreatingQuickOrder) return;
  isCreatingQuickOrder = true;

  const currentYear = new Date().getFullYear();
  const targetNoteTitle = `ออเดอร์ประจำวัน ${currentYear}`;

  dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
  if (window.lucide) window.lucide.createIcons();

  try {
    // 1. ค้นหาโน้ตจากเซิร์ฟเวอร์โดยตรง เพื่อป้องกันการสร้างซ้ำ
    const notesRef = collection(db, "notes");
    const q = query(notesRef, where("title", "==", targetNoteTitle));
    const querySnapshot = await getDocs(q);
    
    let noteId;
    if (!querySnapshot.empty) {
      noteId = querySnapshot.docs[0].id;
    } else {
      // 2. ถ้าเซิร์ฟเวอร์ไม่มีจริงๆ ถึงจะสร้างใหม่ โดยล็อค ID ไว้ (ป้องกันการชนกันร้อยเปอร์เซ็นต์)
      const deterministicDocId = `daily_order_${currentYear}`;
      const newNotePayload = {
        title: targetNoteTitle,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = doc(db, "notes", deterministicDocId);
      await setDoc(docRef, newNotePayload, { merge: true });
      noteId = deterministicDocId;
      showToast(`สร้างโน้ต "${targetNoteTitle}" สำเร็จ!`);
    }

    // 3. สลับบอร์ดงานมาที่โน้ตประจำวัน
    selectNote(noteId);

    // 4. เปิดฟอร์มสร้างการ์ดออเดอร์เปล่าขึ้นมาทันที (Drawer ด้านขวา) เพื่อให้ผู้ใช้กรอกข้อมูลตามปกติ
    showForm(null, true, true);

    dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
    if (window.lucide) window.lucide.createIcons();
  } catch (err) {
    console.error("Error creating quick order daily note:", err);
    showToast("เกิดข้อผิดพลาดในการสร้างออเดอร์ด่วน", "danger");
    dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
    if (window.lucide) window.lucide.createIcons();
  } finally {
    isCreatingQuickOrder = false;
  }
}

// --- หน้าจอตั้งค่าระบบและสิทธิ์การใช้งาน (Settings & Simulated Roles Controllers) ---

const rolePermissionsMap = {
  "Super Admin": {
    emoji: "👑",
    visibility: "เข้าถึงโน้ตและออเดอร์ทั้งหมดในคลาวด์ได้อย่างเสรี 👑",
    edit: "เพิ่ม ลบ แก้ไข ออเดอร์และแก้ไขชื่อหัวข้อโน้ตหลักได้ทั้งหมด ✏️",
    admin: "สามารถเข้าถึงหน้าตั้งค่าขนส่งและปรับปรุงสิทธิ์ผู้ใช้งานได้เต็มที่ ⚙️"
  },
  "Order Manager": {
    emoji: "📝",
    visibility: "เข้าถึงโน้ตและออเดอร์ทั้งหมดเพื่อเช็กและวิเคราะห์ข้อมูลลูกค้า 📝",
    edit: "เพิ่ม แก้ไข รายละเอียดออเดอร์/สติกเกอร์การ์ดได้ แต่ห้ามลบการ์ดหรือลบโน้ตหลัก 🚫",
    admin: "สิทธิ์การเป็นพนักงานทั่วไป ไม่สามารถเข้าถึงหน้าตั้งค่าระบบได้ 🔒"
  },
  "Packer & Picker": {
    emoji: "📦",
    visibility: "เห็นเฉพาะคิวรอแพ็ค, คิวหน้าร้าน/เรียกรถ, และคิวจัดส่งเสร็จสิ้น (ไม่เห็นโน้ตหลัก) 📦",
    edit: "จำกัดการแก้ไขเฉพาะอัปเดตสติกเกอร์สถานะ 'เตรียมของเสร็จ' และระบบจัดส่ง ห้ามแก้ข้อมูลลูกค้า 🚫",
    admin: "สิทธิ์การเป็นพนักงานแพ็ค ไม่สามารถเข้าถึงหน้าตั้งค่าระบบได้ 🔒"
  },
  "Shipper": {
    emoji: "🚚",
    visibility: "เห็นเฉพาะคิวเตรียมสินค้า, คิวลงเลข, และคิวส่งแล้ว (ไม่เห็นโน้ตหลักและคิวรับหน้าร้าน) 🚚",
    edit: "จำกัดการแก้ไขให้สามารถใส่เลขพัสดุจัดส่ง (Tracking) และบันทึกจัดส่งเท่านั้น ห้ามแก้ข้อมูลลูกค้า 🚫",
    admin: "สามารถเข้าถึงหน้าตั้งค่าขนส่งและจัดการเลขขึ้นต้นของผู้จัดส่งได้ 🚚"
  }
};

function updateCleanupBadge() {
  const isAllowedCleanup = hasRole(["Super Admin", "Order Manager"]);
  const cleanupSettingsCount = document.getElementById("cleanup-settings-count");
  if (cleanupSettingsCount) {
    if (isAllowedCleanup) {
      cleanupSettingsCount.textContent = getCleanupNotes().length;
    } else {
      cleanupSettingsCount.textContent = "0";
    }
  }
}

function applyRolePermissions() {
  const roles = getUserRoles();
  console.log("Applying role permissions for:", roles);

  const notesContainer = document.querySelector(".notes-list-container");
  const sidebarSearch = document.querySelector(".sidebar-search");
  const btnNewNote = document.getElementById("btn-new-note");
  const btnPickupQueue = document.getElementById("btn-pickup-queue");

  // Show cleanup queue only for Super Admin or Order Manager
  const isAllowedCleanup = hasRole(["Super Admin", "Order Manager"]);

  // Handle collapsible shortcuts section permissions
  const btnToggleShortcuts = document.getElementById("btn-toggle-shortcuts");
  const shortcutsHeader = document.getElementById("shortcuts-header");
  const isAllowedDropdown = hasRole(["Super Admin", "Order Manager"]);

  if (btnToggleShortcuts) {
    if (isAllowedDropdown) {
      btnToggleShortcuts.style.display = "flex";
      if (shortcutsHeader) {
        shortcutsHeader.style.cursor = "pointer";
      }
      // Apply the preserved state
      const savedCollapsed = localStorage.getItem("shortcutsCollapsed") === "true";
      if (savedCollapsed) {
        collapseShortcuts();
      } else {
        expandShortcuts();
      }
    } else {
      btnToggleShortcuts.style.display = "none";
      if (shortcutsHeader) {
        shortcutsHeader.style.cursor = "default";
      }
      expandShortcuts(); // Always expanded for non-admins
    }
  }

  // Show/Hide Note lists & Sidebar elements
  const showNotesSidebar = hasRole(["Super Admin", "Order Manager", "Packer & Picker"]);
  if (notesContainer) notesContainer.style.display = showNotesSidebar ? "" : "none";
  if (sidebarSearch) sidebarSearch.style.display = showNotesSidebar ? "" : "none";

  const showCreateNoteBtn = hasRole(["Super Admin", "Order Manager"]);
  if (btnNewNote) btnNewNote.style.display = showCreateNoteBtn ? "" : "none";

  const showPickupQueue = hasRole(["Super Admin", "Order Manager", "Packer & Picker"]);
  if (btnPickupQueue) {
    if (showPickupQueue) btnPickupQueue.classList.remove("hidden");
    else btnPickupQueue.classList.add("hidden");
  }

  const showShippingQueue = hasRole(["Super Admin", "Order Manager", "Shipper"]);
  const btnShippingQueue = document.getElementById("btn-shipping-queue");
  if (btnShippingQueue) {
    if (showShippingQueue) btnShippingQueue.classList.remove("hidden");
    else btnShippingQueue.classList.add("hidden");
  }

  const isMobileScreen = window.innerWidth <= 600;

  const shouldAutoSelectDefaultQueue = !isMobileScreen && !isWideMobileViewport();

  // Handle Redirection based on roles
  if (roles.includes("Super Admin") || roles.includes("Order Manager")) {
    if (activeNoteId === "cleanup-queue" && !isAllowedCleanup) {
      selectNote(null);
    }
  } else if (roles.includes("Shipper") && !roles.includes("Packer & Picker")) {
    // Only Shipper: restrict note views
    if (shouldAutoSelectDefaultQueue && !activeNoteId) {
      selectNote("prepare-queue");
    } else if (activeNoteId && (activeNoteId !== "prepare-queue" && activeNoteId !== "shipping-queue" && activeNoteId !== "completed-queue" && activeNoteId !== "undo-queue")) {
      selectNote("prepare-queue");
    }
  } else if (roles.includes("Packer & Picker") && !roles.includes("Shipper")) {
    // Only Packer: restrict normal notes
    const isActiveDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("\u0e2d\u0e2d\u0e40\u0e14\u0e2d\u0e23\u0e4c\u0e1b\u0e23\u0e30\u0e08\u0e33\u0e27\u0e31\u0e19");
    if (shouldAutoSelectDefaultQueue && !activeNoteId) {
      selectNote("prepare-queue");
    } else if (activeNoteId && (activeNoteId !== "prepare-queue" && activeNoteId !== "pickup-queue" && activeNoteId !== "completed-queue" && activeNoteId !== "undo-queue" && !isActiveDailyNote)) {
      selectNote("prepare-queue");
    }
  } else {
    // If no roles or other role combinations (e.g. both Shipper and Packer)
    const hasPacker = roles.includes("Packer & Picker");
    const isActiveDailyNote = hasPacker && activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("\u0e2d\u0e2d\u0e40\u0e14\u0e2d\u0e23\u0e4c\u0e1b\u0e23\u0e30\u0e08\u0e33\u0e27\u0e31\u0e19");
    const allowedQueues = ["prepare-queue", "shipping-queue", "pickup-queue", "completed-queue", "undo-queue"];
    if (shouldAutoSelectDefaultQueue && !activeNoteId) {
      selectNote("prepare-queue");
    } else if (activeNoteId && (!allowedQueues.includes(activeNoteId) && !isActiveDailyNote)) {
      selectNote("prepare-queue");
    }
  }

  // Toggle btn-quick-order visibility based on role
  const btnQuickOrder = document.getElementById("btn-quick-order");
  if (btnQuickOrder) {
    if (hasRole(["Super Admin", "Order Manager", "Packer & Picker"])) {
      btnQuickOrder.style.display = "flex";
    } else {
      btnQuickOrder.style.display = "none";
    }
  }

  // Update badge count
  updateCleanupBadge();

  // Update PDF upload button visibility based on role and active queue
  updatePDFUploadButtonVisibility();

  // Re-render notes list in sidebar to immediately apply role-based filters (e.g. for Packer & Picker)
  renderNotesList();
}

function updateRoleDetailsPreview(roleName) {
  const perm = rolePermissionsMap[roleName] || rolePermissionsMap["Super Admin"];

  const titleEl = document.getElementById("role-preview-title-el");
  const visibilityEl = document.getElementById("role-perm-visibility");
  const editEl = document.getElementById("role-perm-edit");
  const adminEl = document.getElementById("role-perm-admin");

  if (titleEl) titleEl.innerHTML = `<i data-lucide="shield-alert" style="width: 14px; height: 14px; margin-right: 4px; display: inline-block; vertical-align: middle;"></i> <span style="vertical-align: middle;">รายละเอียดสิทธิ์บทบาท: ${perm.emoji} ${roleName}</span>`;
  if (visibilityEl) visibilityEl.textContent = perm.visibility;
  if (editEl) editEl.textContent = perm.edit;
  if (adminEl) adminEl.textContent = perm.admin;

  if (window.lucide) window.lucide.createIcons();
}


// (Packer management and picker modal logic moved to src/modules/packerModal.js)

async function performUndoAction(orderId) {
  const order = allOrders.find(o => o.id === orderId);
  if (!order) return;

  let updateData = { updatedAt: serverTimestamp() };
  let successMsg = "";

  // 1. If completed Express (has trackingNumber) -> Revert to "รอเลขออเดอร์" (shipping-queue)
  if (order.deliveryType === 'express' && order.trackingNumber) {
    if (hasRole(["Super Admin", "Shipper"])) {
      updateData.trackingNumber = "";
      updateData.carrier = "";
      updateData.carrierName = "";
      updateData.shippedAt = null;

      if (order.trackingHistory && order.trackingHistory.length > 0) {
        const trackingHistory = [...order.trackingHistory];
        trackingHistory.pop(); // Remove the last shipment's record
        updateData.trackingHistory = trackingHistory;
      }

      updateData.readyToShip = true;
      updateData.isPacked = true;
      successMsg = "ดึงออเดอร์กลับมาช่องรอเลขพัสดุเรียบร้อย! ↩️";
    } else {
      showToast("คุณไม่มีสิทธิ์ดึงงานในขั้นตอนนี้ 🔒", "danger");
      return;
    }
  }
  // 2. If completed Pickup/Messenger (pickupDone === true) -> Revert to "รอการรับของ/เรียกรถ" (pickup-queue)
  else if (order.deliveryType !== 'express' && order.readyToShip && order.isPacked && order.pickupDone) {
    if (hasRole(["Super Admin", "Order Manager", "Packer & Picker"])) {
      updateData.pickupDone = false;
      updateData.pickupAt = null;

      if (order.trackingHistory && order.trackingHistory.length > 0) {
        const trackingHistory = [...order.trackingHistory];
        trackingHistory.pop(); // Remove the last shipment's record
        updateData.trackingHistory = trackingHistory;
      }

      updateData.readyToShip = true;
      updateData.isPacked = true;
      successMsg = "ดึงออเดอร์กลับมาช่องรอรับของเรียบร้อย! ↩️";
    } else {
      showToast("คุณไม่มีสิทธิ์ดึงงานในขั้นตอนนี้ 🔒", "danger");
      return;
    }
  }
  // 3. If in active note / board main queue (readyToShip === false) but has trackingHistory (undoing previous shipment)
  else if (order.maxShipments > 1 && order.currentShipment > 1 && !order.readyToShip) {
    const isExpress = order.deliveryType === 'express';
    const hasPermission = isExpress
      ? hasRole(["Super Admin", "Shipper"])
      : hasRole(["Super Admin", "Order Manager", "Packer & Picker"]);

    if (hasPermission) {
      const trackingHistory = order.trackingHistory ? [...order.trackingHistory] : [];
      const popped = trackingHistory.pop();
      updateData.trackingHistory = trackingHistory;
      updateData.currentShipment = order.currentShipment - 1;
      updateData.readyToShip = true;
      updateData.isPacked = true;
      updateData.packerName = popped ? (popped.packerName || "") : "";

      if (order.deliveryType === 'express') {
        updateData.trackingNumber = "";
        updateData.carrier = "";
      } else {
        updateData.pickupDone = false;
      }
      successMsg = `ดึงออเดอร์กลับมารอส่งรอบที่ ${order.currentShipment - 1} เรียบร้อย! ↩️`;
    } else {
      showToast("คุณไม่มีสิทธิ์ดึงงานในขั้นตอนนี้ 🔒", "danger");
      return;
    }
  }
  // 4. If in shipping-queue or pickup-queue (readyToShip && isPacked && !trackingNumber && !pickupDone) -> Revert to "รอแพ็ค" (prepare-queue)
  else if (order.readyToShip && order.isPacked && !order.trackingNumber && !order.pickupDone) {
    if (hasRole(["Super Admin", "Order Manager", "Packer & Picker"])) {
      updateData.isPacked = false;
      updateData.packerName = "";
      successMsg = "ดึงออเดอร์กลับมาช่องรอแพ็คของเรียบร้อย! (รีเซ็ตชื่อคนแพ็ค) ↩️";
    } else {
      showToast("คุณไม่มีสิทธิ์ดึงงานในขั้นตอนนี้ 🔒", "danger");
      return;
    }
  }
  // 5. If in prepare-queue (readyToShip && !isPacked) -> Revert to "เตรียมของ" (normal note, readyToShip = false)
  else if (order.readyToShip && !order.isPacked) {
    if (hasRole(["Super Admin", "Order Manager"])) {
      updateData.readyToShip = false;
      successMsg = "ดึงออเดอร์กลับจากคิวจัดส่งไปยังโน้ตหลักเรียบร้อย! ↩️";
    } else {
      showToast("คุณไม่มีสิทธิ์ดึงงานในขั้นตอนนี้ 🔒", "danger");
      return;
    }
  }

  if (successMsg) {
    try {
      await updateDoc(doc(db, "orders", orderId), updateData);
      showToast(successMsg);
    } catch (err) {
      console.error("Undo action error:", err);
      showToast("เกิดข้อผิดพลาดในการดึงงานกลับ", "danger");
    }
  }
}

// (Settings modal logic moved to src/modules/settingsModal.js)

// เปิดบอร์ดค้นหาออเดอร์สากล
function openGlobalSearch() {
  activeNoteId = null;
  activeNoteData = null;
  closeForm();
  closeSettings();

  // ล้างสถานะแอกทีฟของรายชื่อโน้ตและคิวงานทั้งหมด
  document.querySelectorAll(".note-item").forEach(item => item.classList.remove("active"));
  document.querySelectorAll(".shortcut-btn").forEach(btn => btn.classList.remove("active"));

  const searchActiveInput = document.getElementById("search-active-orders");
  if (searchActiveInput) searchActiveInput.value = "";

  if (isMobile) {
    setMobilePane("show-editor");
  }

  renderOrderDashboard();
}

// อัปเดตสถานะการใช้งานของปุ่มเตรียมของพร้อมกันและกล่องเลือกทั้งหมด
function updateBatchPrepareButtonState() {
  const checkboxes = document.querySelectorAll(".order-select-checkbox");
  const checkedCheckboxes = document.querySelectorAll(".order-select-checkbox:checked");
  const checkedCount = checkedCheckboxes.length;

  const btnText = document.getElementById("btn-batch-prepare-text");
  const btnBatchPrepare = document.getElementById("btn-batch-prepare");

  if (isBatchSelectMode) {
    if (btnText) {
      btnText.textContent = `เตรียมของที่เลือก (${checkedCount})`;
    }
    if (btnBatchPrepare) {
      btnBatchPrepare.disabled = checkedCount === 0;
    }
  } else {
    if (btnText) {
      btnText.textContent = "เลือกหลายรายการ";
    }
    if (btnBatchPrepare) {
      btnBatchPrepare.disabled = false;
    }
  }

  const selectAllCheckbox = document.getElementById("select-all-checkbox");
  if (selectAllCheckbox) {
    if (checkboxes.length > 0 && checkedCount === checkboxes.length) {
      selectAllCheckbox.checked = true;
    } else {
      selectAllCheckbox.checked = false;
    }
  }
}

// บันทึกเปลี่ยนสถานะเตรียมของแบบกลุ่ม (Batch Prepare) ไปยัง Firestore
async function performBatchPrepare(orderIds) {
  if (!orderIds || orderIds.length === 0) return;

  if (!hasRole(["Super Admin", "Order Manager", "Packer & Picker"])) {
    showToast("คุณไม่มีสิทธิ์เปลี่ยนสถานะการจัดเตรียมสินค้า 🔒", "danger");
    return;
  }

  dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
  if (window.lucide) window.lucide.createIcons();

  try {
    const batch = writeBatch(db);
    orderIds.forEach(orderId => {
      const orderRef = doc(db, "orders", orderId);
      const order = allOrders.find(o => o.id === orderId);
      if (order) {
        if (order.deliveryType === 'pickup' || order.deliveryType === 'messenger') {
          batch.update(orderRef, {
            readyToShip: true,
            isPacked: false,
            pickupDone: false,
            updatedAt: serverTimestamp()
          });
        } else {
          batch.update(orderRef, {
            readyToShip: true,
            isPacked: false,
            updatedAt: serverTimestamp()
          });
        }
      }
    });

    await batch.commit();
    showToast(`เปลี่ยนสถานะเป็น แพ็คเสร็จแล้ว ${orderIds.length} รายการ 📦`);

    // เคลียร์ Checkbox เลือกทั้งหมด
    const selectAllCheckbox = document.getElementById("select-all-checkbox");
    if (selectAllCheckbox) selectAllCheckbox.checked = false;

    // อัปเดตสถานะปุ่ม
    updateBatchPrepareButtonState();

    dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
    if (window.lucide) window.lucide.createIcons();
  } catch (err) {
    console.error("Batch prepare error:", err);
    showToast("เกิดข้อผิดพลาดในการบันทึกเตรียมของ", "danger");
    dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
    if (window.lucide) window.lucide.createIcons();
  }
}

// --- ระบบแสดงสถานะการตั้งค่าแจ้งเตือนใน UI ---
function updateFcmStatusUI(status, type = "muted") {
  const textEl = document.getElementById("fcm-status-text");
  const iconEl = document.getElementById("fcm-status-icon");
  if (!textEl || !iconEl) return;

  textEl.textContent = status;
  if (type === "success") {
    iconEl.textContent = "✅";
    textEl.style.color = "#10b981";
  } else if (type === "danger") {
    iconEl.textContent = "❌";
    textEl.style.color = "#f87171";
  } else if (type === "warning") {
    iconEl.textContent = "⚠️";
    textEl.style.color = "#fbbf24";
  } else {
    iconEl.textContent = "🔄";
    textEl.style.color = "#94a3b8";
  }
}

// --- ระบบตั้งค่าการแจ้งเตือน FCM (FCM Client Push Notifications Registration) ---
async function setupPushNotifications(email) {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      console.log("FCM: Push notifications are not supported on this browser.");
      updateFcmStatusUI("เบราว์เซอร์นี้ไม่รองรับ Push Notification (หากใช้ iOS ต้องเพิ่ม PWA ลงหน้าจอหลักก่อน)", "danger");
      return;
    }

    const roles = getAssignedRoles();
    const isAllowedRole = roles.includes("Super Admin") || roles.includes("Packer & Picker");
    if (!isAllowedRole) {
      console.log("FCM: Notifications not required for this role. Cleaning up existing tokens.");
      updateFcmStatusUI("บทบาทของคุณไม่ได้รับการแจ้งเตือน (เฉพาะ Super Admin / Packer & Picker)", "muted");
      // ลบ Token ออกจากระบบเผื่อว่าเคยมีสิทธิ์มาก่อน ป้องกันการส่ง Push ไปยังตำแหน่งที่ถูกเปลี่ยน
      await cleanupPushNotifications(email);
      return;
    }

    let permission = Notification.permission;
    if (permission === "default") {
      updateFcmStatusUI("กำลังขอสิทธิ์การแจ้งเตือน...", "warning");
      permission = await Notification.requestPermission();
    }

    if (permission !== "granted") {
      console.log("FCM: Notification permission is not granted.");
      updateFcmStatusUI("ปฏิเสธรับแจ้งเตือน (โปรดเปิดสิทธิ์ในตั้งค่าของเบราว์เซอร์)", "danger");
      return;
    }

    const reg = await navigator.serviceWorker.ready;
    if (!reg) {
      console.warn("FCM: Service worker is not ready.");
      updateFcmStatusUI("Service Worker ยังไม่พร้อมทำงาน กรุณารีโหลดหน้าเว็บใหม่", "warning");
      return;
    }

    const vapidKey = "BKUoZAJeDDm6dj5G7FpqHPPa8-1IP0oZ1Pc4MAETJyi0HAS8HJBLEP35k0i_HOUEkHDPq2_xv2_jdIr4bx4Sv5Y";
    updateFcmStatusUI("กำลังขอรหัส Token จาก Firebase...", "warning");
    const token = await getToken(messaging, {
      serviceWorkerRegistration: reg,
      vapidKey: vapidKey
    });

    if (token) {
      console.log("FCM Token retrieved successfully:", token);

      const staffRef = doc(db, "staff", email);
      const staffSnap = await getDoc(staffRef);
      if (staffSnap.exists()) {
        const data = staffSnap.data();
        let fcmTokens = data.fcmTokens || [];

        if (!fcmTokens.includes(token)) {
          fcmTokens.push(token);
          await updateDoc(staffRef, { fcmTokens: fcmTokens });
          console.log("FCM: Token successfully registered in Firestore.");
        }
        updateFcmStatusUI("เชื่อมต่อและลงทะเบียนสำเร็จ ✅ (พร้อมรับแจ้งเตือนเมื่อปิดแอป)", "success");
      } else {
        updateFcmStatusUI("ไม่พบรายชื่อพนักงานในระบบเพื่อบันทึก Token", "danger");
      }
    } else {
      console.warn("FCM: No registration token available.");
      updateFcmStatusUI("ไม่ได้รับรหัส Token ในอุปกรณ์นี้", "danger");
    }
  } catch (err) {
    console.error("Error setting up FCM push notifications:", err);
    updateFcmStatusUI("เกิดข้อผิดพลาด: " + err.message, "danger");
  }
}

async function cleanupPushNotifications(email) {
  try {
    if (!email || !('serviceWorker' in navigator) || !('Notification' in window)) return;

    const reg = await navigator.serviceWorker.ready;
    if (!reg) return;

    const vapidKey = "BKUoZAJeDDm6dj5G7FpqHPPa8-1IP0oZ1Pc4MAETJyi0HAS8HJBLEP35k0i_HOUEkHDPq2_xv2_jdIr4bx4Sv5Y";
    const token = await getToken(messaging, {
      serviceWorkerRegistration: reg,
      vapidKey: vapidKey
    });

    if (token) {
      const staffRef = doc(db, "staff", email);
      const staffSnap = await getDoc(staffRef);
      if (staffSnap.exists()) {
        const data = staffSnap.data();
        let fcmTokens = data.fcmTokens || [];

        fcmTokens = fcmTokens.filter(t => t !== token);
        await updateDoc(staffRef, { fcmTokens: fcmTokens });
        console.log("FCM: Token successfully removed from Firestore.");
      }
    }
  } catch (err) {
    console.error("Error cleaning up FCM token:", err);
  }
}

// --- การผูก Events ทั่วไปและเริ่มใช้งานแอป (Initialization & Event Binding) ---

function initApp() {
  updateMobileTabLabels();
  
  // ซิงค์การตั้งค่าแจ้งเตือนจาก localStorage ไปยัง Cache API สำหรับ Service Worker เมื่อเปิดแอป
  if ('caches' in window) {
    const enabled = localStorage.getItem("notiEnabled") !== "false";
    const startTime = localStorage.getItem("notiStartTime") || "08:00";
    const endTime = localStorage.getItem("notiEndTime") || "18:00";
    const days = JSON.parse(localStorage.getItem("notiDays") || "[1,2,3,4,5,6,0]");
    const soundType = localStorage.getItem("notiSoundType") || "default";
    const notiSettings = { enabled, startTime, endTime, days, soundType };
    
    caches.open("noti-settings-cache").then(cache => {
      cache.put("/settings", new Response(JSON.stringify(notiSettings)));
    }).catch(err => console.warn("Failed to sync settings to caches:", err));
  }

  // โหลดฐานข้อมูลภูมิศาสตร์ไปรษณีย์ไทยเพื่อเข้า Scoring System ของ parser.js
  fetch('./thailand-address.json')
    .then(response => response.json())
    .then(data => {
      setAddressDatabase(data);
      console.log("Thai geography database loaded successfully!");
      if (activeNoteId) {
        renderOrderDashboard();
        renderNotesList();
      }
    })
    .catch(err => {
      console.error("Failed to load address database:", err);
    });

  disableEditor();
  // Realtime sync will be started after Google Authentication check

  // ผูกการทำงานปุ่มเตรียมของพร้อมกัน และ Checkbox เลือกทั้งหมด
  if (dom.btnBatchPrepare) {
    dom.btnBatchPrepare.addEventListener("click", () => {
      if (!isBatchSelectMode) {
        isBatchSelectMode = true;
        renderOrderDashboard();
      } else {
        const checkedCheckboxes = document.querySelectorAll(".order-select-checkbox:checked");
        const orderIds = Array.from(checkedCheckboxes).map(chk => chk.getAttribute("data-id"));
        if (orderIds.length > 0) {
          showConfirm("ยืนยันการเตรียมของ", `คุณต้องการเปลี่ยนสถานะเป็นเตรียมของพร้อมกันจำนวน ${orderIds.length} รายการใช่หรือไม่?`, async () => {
            await performBatchPrepare(orderIds);
            isBatchSelectMode = false;
            renderOrderDashboard();
          });
        }
      }
    });
  }

  if (dom.btnBatchCancel) {
    dom.btnBatchCancel.addEventListener("click", () => {
      isBatchSelectMode = false;
      renderOrderDashboard();
    });
  }

  if (dom.selectAllCheckbox) {
    dom.selectAllCheckbox.addEventListener("change", (e) => {
      const isChecked = e.target.checked;
      const checkboxes = document.querySelectorAll(".order-select-checkbox");
      checkboxes.forEach(chk => {
        chk.checked = isChecked;
      });
      updateBatchPrepareButtonState();
    });
  }

  // ก. ปุ่มควบคุมฝั่งโน้ตด้านซ้าย
  dom.btnNewNote.addEventListener("click", createNewNote);
  if (dom.btnQuickOrder) {
    dom.btnQuickOrder.addEventListener("click", createQuickOrder);
  }
  if (dom.searchNotes) {
    dom.searchNotes.addEventListener("input", (e) => {
      renderNotesList();
      triggerAutoGlobalSearch(e.target.value.trim());
    });
  }

  const prepareQueueBtn = document.getElementById("btn-prepare-queue");
  if (prepareQueueBtn) {
    prepareQueueBtn.addEventListener("click", () => selectNote("prepare-queue"));
  }

  const shippingQueueBtn = document.getElementById("btn-shipping-queue");
  if (shippingQueueBtn) {
    shippingQueueBtn.addEventListener("click", () => selectNote("shipping-queue"));
  }

  const completedQueueBtn = document.getElementById("btn-completed-queue");
  if (completedQueueBtn) {
    completedQueueBtn.addEventListener("click", () => selectNote("completed-queue"));
  }

  const pickupQueueBtn = document.getElementById("btn-pickup-queue");
  if (pickupQueueBtn) {
    pickupQueueBtn.addEventListener("click", () => selectNote("pickup-queue"));
  }

  const undoQueueBtn = document.getElementById("btn-undo-queue");
  if (undoQueueBtn) {
    undoQueueBtn.addEventListener("click", () => selectNote("undo-queue"));
  }
  
  const pendingInfoQueueBtn = document.getElementById("btn-pending-info-queue");
  if (pendingInfoQueueBtn) {
    pendingInfoQueueBtn.addEventListener("click", () => selectNote("pending-info-queue"));
  }


  const shortcutsHeader = document.getElementById("shortcuts-header");
  if (shortcutsHeader) {
    shortcutsHeader.addEventListener("click", () => {
      toggleShortcuts();
    });
  }

  // ดักการค้นหาข้อความภายในการ์ดออเดอร์ของโน้ตปัจจุบัน (Active Note Search)
  const searchActiveInput = document.getElementById("search-active-orders");
  if (searchActiveInput) {
    searchActiveInput.addEventListener("input", (e) => {
      renderOrderDashboard();
      triggerAutoGlobalSearch(e.target.value.trim());
    });
  }

  // ดักการค้นหาเฉพาะในโน้ตนี้ (Local Note Search)
  const searchLocalInput = document.getElementById("search-local-note");
  if (searchLocalInput) {
    searchLocalInput.addEventListener("input", () => {
      renderOrderDashboard();
    });
  }

  // ข. จัดการบันทึกแก้ไขชื่อหัวข้อโน้ตแบบชะลอจังหวะ (Debounce)
  let titleTimeout = null;
  dom.noteTitleInput.addEventListener("input", () => {
    if (!activeNoteId) return;

    renderNotesList();

    clearTimeout(titleTimeout);
    dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
    if (window.lucide) window.lucide.createIcons();

    titleTimeout = setTimeout(() => {
      const noteRef = doc(db, "notes", activeNoteId);
      updateDoc(noteRef, {
        title: dom.noteTitleInput.value.trim() || "ไม่มีชื่อหัวข้อโน้ต",
        updatedAt: serverTimestamp()
      }).then(() => {
        dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
        if (window.lucide) window.lucide.createIcons();
      }).catch(err => {
        console.error("Error updating note title:", err);
      });
    }, 600);
  });

  // ค. ดักฟังก์ชันปุ่มเด็ดหัวโน้ตทิ้งพร้อมกับออเดอร์ลูกในสังกัดทั้งหมด (Batch delete)
  window.handleDeleteNoteClick = async () => {
    console.log("Delete note button clicked! activeNoteId =", activeNoteId);
    try {
      if (!activeNoteId) {
        console.warn("Cannot delete note: activeNoteId is falsy.");
        showToast("ไม่สามารถลบโน้ตได้เนื่องจากไม่มีข้อมูลโน้ตที่เลือก", "danger");
        return;
      }

      const noteTitle = dom.noteTitleInput.value || "โน้ตไม่มีชื่อ";
      console.log("Note title to delete:", noteTitle);

      const confirmMessage = `คุณต้องการลบโน้ตหัวข้อหลัก "${noteTitle}" นี้ใช่หรือไม่? \nข้อมูลการ์ดรายการออเดอร์ของพนักงานทั้งหมดในโน้ตนี้จะถูกกวาดล้างลบถอนรากทันที! ไม่สามารถกู้คืนได้`;

      showConfirm("ยืนยันการลบโน้ตหลัก", confirmMessage, async () => {
        console.log("Delete confirmed. Starting batch delete...");
        dom.saveStatusText.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
        if (window.lucide) window.lucide.createIcons();

        const targetDeleteId = activeNoteId;

        try {
          const qOrders = query(collection(db, "orders"), where("noteId", "==", targetDeleteId));
          const orderSnap = await getDocs(qOrders);
          const batch = writeBatch(db);

          console.log(`Found ${orderSnap.size} orders to delete in batch.`);
          orderSnap.forEach((orderDoc) => {
            batch.delete(orderDoc.ref);
          });

          batch.delete(doc(db, "notes", targetDeleteId));
          await batch.commit();
          console.log("Batch delete commit successful!");

          showToast("ลบหัวข้อและล้างออเดอร์พัสดุทั้งหมดสำเร็จ!");
          activeNoteId = null;
          activeNoteData = null;
          disableEditor();

          dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
          if (window.lucide) window.lucide.createIcons();

          if (isMobile) {
            setMobilePane("show-sidebar");
          }
        } catch (err) {
          console.error("Batch delete note error:", err);
          showToast("เกิดข้อผิดพลาดในการลบธุรกรรม", "danger");
          dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
          if (window.lucide) window.lucide.createIcons();
        }
      });
    } catch (err) {
      console.error("Batch delete note error:", err);
      showToast("เกิดข้อผิดพลาดในการลบธุรกรรม", "danger");
      dom.saveStatusText.innerHTML = `<i data-lucide="check"></i>`;
      if (window.lucide) window.lucide.createIcons();
    }
  };

  dom.btnDeleteNote.addEventListener("click", window.handleDeleteNoteClick);

  // ง. แท็บลิ้นชักกรอกฟอร์มฝั่งขวา
  const addOrderBtn = document.getElementById("btn-add-order");
  if (addOrderBtn) {
    addOrderBtn.addEventListener("click", () => showForm());
  }

  dom.btnCancelOrder.addEventListener("click", closeForm);
  dom.btnCloseForm.addEventListener("click", closeForm);
  dom.btnSaveOrder.addEventListener("click", saveOrderData);

  if (dom.btnFormUndo) {
    dom.btnFormUndo.addEventListener("click", () => {
      const orderId = formInputs.orderId.value;
      if (!orderId) return;
      showConfirm("ยืนยันการดึงงานกลับ", "คุณต้องการดึงสถานะงานของออเดอร์นี้กลับใช่หรือไม่?", async () => {
        await performUndoAction(orderId);
        closeForm();
      });
    });
  }

  // ผูกตัวดักจับการอัปโหลดไฟล์ PDF ขนส่ง และระบบจับคู่ออเดอร์อัตโนมัติ
  setupPdfScanner({
    getActiveNoteId: () => activeNoteId,
    getAllOrders: () => allOrders
  });

  // ผูกตัวดักจับหน้าต่างการตั้งค่าและจัดการสิทธิ์พนักงาน
  setupSettingsModal({
    getCurrentUserStaffData: () => currentUserStaffData,
    getCleanupNotes: () => getCleanupNotes(),
    getActiveNoteId: () => activeNoteId || (allNotes.length > 0 ? allNotes[0].id : "temp-note"),
    onRenderOrderDashboard: () => renderOrderDashboard(),
    onSelectNote: (noteId) => selectNote(noteId),
    onSetupPushNotifications: (email) => setupPushNotifications(email),
    onReturnToPackerSelect: () => {
      reopenPackerSelectModal();
    }
  });


  // การสลับธีม สว่าง/มืด (Theme Toggle Logic)
  const btnToggleTheme = document.getElementById("btn-toggle-theme");
  const btnToggleThemeMobile = document.getElementById("btn-toggle-theme-mobile");

  function applyTheme(theme) {
    if (theme === "light") {
      document.body.classList.add("light-theme");
      document.body.classList.remove("dark-theme");
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) metaThemeColor.setAttribute("content", "#eae6db");
    } else {
      document.body.classList.remove("light-theme");
      document.body.classList.add("dark-theme");
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) metaThemeColor.setAttribute("content", "#0e1322");
    }
    localStorage.setItem("appTheme", theme);
  }

  // เรียกใช้งานการตั้งค่าธีมเริ่มต้นทันทีเพื่อให้คลาสซิงค์กัน 100%
  applyTheme(savedTheme);

  const toggleThemeAction = () => {
    const isLight = document.body.classList.contains("light-theme");
    applyTheme(isLight ? "dark" : "light");
  };

  if (btnToggleTheme) {
    btnToggleTheme.addEventListener("click", toggleThemeAction);
  }

  if (btnToggleThemeMobile) {
    btnToggleThemeMobile.addEventListener("click", toggleThemeAction);
  }

  // (Settings modal listeners initialized inside setupSettingsModal)

  // ผูกการคลิกปุ่มและกด Enter เพื่อเพิ่มรายชื่อคนแพ็ค
  // ผูกการทำงานระบบคนแพ็ค (Packer Selection Modal & Management)
  setupPackerModal({
    onShowSettings: () => {
      setReturnToPackerSelectAfterSettings(true);
      showSettings();
      switchSettingsTab("packers");
    }
  });

  // btn-goto-cleanup initialized in setupSettingsModal

  // ผูกเหตุการณ์การเข้าสู่ระบบ/ออกจากระบบ และการจัดการสิทธิ์พนักงาน
  const btnGoogleLogin = document.getElementById("btn-google-login");
  if (btnGoogleLogin) {
    btnGoogleLogin.addEventListener("click", () => {
      const provider = new GoogleAuthProvider();
      // เรียกใช้แบบ Synchronous ทันทีเพื่อป้องกันไม่ให้เบราว์เซอร์มองว่าไม่ใช่ User Gesture (ป้องกัน Popup Blocker)
      signInWithPopup(auth, provider)
        .then((result) => {
          console.log("Sign-in popup success:", result.user.email);
        })
        .catch((err) => {
          console.error("Login failed:", err);
          showToast("เข้าสู่ระบบไม่สำเร็จ: " + err.message, "danger");
        });
    });
  }

  // ผูกเหตุการณ์ล็อกอินด้วย Email / Password
  const btnEmailLogin = document.getElementById("btn-email-login");
  if (btnEmailLogin) {
    btnEmailLogin.addEventListener("click", () => {
      const emailInput = document.getElementById("login-email");
      const passwordInput = document.getElementById("login-password");
      if (!emailInput || !passwordInput) return;

      const email = emailInput.value.trim().toLowerCase();
      const password = passwordInput.value;

      if (!email || !password) {
        showToast("กรุณากรอกอีเมลและรหัสผ่านให้ครบถ้วน", "warning");
        return;
      }

      showToast("กำลังตรวจสอบสิทธิ์...", "info");

      signInWithEmailAndPassword(auth, email, password)
        .then((result) => {
          console.log("Sign-in email success:", result.user.email);
        })
        .catch((err) => {
          console.error("Email login failed:", err);
          let thaiMsg = "เข้าสู่ระบบไม่สำเร็จ: ";
          if (err.code === "auth/invalid-credential" || err.code === "auth/wrong-password" || err.code === "auth/user-not-found") {
            thaiMsg += "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
          } else if (err.code === "auth/invalid-email") {
            thaiMsg += "รูปแบบอีเมลไม่ถูกต้อง";
          } else {
            thaiMsg += err.message;
          }
          showToast(thaiMsg, "danger");
        });
    });
  }

  // ผูกเหตุการณ์ส่งลิงก์ตั้งรหัสใหม่ / ลืมรหัสผ่าน
  const btnForgotPassword = document.getElementById("btn-forgot-password");
  if (btnForgotPassword) {
    btnForgotPassword.addEventListener("click", (e) => {
      e.preventDefault();
      const emailInput = document.getElementById("login-email");
      let prefilledEmail = "";
      if (emailInput) {
        prefilledEmail = emailInput.value.trim();
      }

      const email = prompt("กรุณาระบุอีเมล (Gmail) เพื่อรับลิงก์สร้างหรือรีเซ็ตรหัสผ่าน:", prefilledEmail);
      if (email === null) return; // กด Cancel

      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail) {
        showToast("กรุณากรอกอีเมลที่ต้องการส่งลิงก์", "warning");
        return;
      }

      showToast("กำลังส่งอีเมลคำขอ...", "info");

      sendPasswordResetEmail(auth, trimmedEmail)
        .then(() => {
          alert(`ระบบได้ส่งลิงก์สำหรับตั้งรหัสผ่านไปยังอีเมล ${trimmedEmail} เรียบร้อยแล้ว!\nกรุณาตรวจสอบกล่องจดหมาย (หรือกล่องอีเมลขยะ/Spam) และดำเนินการตามขั้นตอนเพื่อกำหนดรหัสผ่านใหม่ครับ`);
          showToast("ส่งอีเมลตั้งรหัสผ่านเรียบร้อยแล้ว");
        })
        .catch((err) => {
          console.error("Send password reset email failed:", err);
          let thaiMsg = "ส่งคำขอไม่สำเร็จ: ";
          if (err.code === "auth/user-not-found") {
            thaiMsg += "ไม่พบอีเมลนี้ในระบบการเข้าสู่ระบบ";
          } else if (err.code === "auth/invalid-email") {
            thaiMsg += "รูปแบบอีเมลไม่ถูกต้อง";
          } else {
            thaiMsg += err.message;
          }
          showToast(thaiMsg, "danger");
        });
    });
  }

  const btnAuthLogout = document.getElementById("btn-auth-logout");
  if (btnAuthLogout) {
    btnAuthLogout.addEventListener("click", async () => {
      try {
        stopRealtimeSync();
        if (currentUserStaffData && currentUserStaffData.email) {
          await cleanupPushNotifications(currentUserStaffData.email);
        }
        await signOut(auth);
        showToast("ออกจากระบบแล้ว");
        window.location.reload();
      } catch (err) {
        console.error("Logout failed:", err);
      }
    });
  }

// staff listeners initialized in setupSettingsModal

  // ตั้งค่า Auth State Observer
  onAuthStateChanged(auth, async (user) => {
    const loginOverlay = document.getElementById("login-overlay");
    const loginErrorMsg = document.getElementById("login-error-msg");

    console.log("onAuthStateChanged event fired. User:", user ? user.email : "null");

    if (user) {
      const email = user.email.toLowerCase();
      console.log("Attempting to verify email in Firestore 'staff' collection:", email);

      try {
        const docRef = doc(db, "staff", email);
        console.log("Querying document path:", docRef.path);
        const docSnap = await getDoc(docRef);
        console.log("Document fetch completed. Exists?:", docSnap.exists());

        if (docSnap.exists()) {
          currentUserStaffData = docSnap.data();
          currentUserStaffData.email = email;
          console.log("Staff verified successfully! Data:", currentUserStaffData);

          localStorage.setItem("userRoles", JSON.stringify(currentUserStaffData.roles || []));
          localStorage.setItem("userName", currentUserStaffData.displayName || user.displayName || email);

          // ซิงค์การตั้งค่าแจ้งเตือนจาก Firestore มายัง LocalStorage และ Cache API
          if (currentUserStaffData.notificationSettings) {
            const settings = currentUserStaffData.notificationSettings;
            localStorage.setItem("notiEnabled", settings.enabled !== false ? "true" : "false");
            localStorage.setItem("notiStartTime", settings.startTime || "08:00");
            localStorage.setItem("notiEndTime", settings.endTime || "18:00");
            localStorage.setItem("notiDays", JSON.stringify(settings.days || [1,2,3,4,5,6,0]));
            if (settings.soundType) {
              localStorage.setItem("notiSoundType", settings.soundType);
            }
            
            if ('caches' in window) {
              caches.open("noti-settings-cache").then(cache => {
                cache.put("/settings", new Response(JSON.stringify(settings)));
              });
            }
          }

          if (loginOverlay) loginOverlay.style.display = "none";
          if (loginErrorMsg) loginErrorMsg.style.display = "none";

          updateUserProfileUI(currentUserStaffData);
          applyRolePermissions();
          setupPushNotifications(email);

          // เริ่มต้น Realtime Sync หลังจากตรวจสอบสิทธิ์และตั้งค่า Roles สำเร็จแล้วเท่านั้น ป้องกัน Race Condition
          console.log("Starting real-time sync listeners for verified user...");
          initRealtimeSync();

        } else {
          console.warn(`Email ${email} was authenticated, but not found in Firestore 'staff' collection.`);
          stopRealtimeSync();
          currentUserStaffData = null;
          if (loginOverlay) loginOverlay.style.display = "flex";
          if (loginErrorMsg) {
            loginErrorMsg.style.display = "block";
            loginErrorMsg.textContent = `อีเมล ${email} ไม่มีสิทธิ์เข้าใช้งานระบบ กรุณาติดต่อ Super Admin 🔒`;
          }
          await signOut(auth);
        }
      } catch (err) {
        console.error("Firestore verification error:", err);
        stopRealtimeSync();
        currentUserStaffData = null;
        if (loginOverlay) loginOverlay.style.display = "flex";
        if (loginErrorMsg) {
          loginErrorMsg.style.display = "block";
          loginErrorMsg.textContent = "เกิดข้อผิดพลาดในการตรวจสอบสิทธิ์: " + err.message;
        }
        await signOut(auth);
      }
    } else {
      console.log("No authenticated user session found.");
      stopRealtimeSync();
      currentUserStaffData = null;
      localStorage.removeItem("userRoles");
      localStorage.removeItem("userName");
      localStorage.removeItem("activeRole");

      if (loginOverlay) {
        loginOverlay.style.display = "flex";
      }
      updateUserProfileUI(null);
      applyRolePermissions();
      renderNotesList();
      disableEditor();
    }

    if (activeNoteId || isWideMobileViewport()) {
      renderOrderDashboard();
    }
  });

  // ดัก Live Auto-fill เมื่อพิมพ์หรือวางข้อมูลดิบลูกค้า
  formInputs.rawText.addEventListener("input", handleLiveAutofill);

  // การสลับปุ่มสถานะชำระเงินภายในฟอร์มขวา
  formInputs.statusPaid.addEventListener("click", () => {
    formInputs.statusPaid.classList.add("active");
    formInputs.statusUnpaid.classList.remove("active");
    formInputs.statusDeposit.classList.remove("active");
    formInputs.depositAmountGroup.style.display = "none";
  });
  formInputs.statusUnpaid.addEventListener("click", () => {
    formInputs.statusUnpaid.classList.add("active");
    formInputs.statusPaid.classList.remove("active");
    formInputs.statusDeposit.classList.remove("active");
    formInputs.depositAmountGroup.style.display = "none";
  });
  formInputs.statusDeposit.addEventListener("click", () => {
    formInputs.statusDeposit.classList.add("active");
    formInputs.statusPaid.classList.remove("active");
    formInputs.statusUnpaid.classList.remove("active");
    formInputs.depositAmountGroup.style.display = "block";
    formInputs.depositAmount.focus();
  });

  // การสลับปุ่มความพร้อมการส่งภายในฟอร์มขวา
  formInputs.statusReady.addEventListener("click", () => {
    formInputs.statusReady.classList.add("active");
    formInputs.statusUnready.classList.remove("active");
  });
  formInputs.statusUnready.addEventListener("click", () => {
    formInputs.statusUnready.classList.add("active");
    formInputs.statusReady.classList.remove("active");
  });

  // การสลับสติกเกอร์การขนส่งภายในฟอร์มขวา
  formDeliveryStickers.forEach(sticker => {
    sticker.addEventListener("click", () => {
      formDeliveryStickers.forEach(s => s.classList.remove("active"));
      sticker.classList.add("active");
    });
  });

  // การสลับสติกเกอร์ช่องทางการขายภายในฟอร์มขวา
  formPlatformStickers.forEach(sticker => {
    sticker.addEventListener("click", () => {
      formPlatformStickers.forEach(s => s.classList.remove("active"));
      sticker.classList.add("active");
    });
  });

  // จ. ตัวจัดการ Event Delegation บนบอร์ดการ์ดกลาง (Order Board actions delegation)
  dom.ordersCardsListDiv.addEventListener("click", async (e) => {
    const copyOrderCardImageBtn = e.target.closest(".btn-copy-order-card-image");
    if (copyOrderCardImageBtn) {
      const card = copyOrderCardImageBtn.closest(".order-card");
      if (card) await copyOrderCardImage(card, copyOrderCardImageBtn);
      return;
    }

    const downloadOrderCardImageBtn = e.target.closest(".btn-download-order-card-image");
    if (downloadOrderCardImageBtn) {
      const card = downloadOrderCardImageBtn.closest(".order-card");
      if (card) await downloadOrderCardImage(card, downloadOrderCardImageBtn);
      return;
    }

    // ดักการคลิก Checkbox เลือกรายการ
    const selectCheckbox = e.target.closest(".order-select-checkbox");
    if (selectCheckbox) {
      updateBatchPrepareButtonState();
      return;
    }

    // 0.5. กดปุ่มดึงงานกลับ (Undo Action) บนการ์ดบอร์ด Undo tab
    const undoBtn = e.target.closest(".btn-undo-action");
    if (undoBtn) {
      const orderId = undoBtn.getAttribute("data-id");
      showConfirm("ยืนยันการดึงงานกลับ", "คุณต้องการดึงออเดอร์นี้กลับไปขั้นตอนก่อนหน้าใช่หรือไม่? (ข้อมูลที่บันทึกในสเต็ปนี้จะถูกรีเซ็ต)", () => {
        performUndoAction(orderId);
      });
      return;
    }

    // Color Picker
    const colorBtn = e.target.closest(".btn-color-picker");
    if (colorBtn) {
      const isAllowedEdit = hasRole(["Super Admin", "Order Manager"]);
      if (!isAllowedEdit) return;
      const orderId = colorBtn.getAttribute("data-id");
      if (typeof showColorPickerDropdown === 'function') showColorPickerDropdown(colorBtn, orderId);
      return;
    }

    // Toggle Keyed
    const toggleKeyedBtn = e.target.closest(".btn-toggle-keyed");
    if (toggleKeyedBtn) {
      const isAllowedEdit = hasRole(["Super Admin", "Shipper"]);
      if (!isAllowedEdit) return;
      const orderId = toggleKeyedBtn.getAttribute("data-id");
      if (typeof toggleKeyedStatus === 'function') toggleKeyedStatus(orderId);
      return;
    }

    // Set Pending
    const setPendingBtn = e.target.closest(".btn-set-pending");
    if (setPendingBtn) {
      const isAllowedEdit = hasRole(["Super Admin", "Shipper", "Packer & Picker"]);
      if (!isAllowedEdit) return;
      const orderId = setPendingBtn.getAttribute("data-id");
      showConfirm("ยืนยันการส่งขอข้อมูล", "ออเดอร์นี้จะถูกส่งไปที่คิว 'ขอข้อมูลเพิ่ม' แน่ใจหรือไม่?", async () => {
        try {
          await updateDoc(doc(db, "orders", orderId), {
            isPendingInfo: true,
            updatedAt: serverTimestamp()
          });
          showToast("ส่งขอข้อมูลเพิ่มเรียบร้อย", "success");
        } catch (err) {
          console.error(err);
          showToast("เกิดข้อผิดพลาด", "danger");
        }
      });
      return;
    }

    // Clear Pending
    const clearPendingBtn = e.target.closest(".btn-clear-pending");
    if (clearPendingBtn) {
      const isAllowedEdit = hasRole(["Super Admin", "Order Manager"]);
      if (!isAllowedEdit) return;
      const orderId = clearPendingBtn.getAttribute("data-id");
      try {
        await updateDoc(doc(db, "orders", orderId), {
          isPendingInfo: false,
          updatedAt: serverTimestamp()
        });
        showToast("ส่งข้อมูลกลับเรียบร้อย", "success");
      } catch (err) {
        console.error(err);
        showToast("เกิดข้อผิดพลาด", "danger");
      }
      return;
    }

    // 1. กดปุ่มแก้ไขการ์ด (ดึงข้อมูลเข้าฟอร์มขวา)
    const editBtn = e.target.closest(".btn-edit-order");
    if (editBtn) {
      const isDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน");
      const isAllowedEdit = hasRole(["Super Admin", "Order Manager"]) || (hasRole("Packer & Picker") && isDailyNote);
      if (!isAllowedEdit) {
        showToast("คุณไม่มีสิทธิ์แก้ไขการ์ดออเดอร์ 🔒", "danger");
        return;
      }
      const orderId = editBtn.getAttribute("data-id");
      showForm(orderId);
      return;
    }

    // 2. กดปุ่มลบการ์ดรายตัวออกจากคลาวด์
    const deleteBtn = e.target.closest(".btn-delete-order");
    if (deleteBtn) {
      const isDailyNote = activeNoteData && activeNoteData.title && activeNoteData.title.startsWith("ออเดอร์ประจำวัน");
      const isAllowedDelete = hasRole(["Super Admin", "Order Manager"]) || (hasRole("Packer & Picker") && isDailyNote);
      if (!isAllowedDelete) {
        showToast("คุณไม่มีสิทธิ์ลบการ์ดออเดอร์ 🔒", "danger");
        return;
      }
      const orderId = deleteBtn.getAttribute("data-id");
      const order = allOrders.find(o => o.id === orderId);
      if (order) {
        const confirmMessage = `คุณต้องการลบออเดอร์ของ "${order.thaiName || order.customerName || 'ลูกค้า'}" ใช่หรือไม่?`;
        showConfirm("ยืนยันการลบออเดอร์", confirmMessage, async () => {
          try {
            await deleteDoc(doc(db, "orders", orderId));
            showToast("ลบออเดอร์สำเร็จ!");
          } catch (err) {
            console.error("Delete order card error:", err);
            showToast("เกิดข้อผิดพลาดในการลบ", "danger");
          }
        });
      }
      return;
    }

    // 3. ปิด/เปิดแถบข้อความต้นฉบับพับย่นได้ (Collapsible Toggle)
    const collTrigger = e.target.closest(".collapsible-trigger");
    if (collTrigger) {
      const orderId = collTrigger.getAttribute("data-id");
      const contentDiv = document.getElementById(`collapsible-${orderId}`);
      if (contentDiv) {
        const isExpanded = contentDiv.classList.toggle("expanded");
        collTrigger.classList.toggle("active", isExpanded);
      }
      return;
    }

    // 4. กดปุ่มก็อปปี้รายฟิลด์ (Copy individual fields)
    const copyFieldBtn = e.target.closest(".btn-copy-field:not(.btn-copy-tracking)");
    if (copyFieldBtn) {
      const copyVal = copyFieldBtn.getAttribute("data-copy-val");
      navigator.clipboard.writeText(copyVal).then(() => {
        copyFieldBtn.classList.add("copied");
        const originalHTML = copyFieldBtn.innerHTML;
        copyFieldBtn.innerHTML = `<i data-lucide="check" style="width: 14px; height: 14px;"></i>`;
        if (window.lucide) window.lucide.createIcons();

        showToast("คัดลอกข้อมูลช่องนี้แล้ว!");
        setTimeout(() => {
          copyFieldBtn.classList.remove("copied");
          copyFieldBtn.innerHTML = originalHTML;
          if (window.lucide) window.lucide.createIcons();
        }, 1500);
      });
      return;
    }

    // 5.5 ปุ่มคัดลอกเลขพัสดุจัดส่งสะดวกรวดเร็ว
    const copyTrackingBtn = e.target.closest(".btn-copy-tracking");
    if (copyTrackingBtn) {
      const trackingVal = copyTrackingBtn.getAttribute("data-tracking") || "";
      let carrierVal = copyTrackingBtn.getAttribute("data-carrier") || "";

      let textToCopy = trackingVal;

      // If carrierVal is empty, try to detect it dynamically on the fly
      if (!carrierVal) {
        const carrierObj = detectCarrier(trackingVal);
        if (carrierObj) {
          carrierVal = carrierObj.name;
        }
      }

      if (carrierVal) {
        // Remove any potential emojis and trim
        let name = carrierVal.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F000}-\u{1FBF0}]/gu, '');
        name = name.replace(/[📬⚡🧡🔴📦🌐]/g, '').trim();

        const nameUpper = name.toUpperCase();
        if (nameUpper === "EMS") {
          textToCopy = `ThaiPost EMS : ${trackingVal} สามารถเช็คพัสดุได้ที่ https://track.thailandpost.co.th/`;
        } else if (nameUpper === "FLASH") {
          textToCopy = `Flash : ${trackingVal} สามารถเช็คพัสดุได้ที่ https://www.flashexpress.co.th/fle/tracking`;
        } else if (nameUpper === "SPX") {
          textToCopy = `SPX : ${trackingVal} สามารถเช็คพัสดุได้ที่ https://spx.co.th/track`;
        } else if (nameUpper === "DHL") {
          textToCopy = `DHL : ${trackingVal} สามารถเช็คพัสดุได้ที่ https://www.dhl.com/th-th/home/tracking.html`;
        } else if (nameUpper === "KEX" || nameUpper === "KERRY") {
          textToCopy = `KEX : ${trackingVal} สามารถเช็คพัสดุได้ที่ https://th.kex-express.com/th/track/`;
        } else {
          textToCopy = `${name} : ${trackingVal}`;
        }
      }

      navigator.clipboard.writeText(textToCopy).then(() => {
        copyTrackingBtn.classList.add("copied");
        const originalHTML = copyTrackingBtn.innerHTML;
        copyTrackingBtn.innerHTML = `<i data-lucide="check" style="width: 14px; height: 14px; color: var(--color-success);"></i>`;
        if (window.lucide) window.lucide.createIcons();

        showToast("คัดลอกเลขพัสดุสำเร็จ!");
        setTimeout(() => {
          copyTrackingBtn.classList.remove("copied");
          copyTrackingBtn.innerHTML = originalHTML;
          if (window.lucide) window.lucide.createIcons();
        }, 1500);
      });
      return;
    }

    // 6. บันทึกปุ่มเลขพัสดุ (สำหรับพนักงานตรวจสอบขั้นตอนสุดท้าย)
    const trackingBtn = e.target.closest(".btn-tracking-save");
    if (trackingBtn) {
      const orderId = trackingBtn.getAttribute("data-id");
      const trackingInput = document.getElementById(`tracking-input-${orderId}`);
      if (trackingInput) {
        const trackingVal = trackingInput.value.trim();
        const carrierVal = trackingInput.getAttribute("data-selected-carrier") || "อื่นๆ";
        const order = allOrders.find(o => o.id === orderId);
        if (!order) return;

        const maxShipments = order.maxShipments || 1;
        const currentShipment = order.currentShipment || 1;

        try {
          if (trackingVal) {
            const historyRecord = {
              shipment: currentShipment,
              trackingNumber: trackingVal,
              carrier: carrierVal,
              packerName: order.packerName || "",
              shippedAt: new Date().toISOString()
            };

            const trackingHistory = order.trackingHistory ? [...order.trackingHistory] : [];
            const existingIndex = trackingHistory.findIndex(h => h.shipment === currentShipment);
            if (existingIndex > -1) {
              trackingHistory[existingIndex] = historyRecord;
            } else {
              trackingHistory.push(historyRecord);
            }

            const updateData = {
              trackingHistory,
              updatedAt: serverTimestamp()
            };

            if (currentShipment < maxShipments) {
              updateData.currentShipment = currentShipment + 1;
              updateData.readyToShip = false;
              updateData.isPacked = false;
              updateData.trackingNumber = "";
              updateData.carrier = "";
              updateData.packerName = "";
              showToast(`บันทึกเลขพัสดุรอบที่ ${currentShipment} สำเร็จ! ออเดอร์สลับไปรอเตรียมของรอบที่ ${currentShipment + 1}`);
            } else {
              updateData.readyToShip = true;
              updateData.isPacked = true;
              updateData.trackingNumber = trackingVal;
              updateData.carrier = carrierVal;
              updateData.completedAt = serverTimestamp();
              showToast("บันทึกเลขพัสดุรอบสุดท้ายสำเร็จ! ดำเนินการเสร็จสมบูรณ์ ✅");
            }
            await updateDoc(doc(db, "orders", orderId), updateData);
          } else {
            const updateData = {
              trackingNumber: "",
              carrier: "",
              updatedAt: serverTimestamp()
            };
            await updateDoc(doc(db, "orders", orderId), updateData);
            showToast("ลบเลขพัสดุเรียบร้อย!");
          }
        } catch (err) {
          console.error("Tracking save error:", err);
          showToast("ล้มเหลวในการลงทะเบียนเลขจัดส่ง", "danger");
        }
      }
      return;
    }

    // 5.6 คลิกเลือกแบรนด์จัดส่งกรณีรหัสขึ้นต้นซ้ำซ้อน (Handle carrier choice click)
    const carrierChoiceBtn = e.target.closest(".btn-carrier-choice");
    if (carrierChoiceBtn) {
      const orderId = carrierChoiceBtn.getAttribute("data-order-id");
      const carrierName = carrierChoiceBtn.getAttribute("data-carrier");
      const input = document.getElementById(`tracking-input-${orderId}`);
      if (input) {
        input.setAttribute("data-selected-carrier", carrierName);

        // Highlight active button and remove from others on the same card
        const detectDiv = document.getElementById(`live-carrier-${orderId}`);
        if (detectDiv) {
          detectDiv.querySelectorAll(".btn-carrier-choice").forEach(btn => {
            if (btn.getAttribute("data-carrier") === carrierName) {
              btn.classList.add("active");
            } else {
              btn.classList.remove("active");
            }
          });
        }
      }
      return;
    }

    // 6.5 ปุ่มจัดส่งเรียบร้อยสำหรับรับหน้าร้าน/ไรเดอร์ (Complete pickup/rider order)
    const pickupCompleteBtn = e.target.closest(".btn-pickup-complete");
    if (pickupCompleteBtn) {
      const orderId = pickupCompleteBtn.getAttribute("data-id");
      const order = allOrders.find(o => o.id === orderId);
      if (order) {
        const maxShipments = order.maxShipments || 1;
        const currentShipment = order.currentShipment || 1;
        const deliveryType = order.deliveryType || "pickup";

        try {
          const historyRecord = {
            shipment: currentShipment,
            trackingNumber: "รับหน้าร้าน/ส่งไรเดอร์",
            carrier: deliveryType === 'pickup' ? "รับหน้าร้าน 🏪" : "เรียกรถ 🚗",
            packerName: order.packerName || "",
            shippedAt: new Date().toISOString()
          };

          const trackingHistory = order.trackingHistory ? [...order.trackingHistory] : [];
          const existingIndex = trackingHistory.findIndex(h => h.shipment === currentShipment);
          if (existingIndex > -1) {
            trackingHistory[existingIndex] = historyRecord;
          } else {
            trackingHistory.push(historyRecord);
          }

          const updateData = {
            trackingHistory,
            updatedAt: serverTimestamp()
          };

          if (currentShipment < maxShipments) {
            updateData.currentShipment = currentShipment + 1;
            updateData.readyToShip = false;
            updateData.isPacked = false;
            updateData.pickupDone = false;
            updateData.packerName = "";
            showToast(`จัดส่งรอบที่ ${currentShipment} เรียบร้อย! ออเดอร์สลับไปรอเตรียมของรอบที่ ${currentShipment + 1}`);
          } else {
            updateData.readyToShip = true;
            updateData.isPacked = true;
            updateData.pickupDone = true;
            updateData.completedAt = serverTimestamp();
            showToast("จัดส่งรอบสุดท้ายเรียบร้อย! ดำเนินการเสร็จสมบูรณ์ ✅");
          }
          await updateDoc(doc(db, "orders", orderId), updateData);
        } catch (err) {
          console.error("Pickup complete click error:", err);
          showToast("เกิดข้อผิดพลาดในการบันทึก", "danger");
        }
      }
      return;
    }

    // 6.6 ปุ่มเตรียมของสำหรับรับหน้าร้าน/ไรเดอร์ (Prepare pickup/rider order)
    const pickupPrepareBtn = e.target.closest(".btn-pickup-prepare:not(.btn-express-prepare)");
    if (pickupPrepareBtn) {
      const orderId = pickupPrepareBtn.getAttribute("data-id");
      try {
        await updateDoc(doc(db, "orders", orderId), {
          readyToShip: true,
          pickupDone: false,
          updatedAt: serverTimestamp()
        });
        showToast("เปลี่ยนสถานะเป็น รับหน้าร้าน/ส่งไรเดอร์ 🏪");
      } catch (err) {
        console.error("Pickup prepare click error:", err);
        showToast("เกิดข้อผิดพลาดในการบันทึก", "danger");
      }
      return;
    }

    // 6.7 ปุ่มเตรียมของสำหรับพัสดุ Express (Prepare express order)


    // 7. สลับติ๊กสติกเกอร์การเงินตรงตัวการ์ดได้ทันที (Payment Toggle on card - วนลูป รอชำระเงิน -> มัดจำ -> ชำระเงินแล้ว)
    const paySticker = e.target.closest(".sticker-payment");
    if (paySticker) {
      const isQueuePage = (activeNoteId === "prepare-queue" || activeNoteId === "shipping-queue" || activeNoteId === "completed-queue" || activeNoteId === "pickup-queue");
      if (isQueuePage) {
        showToast("ไม่สามารถแก้ไขแบบรวดเร็วในหน้านี้ได้ กรุณากดปุ่มดินสอ ✏️", "warning");
        return;
      }
      const orderId = paySticker.getAttribute("data-id");
      const order = allOrders.find(o => o.id === orderId);
      if (order) {
        let currentStatus = order.paymentStatus || (order.isPaid ? "paid" : "unpaid");
        let nextStatus = "unpaid";
        let nextDepositAmount = order.depositAmount || "";

        if (currentStatus === "unpaid") {
          nextStatus = "deposit";
          const inputAmt = prompt("ระบุจำนวนเงินมัดจำ (บาท):", order.depositAmount || "500");
          if (inputAmt === null) return; // กดยกเลิก
          nextDepositAmount = inputAmt.trim();
        } else if (currentStatus === "deposit") {
          nextStatus = "paid";
          nextDepositAmount = "";
        } else {
          nextStatus = "unpaid";
          nextDepositAmount = "";
        }

        try {
          await updateDoc(doc(db, "orders", orderId), {
            paymentStatus: nextStatus,
            depositAmount: nextDepositAmount,
            isPaid: nextStatus === "paid",
            updatedAt: serverTimestamp()
          });

          let statusMsg = "เปลี่ยนเป็น รอชำระเงิน";
          if (nextStatus === "paid") statusMsg = "เปลี่ยนเป็น ชำระเงินแล้ว";
          else if (nextStatus === "deposit") statusMsg = `เปลี่ยนเป็น มัดจำแล้ว (${nextDepositAmount} ฿)`;

          showToast(statusMsg);
        } catch (err) {
          console.error("Payment sticker toggle error:", err);
        }
      }
      return;
    }

    // 8. สลับประเภทขนส่งตรงตัวการ์ดได้ทันที (Delivery Type toggle on card)
    const delSticker = e.target.closest(".sticker-delivery");
    if (delSticker) {
      const isQueuePage = (activeNoteId === "prepare-queue" || activeNoteId === "shipping-queue" || activeNoteId === "completed-queue" || activeNoteId === "pickup-queue");
      if (isQueuePage) {
        showToast("ไม่สามารถแก้ไขแบบรวดเร็วในหน้านี้ได้ กรุณากดปุ่มดินสอ ✏️", "warning");
        return;
      }
      const orderId = delSticker.getAttribute("data-id");
      const type = delSticker.getAttribute("data-type");
      try {
        await updateDoc(doc(db, "orders", orderId), {
          deliveryType: type,
          updatedAt: serverTimestamp()
        });
        showToast("อัปเดตระบบขนส่งการ์ดใบนี้สำเร็จ");
      } catch (err) {
        console.error("Delivery sticker toggle error:", err);
      }
      return;
    }

    // 8.5 สลับความพร้อมการส่งตรงตัวการ์ดได้ทันที (Ready to Ship toggle on card - กดมาร์กพร้อมจัดส่งได้ทันทีเพื่อความรวดเร็ว)
    const readySticker = e.target.closest(".sticker-ready");
    if (readySticker) {
      if (!hasRole(["Super Admin", "Order Manager", "Packer & Picker"])) {
        showToast("ไม่มีสิทธิ์เปลี่ยนสถานะการจัดเตรียมสินค้า 🔒", "danger");
        return;
      }
      const isQueuePage = (activeNoteId === "completed-queue");
      if (isQueuePage) {
        showToast("ไม่สามารถแก้ไขแบบรวดเร็วในหน้านี้ได้ กรุณากดปุ่มดินสอ ✏️", "warning");
        return;
      }
      const orderId = readySticker.getAttribute("data-id");
      const order = allOrders.find(o => o.id === orderId);
      if (order) {
        if (order.deliveryType === 'pickup' || order.deliveryType === 'messenger') {
          if (order.pickupDone) {
            showToast("ออเดอร์นี้ดำเนินการเรียบร้อยแล้ว ✅");
            return;
          }
          let nextReady = false;
          let nextPacked = false;
          let nextPickupDone = false;

          if (!order.readyToShip) {
            nextReady = true;
            nextPacked = false;
            nextPickupDone = false;
            showToast("เปลี่ยนเป็น แพ็คเสร็จแล้ว 📦");
          } else if (order.readyToShip && !order.isPacked) {
            // Intercept transition to packed - show picker selection modal!
            openPackerSelectModal(order);
            return;
          } else if (order.readyToShip && order.isPacked && !order.pickupDone) {
            nextReady = true;
            nextPacked = true;
            nextPickupDone = true;
            showToast("ดำเนินการเสร็จสิ้นเรียบร้อย! ✅");
          }

          try {
            await updateDoc(doc(db, "orders", orderId), {
              readyToShip: nextReady,
              isPacked: nextPacked,
              pickupDone: nextPickupDone,
              updatedAt: serverTimestamp(),
              ...( nextPickupDone && !order.pickupDone ? { completedAt: serverTimestamp() } : {} )
            });
          } catch (err) {
            console.error("Pickup ready cycle toggle error:", err);
          }
        } else {
          // Normal express behavior (Three-stage cycle)
          if (order.trackingNumber) {
            showToast("ออเดอร์นี้จัดส่งเรียบร้อยแล้ว ✅");
            return;
          }
          if (order.readyToShip && order.isPacked) {
            showToast("ออเดอร์นี้แพ็คเรียบร้อยแล้ว กรุณาระบุเลขพัสดุจัดส่ง 📦");
            return;
          }
          let nextReady = false;
          let nextPacked = false;
          if (!order.readyToShip) {
            nextReady = true;
            nextPacked = false;
            showToast("เปลี่ยนเป็น แพ็คเสร็จแล้ว 📦");
          } else if (order.readyToShip && !order.isPacked) {
            // Intercept transition to packed - show picker selection modal!
            openPackerSelectModal(order);
            return;
          } else {
            nextReady = false;
            nextPacked = false;
            showToast("เปลี่ยนเป็น เตรียมของ 📦");
          }
          try {
            await updateDoc(doc(db, "orders", orderId), {
              readyToShip: nextReady,
              isPacked: nextPacked,
              updatedAt: serverTimestamp()
            });
          } catch (err) {
            console.error("Express ready sticker cycle toggle error:", err);
          }
        }
      }
      return;
    }
  });

  // จองปุ่ม Enter สำหรับบันทึกเลขพัสดุโดยสะดวก
  dom.ordersCardsListDiv.addEventListener("keydown", async (e) => {
    if (e.key === "Enter" && e.target.classList.contains("tracking-input")) {
      const trackingInput = e.target;
      if (trackingInput.disabled) return; // ป้องกันการกด Enter บนบอร์ดพัสดุส่งเสร็จสิ้น
      const orderId = trackingInput.id.replace("tracking-input-", "");
      const trackingVal = trackingInput.value.trim();
      const carrierVal = trackingInput.getAttribute("data-selected-carrier") || "อื่นๆ";
      try {
        const order = allOrders.find(o => o.id === orderId);
        if (!order) return;

        const maxShipments = order.maxShipments || 1;
        const currentShipment = order.currentShipment || 1;

        if (trackingVal) {
          const historyRecord = {
            shipment: currentShipment,
            trackingNumber: trackingVal,
            carrier: carrierVal,
            packerName: order.packerName || "",
            shippedAt: new Date().toISOString()
          };

          const trackingHistory = order.trackingHistory ? [...order.trackingHistory] : [];
          const existingIndex = trackingHistory.findIndex(h => h.shipment === currentShipment);
          if (existingIndex > -1) {
            trackingHistory[existingIndex] = historyRecord;
          } else {
            trackingHistory.push(historyRecord);
          }

          const updateData = {
            trackingHistory,
            updatedAt: serverTimestamp()
          };

          if (currentShipment < maxShipments) {
            updateData.currentShipment = currentShipment + 1;
            updateData.readyToShip = false;
            updateData.isPacked = false;
            updateData.trackingNumber = "";
            updateData.carrier = "";
            updateData.packerName = "";
            showToast(`บันทึกเลขพัสดุรอบที่ ${currentShipment} สำเร็จ! ออเดอร์สลับไปรอเตรียมของรอบที่ ${currentShipment + 1}`);
          } else {
            updateData.readyToShip = true;
            updateData.isPacked = true;
            updateData.trackingNumber = trackingVal;
            updateData.carrier = carrierVal;
              updateData.completedAt = serverTimestamp();
            showToast("บันทึกเลขพัสดุรอบสุดท้ายสำเร็จ! ดำเนินการเสร็จสมบูรณ์ ✅");
          }
          await updateDoc(doc(db, "orders", orderId), updateData);
          trackingInput.blur();
        } else {
          const updateData = {
            trackingNumber: "",
            carrier: "",
            updatedAt: serverTimestamp()
          };
          await updateDoc(doc(db, "orders", orderId), updateData);
          showToast("ลบเลขพัสดุเรียบร้อย!");
        }
      } catch (err) {
        console.error("Tracking input keydown error:", err);
        showToast("ไม่สามารถลงเลขพัสดุผ่าน Enter", "danger");
      }
    }
  });

  // ดักการพิมพ์เลขพัสดุเพื่อวิเคราะห์แบรนด์จัดส่งสด (Live Carrier Auto-Detect)
  dom.ordersCardsListDiv.addEventListener("input", (e) => {
    if (e.target.classList.contains("tracking-input")) {
      const orderId = e.target.getAttribute("data-id") || e.target.id.replace("tracking-input-", "");
      const trackingVal = e.target.value.trim();
      updateLiveCarrierBadge(orderId, trackingVal);
    }
  });

  // ฉ. ตัวควบคุมระบบมือถือและการขยับหน้าต่าง (Mobile tab switcher integration - ปุ่มเดิมนำออกแล้วใช้แถบนำทางด้านล่างแทน)
  if (dom.mobileOrdersToEditor) {
    dom.mobileOrdersToEditor.addEventListener("click", () => setMobilePane("show-editor"));
  }

  dom.navTabs.forEach(tab => {
    tab.addEventListener("click", () => {
      const targetPane = tab.getAttribute("data-target");
      if (targetPane === "sidebar-panel") {
        closeForm();
        closeSettings();
        setMobilePane("show-sidebar");
      }
      else if (targetPane === "editor-panel") {
        if (window.innerWidth <= 600) {
          activeNoteId = null;
          activeNoteData = null;
          closeForm();
          closeSettings();
          setMobilePane("show-editor");

          const searchActiveInput = document.getElementById("search-active-orders");
          if (searchActiveInput) searchActiveInput.value = "";

          renderOrderDashboard();
        } else {
          if (!activeNoteId) {
            showToast("กรุณาเลือกหรือสร้างโน้ตคำสั่งซื้อก่อนครับ", "danger");
            return;
          }
          closeForm();
          closeSettings();
          setMobilePane("show-editor");
        }
      }
      else if (targetPane === "add-order-action") {
        if (!activeNoteId) {
          showToast("กรุณาเลือกหรือสร้างโน้ตคำสั่งซื้อก่อนครับ", "danger");
          return;
        }
        closeSettings();
        showForm();
      }
      else if (targetPane === "settings-panel") {
        closeForm();
        showSettings();
      }
    });
  });

  adjustSearchLayout();

  window.addEventListener("resize", () => {
    const wasMobile = isMobile;
    const wasCompactMobile = isCompactMobile;
    isMobile = window.innerWidth <= 1024;
    isCompactMobile = window.innerWidth <= 600;

    updateMobileTabLabels();

    if (wasCompactMobile !== isCompactMobile) {
      if (isCompactMobile) {
        setMobilePane("show-sidebar");
      } else if (isMobile) {
        dom.appContainer.classList.remove("show-sidebar", "show-editor", "show-orders");
      }
    }

    if (wasMobile !== isMobile && !isMobile) {
      dom.appContainer.classList.remove("show-sidebar", "show-editor", "show-orders");
    }

    adjustSearchLayout();
  });

  // ผูกปุ่ม Back ลูกศรย้อนกลับบนมือถือ
  const btnMobileBack = document.getElementById("btn-mobile-back");
  if (btnMobileBack) {
    btnMobileBack.addEventListener("click", () => {
      if (isMobile) {
        window.history.back();
      }
    });
  }

  // ตั้งค่าระบบประวัติการท่องเว็บ (History API) สำหรับการกดปุ่ม Back บน Android / Browser
  if (isMobile) {
    history.replaceState({ pane: "show-sidebar" }, "");

    window.addEventListener("popstate", (e) => {
      const state = e.state;
      const targetPane = state ? state.pane : "show-sidebar";

      // 1. จัดการการปิด/เปิด Settings Modal
      if (targetPane === "show-settings") {
        showSettings();
      } else {
        closeSettings(false);
      }

      // 2. จัดการการปิด/เปิด Form Modal
      if (targetPane === "show-orders") {
        dom.appContainer.classList.add("show-form");
        setMobilePane("show-orders");
      } else {
        closeForm(false);
        setMobilePane(targetPane);
      }
    });
  }

  // กำหนดหน้าเริ่มต้นเมื่อเปิดแอปบนหน้าจอมือถือ (Sidebar แถบรายชื่อโน้ต)
  if (isMobile) {
    if (isCompactMobile) {
      setMobilePane("show-sidebar");
    }
  }
}

function adjustSearchLayout() {
  const searchWrapper = document.getElementById("board-search-wrapper-div");
  const shortcutsWrapper = document.getElementById("shortcuts-wrapper");
  const boardContentWrapper = document.querySelector(".board-content-wrapper");
  const actionBar = document.getElementById("board-action-bar-div");

  if (!searchWrapper) return;

  if (window.innerWidth > 600) {
    // Desktop: Move active orders search bar into the sidebar, above shortcuts-wrapper
    if (shortcutsWrapper && searchWrapper.parentNode !== shortcutsWrapper.parentNode) {
      shortcutsWrapper.parentNode.insertBefore(searchWrapper, shortcutsWrapper);
    }
    searchWrapper.style.display = "flex"; // Force show in sidebar on desktop!
  } else {
    // Mobile: Move active orders search bar back to the board pane, under the note-title-row or before action-bar
    if (boardContentWrapper && actionBar && searchWrapper.parentNode !== boardContentWrapper) {
      boardContentWrapper.insertBefore(searchWrapper, actionBar);
    }
  }
}

function setSearchWrapperDisplay(displayStyle) {
  const searchWrapper = document.getElementById("board-search-wrapper-div");
  if (!searchWrapper) return;
  if (window.innerWidth > 600) {
    searchWrapper.style.display = "flex";
  } else {
    searchWrapper.style.display = displayStyle;
  }
}

const ORDER_CARD_EXPORT_COLORS = {
  card: "#ffffff",
  row: "#f4f4f4",
  items: "#eefaf3",
  text: "#2b221a",
  muted: "#6b5c51",
  border: "rgba(92, 79, 68, 0.12)",
  successBg: "#e8f7f2",
  warningBg: "#fff3eb",
  infoBg: "#eef8fe"
};

function getExportableOrderCard(card) {
  const clone = card.cloneNode(true);
  const cardWidth = Math.ceil(card.getBoundingClientRect().width || 490);
  const exportColors = ORDER_CARD_EXPORT_COLORS;

  clone.classList.add("order-card-export-clone", "light-theme");
  clone.style.width = `${cardWidth}px`;
  clone.style.maxWidth = `${cardWidth}px`;
  clone.style.transform = "none";
  clone.style.transition = "none";
  clone.style.setProperty("--bg-card", exportColors.card);
  clone.style.setProperty("--bg-card-hover", exportColors.card);
  clone.style.setProperty("--bg-field-row", exportColors.row);
  clone.style.setProperty("--bg-input", "#f8f5ee");
  clone.style.setProperty("--color-text-main", exportColors.text);
  clone.style.setProperty("--color-text-muted", exportColors.muted);
  clone.style.setProperty("background", exportColors.card, "important");
  clone.style.setProperty("background-color", exportColors.card, "important");
  clone.style.setProperty("color", exportColors.text, "important");
  clone.style.setProperty("border-color", exportColors.border, "important");

  clone.querySelectorAll([
    ".order-card-index",
    ".order-card-export-actions",
    ".header-actions",
    ".collapsible-trigger",
    ".collapsible-content",
    ".order-packer-tag",
    ".shipment-status-summary",
    ".sticker-ready",
    ".order-select-checkbox",
    ".btn-copy-field",
    ".tracking-input-wrapper",
    "button",
    "input",
    "textarea",
    "select"
  ].join(",")).forEach(el => el.remove());

  clone.querySelectorAll(".sticker-delivery:not(.active)").forEach(el => el.remove());
  clone.querySelectorAll(".address-field-row").forEach(el => {
    el.style.setProperty("background", exportColors.row, "important");
    el.style.setProperty("background-color", exportColors.row, "important");
  });
  clone.querySelectorAll(".stickers-container").forEach(el => {
    el.style.setProperty("background", exportColors.card, "important");
    el.style.setProperty("background-color", exportColors.card, "important");
    el.style.setProperty("border-top-color", exportColors.border, "important");
    el.style.setProperty("border-bottom-color", exportColors.border, "important");
  });
  clone.querySelectorAll(".platform-tag").forEach(el => {
    el.style.setProperty("background-color", exportColors.successBg, "important");
  });
  clone.querySelectorAll(".card-items-block").forEach(el => {
    el.style.setProperty("background", exportColors.items, "important");
    el.style.setProperty("background-color", exportColors.items, "important");
  });
  clone.querySelectorAll(".field-value, .card-items-block p").forEach(el => {
    el.style.setProperty("color", exportColors.text, "important");
  });
  clone.querySelectorAll(".field-label, .card-items-block h4").forEach(el => {
    el.style.setProperty("color", exportColors.muted, "important");
  });
  clone.querySelectorAll(".sticker-payment.paid").forEach(el => {
    el.style.setProperty("background-color", exportColors.successBg, "important");
  });
  clone.querySelectorAll(".sticker-payment.deposit, .sticker-delivery.active.pickup").forEach(el => {
    el.style.setProperty("background-color", exportColors.warningBg, "important");
  });
  clone.querySelectorAll(".sticker-delivery.active.express").forEach(el => {
    el.style.setProperty("background-color", exportColors.infoBg, "important");
  });
  clone.querySelectorAll(".sticker-delivery.active.messenger").forEach(el => {
    el.style.setProperty("background-color", exportColors.successBg, "important");
  });

  return clone;
}

async function renderOrderCardImageBlob(card) {
  let html2canvasLib = window.html2canvas;
  if (!html2canvasLib) {
    try {
      const module = await import("html2canvas");
      html2canvasLib = module.default || module;
    } catch (e) {
      console.error("Failed to dynamically load html2canvas:", e);
      throw new Error("ไม่สามารถโหลดไลบรารีสร้างภาพการ์ดออเดอร์ได้");
    }
  }

  const clone = getExportableOrderCard(card);
  const cloneBackground = ORDER_CARD_EXPORT_COLORS.card;
  const holder = document.createElement("div");
  holder.className = "order-card-export-holder";
  holder.style.backgroundColor = cloneBackground;
  holder.appendChild(clone);
  document.body.appendChild(holder);

  try {
    const canvas = await html2canvasLib(clone, {
      backgroundColor: cloneBackground,
      scale: 2,
      useCORS: true,
      logging: false
    });

    return await new Promise((resolve, reject) => {
      const paintedCanvas = document.createElement("canvas");
      paintedCanvas.width = canvas.width;
      paintedCanvas.height = canvas.height;

      const ctx = paintedCanvas.getContext("2d");
      ctx.fillStyle = cloneBackground;
      ctx.fillRect(0, 0, paintedCanvas.width, paintedCanvas.height);
      ctx.drawImage(canvas, 0, 0);

      paintedCanvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error("Could not create image blob"));
      }, "image/png", 1);
    });
  } finally {
    holder.remove();
  }
}

function getOrderImageFileName(card) {
  const orderId = card.dataset.id || "order";
  return `meanbook-order-${orderId}.png`;
}

async function copyOrderCardImage(card, button) {
  if (!hasRole(["Super Admin", "Order Manager"])) {
    showToast("คุณไม่มีสิทธิ์คัดลอกรูปการ์ดออเดอร์", "danger");
    return;
  }

  if (!navigator.clipboard || !window.ClipboardItem) {
    showToast("เบราว์เซอร์นี้ยังไม่รองรับการคัดลอกรูปภาพ กรุณาใช้ปุ่มดาวน์โหลดแทน", "warning");
    return;
  }

  const originalHTML = button.innerHTML;
  button.disabled = true;
  button.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
  if (window.lucide) window.lucide.createIcons();

  try {
    const blob = await renderOrderCardImageBlob(card);
    await navigator.clipboard.write([
      new ClipboardItem({ "image/png": blob })
    ]);
    button.classList.add("copied");
    button.innerHTML = `<i data-lucide="check"></i>`;
    showToast("คัดลอกรูปการ์ดออเดอร์แล้ว");
  } catch (err) {
    console.error("Copy order card image failed:", err);
    showToast("คัดลอกรูปไม่สำเร็จ กรุณาลองดาวน์โหลดรูปแทน", "danger");
  } finally {
    if (window.lucide) window.lucide.createIcons();
    setTimeout(() => {
      button.disabled = false;
      button.classList.remove("copied");
      button.innerHTML = originalHTML;
      if (window.lucide) window.lucide.createIcons();
    }, 1400);
  }
}

async function downloadOrderCardImage(card, button) {
  if (!hasRole(["Super Admin", "Order Manager"])) {
    showToast("คุณไม่มีสิทธิ์ดาวน์โหลดรูปการ์ดออเดอร์", "danger");
    return;
  }

  const originalHTML = button.innerHTML;
  button.disabled = true;
  button.innerHTML = `<i data-lucide="loader-2" class="spin-icon"></i>`;
  if (window.lucide) window.lucide.createIcons();

  try {
    const blob = await renderOrderCardImageBlob(card);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = getOrderImageFileName(card);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast("ดาวน์โหลดรูปการ์ดออเดอร์แล้ว");
  } catch (err) {
    console.error("Download order card image failed:", err);
    showToast("ดาวน์โหลดรูปไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", "danger");
  } finally {
    button.disabled = false;
    button.innerHTML = originalHTML;
    if (window.lucide) window.lucide.createIcons();
  }
}



// สั่งรันโปรแกรมเมื่อพร้อมใช้งาน
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp);
} else {
  initApp();
}

// ลงทะเบียน Service Worker เพื่อรองรับการแจ้งเตือนแบบ Push Notifications แม้ปิดหน้าเว็บ/แอป และระบบแคชแบบ Offline
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("./sw.js")
      .then((reg) => {
        console.log("[PWA] Service Worker registered successfully with scope:", reg.scope);

        // ตรวจสอบการอัปเดต Service Worker เมื่อผู้ใช้สลับกลับมาที่หน้าต่างแอป
        window.addEventListener("focus", () => {
          reg.update().catch(() => {});
        });

        // ดักจับเมื่อมีเวอร์ชันใหม่ดาวน์โหลดและติดตั้งเสร็จในเบื้องหลัง
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                console.log("[PWA] New version ready!");
                if (typeof showToast === "function") {
                  showToast("พบเวอร์ชันใหม่ของระบบ! สามารถกดปุ่มรีเฟรชเพื่ออัปเดตได้ทันที", "info");
                }
              }
            });
          }
        });
      })
      .catch((err) => {
        console.error("[PWA] Service Worker registration failed:", err);
      });
  });
}

// เพิ่มฟังก์ชันสไลด์เลื่อนซ้าย-ขวาด้วยลูกกลิ้งเมาส์ (Vertical to Horizontal wheel scroll) สำหรับผู้ใช้บนอุปกรณ์/หน้าจอที่กว้างไม่เกิน 1600px
window.addEventListener("load", () => {
  const scrollableElements = ['.settings-tabs-header', '.sticker-buttons'];
  scrollableElements.forEach(selector => {
    document.body.addEventListener("wheel", (e) => {
      // ไม่ทำงานบนหน้าจอขนาดใหญ่ที่ปุ่มเรียงขึ้นบรรทัดใหม่แล้ว เพื่อไม่ให้ขัดขวางการเลื่อนแนวตั้ง
      if (window.innerWidth > 1600) return;

      const target = e.target.closest(selector);
      if (target) {
        if (e.deltaY !== 0) {
          e.preventDefault();
          target.scrollLeft += e.deltaY;
        }
      }
    }, { passive: false });
  });
});


// Color Picker Logic
  window.updateOrderColor = async (orderId, color) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { cardColor: color });
      const o = allOrders.find(x => x.id === orderId);
      if (o) o.cardColor = color;
      if (typeof searchOrdersMap !== 'undefined' && searchOrdersMap && searchOrdersMap.has(orderId)) {
        searchOrdersMap.get(orderId).cardColor = color;
      }
      renderOrderDashboard();
      showToast("อัปเดตสีการ์ดเรียบร้อย", "success");
    } catch (e) {
      console.error(e);
      showToast("เกิดข้อผิดพลาดในการเปลี่ยนสี", "danger");
    }
  };

  window.showColorPickerDropdown = function(btn, orderId) {
    document.querySelectorAll(".color-picker-dropdown").forEach(el => el.remove());
    const safeOrderId = escapeHTML(orderId);
    const dropdown = document.createElement("div");
    dropdown.className = "color-picker-dropdown";
    dropdown.innerHTML = `
      <div class="color-swatch swatch-clear" title="ล้างสี" onclick="updateOrderColor('${safeOrderId}', ''); this.parentElement.remove();"><i data-lucide="x" style="width:14px; height:14px;"></i></div>
      <div class="color-swatch swatch-yellow" title="สีเหลือง" onclick="updateOrderColor('${safeOrderId}', 'yellow'); this.parentElement.remove();"></div>
      <div class="color-swatch swatch-red" title="สีแดง" onclick="updateOrderColor('${safeOrderId}', 'red'); this.parentElement.remove();"></div>
      <div class="color-swatch swatch-purple" title="สีม่วง" onclick="updateOrderColor('${safeOrderId}', 'purple'); this.parentElement.remove();"></div>
      <div class="color-swatch swatch-blue" title="สีฟ้า" onclick="updateOrderColor('${safeOrderId}', 'blue'); this.parentElement.remove();"></div>
    `;
    btn.parentElement.style.position = "relative";
    btn.parentElement.appendChild(dropdown);
    if (window.lucide) window.lucide.createIcons();

    setTimeout(() => {
      const outsideClick = (e) => {
        if (!dropdown.contains(e.target) && !btn.contains(e.target)) {
          dropdown.remove();
          document.removeEventListener("click", outsideClick);
        }
      };
      document.addEventListener("click", outsideClick);
    }, 10);
  };

// ---------------- AUTO-GLOBAL SEARCH ----------------
let globalSearchTimeout = null;

function getCurrentSearchInputQuery() {
  const noteSearchVal = dom.searchNotes ? dom.searchNotes.value.trim() : "";
  const activeSearchInput = document.getElementById("search-active-orders");
  const activeSearchVal = activeSearchInput ? activeSearchInput.value.trim() : "";
  return noteSearchVal || activeSearchVal;
}

async function triggerAutoGlobalSearch(queryText) {
  if (globalSearchTimeout) {
    clearTimeout(globalSearchTimeout);
    globalSearchTimeout = null;
  }

  const q = (queryText || "").trim();
  if (!q || q.length < 2) return; // Only search if >= 2 chars

  const normalizedKey = q.toLowerCase();
  if (completedSearchQueries.has(normalizedKey)) {
    return; // Already queried Firestore for this term
  }

  const searchSeq = ++currentSearchSeq;

  globalSearchTimeout = setTimeout(async () => {
    // Check if another query came in while waiting
    if (searchSeq !== currentSearchSeq) return;

    // Check if input was cleared in the meantime
    const currentInputText = getCurrentSearchInputQuery();
    if (!currentInputText || currentInputText.length < 2) return;

    try {
      console.log(`[Global Search #${searchSeq}] Searching Firestore for:`, q);
      showToast("กำลังสืบค้นข้อมูลย้อนหลัง...", "info");
      const ordersRef = collection(db, "orders");
      const queries = [];
      const endText = q + '\uf8ff';

      // 1. Prefix range on customerName and thaiName (including casing variations for English text)
      const nameQueries = new Set([q]);
      if (/[a-zA-Z]/.test(q)) {
        nameQueries.add(q.toLowerCase());
        nameQueries.add(q.toUpperCase());
        nameQueries.add(q.charAt(0).toUpperCase() + q.slice(1).toLowerCase());
      }

      nameQueries.forEach(nameText => {
        const nameEnd = nameText + '\uf8ff';
        queries.push(query(ordersRef, where("customerName", ">=", nameText), where("customerName", "<=", nameEnd), limit(50)));
        queries.push(query(ordersRef, where("thaiName", ">=", nameText), where("thaiName", "<=", nameEnd), limit(50)));
      });

      // 2. Tracking Number (Prefix range query includes exact match)
      queries.push(query(ordersRef, where("trackingNumber", ">=", q), where("trackingNumber", "<=", endText), limit(50)));

      // 3. Phone number queries (prefix range query includes exact match)
      queries.push(query(ordersRef, where("phone", ">=", q), where("phone", "<=", endText), limit(50)));

      // 4. Normalized phone digit variations
      let digits = q.replace(/\D/g, "");
      if (digits.startsWith("66")) {
        digits = "0" + digits.slice(2);
      }

      if (digits.length >= 3 && digits !== q) {
        const endDigits = digits + '\uf8ff';
        queries.push(query(ordersRef, where("phone", ">=", digits), where("phone", "<=", endDigits), limit(50)));
      }

      // 5. Common hyphenated / spaced formats for Thai phone numbers
      const phoneCandidates = new Set();
      if (digits.length === 10) {
        phoneCandidates.add(`${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`);
        phoneCandidates.add(`${digits.slice(0, 3)}-${digits.slice(3)}`);
        phoneCandidates.add(`${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`);
        phoneCandidates.add(`${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`);
      } else if (digits.length === 9) {
        phoneCandidates.add(`${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`);
        phoneCandidates.add(`${digits.slice(0, 2)}-${digits.slice(2)}`);
        phoneCandidates.add(`${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`);
      } else if (digits.length >= 3) {
        phoneCandidates.add(`${digits.slice(0, 3)}-${digits.slice(3)}`);
      }

      phoneCandidates.forEach(cand => {
        if (cand !== q && cand !== digits) {
          queries.push(query(ordersRef, where("phone", ">=", cand), where("phone", "<=", cand + '\uf8ff'), limit(30)));
        }
      });

      // 6. Items (รายการสั่งซื้อ) - Prefix range with casing variations
      const itemQueries = new Set([q]);
      if (/[a-zA-Z]/.test(q)) {
        itemQueries.add(q.toLowerCase());
        itemQueries.add(q.toUpperCase());
        itemQueries.add(q.charAt(0).toUpperCase() + q.slice(1).toLowerCase());
      }
      itemQueries.forEach(itemText => {
        const itemEnd = itemText + '\uf8ff';
        queries.push(query(ordersRef, where("items", ">=", itemText), where("items", "<=", itemEnd), limit(50)));
      });

      const results = await Promise.allSettled(queries.map(qObj => getDocs(qObj)));

      // Check again after async fetch if this search is still active!
      if (searchSeq !== currentSearchSeq) {
        console.log(`[Global Search #${searchSeq}] Stale search discarded (superseded by #${currentSearchSeq})`);
        return;
      }

      const activeQueryNow = getCurrentSearchInputQuery();
      if (!activeQueryNow || activeQueryNow.length < 2) {
        console.log(`[Global Search #${searchSeq}] Search input was cleared during fetch, discarding results`);
        return;
      }

      // Mark this query as completed
      completedSearchQueries.add(normalizedKey);
      if (completedSearchQueries.size > 150) {
        const firstKey = completedSearchQueries.values().next().value;
        completedSearchQueries.delete(firstKey);
      }

      let foundNew = false;
      results.forEach(res => {
        if (res.status === "fulfilled" && res.value) {
          res.value.forEach(doc => {
            if (!allOrders.some(o => o.id === doc.id)) {
              searchOrdersCache.set(doc.id, { id: doc.id, ...doc.data() });
              foundNew = true;
            }
          });
        } else if (res.status === "rejected") {
          console.warn("Auto-Global Search query warning:", res.reason);
        }
      });

      if (foundNew) {
        rebuildAllOrdersFromCaches();
        showToast("ค้นพบออเดอร์เก่าและดึงข้อมูลสำเร็จ!", "success");
        renderOrderDashboard();
        renderNotesList();
      }
    } catch (err) {
      console.error("Auto-Global Search Error:", err);
    }
  }, 400); // 0.4s debounce
}
// ----------------------------------------------------

// --- Refresh Buttons Setup ---
function setupRefreshButtons() {
  const refreshHandler = () => {
    if (typeof showToast === "function") showToast("กำลังรีเฟรชข้อมูล...", "info");
    setTimeout(() => window.location.reload(), 300);
  };
  
  const btnRefreshMobile = document.getElementById("btn-refresh-mobile");
  if (btnRefreshMobile) btnRefreshMobile.addEventListener("click", refreshHandler);
  
  const btnRefreshDesktop = document.getElementById("btn-refresh-desktop");
  if (btnRefreshDesktop) btnRefreshDesktop.addEventListener("click", refreshHandler);
  
  const btnRefreshEditor = document.getElementById("btn-refresh-editor");
  if (btnRefreshEditor) btnRefreshEditor.addEventListener("click", refreshHandler);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setupRefreshButtons);
} else {
  setupRefreshButtons();
}
